// src/pages/Tabs/OffStreetCurrentStatusPage.jsx
import { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronLeft,
  ChevronDown,
  ChevronRight,
  MapPin,
  Building2,
  CircleDot,
  X,
  Navigation,
  Layers,
  Search,
  SlidersHorizontal,
  CalendarRange,
  Check,
  Calendar,
  TrendingUp,
  Loader2,
  CreditCard,
  Wallet,
  Globe,
  BarChart2,
  Clock,
  AlertTriangle,
  Ban,
  MonitorSmartphone,
  LogOut,
  Banknote,
  Smartphone,
  Tablet,
  CalendarCheck,
  ShieldCheck,
  LogIn,
  BadgeCheck,
} from "lucide-react";
import { GOOGLE_MAP_KEY } from "@/constants/constant";
import { useTheme } from "@/components/theme-provider";
import { apiCall } from "@/api/apiCall";
import { usePermissions } from "@/context/PermissionContext";
import LoadingComponent from "@/components/common/LoadingComponent";
import ErrorState from "@/components/common/ErrorStateComponent";
import ReactSelect from "@/components/common/ReactSelect";
import AreaChart from "@/components/BoardPack/components/charts/AreaChart";
import HourlyRevenueChart from "@/pages/HourlyRevenueChart";
import MapShimmer from "@/components/common/MapShimmer";
import {
  PaymentTableShimmer,
  PndListShimmer,
  StatsGridShimmer,
} from "@/components/child/SectionShimmer";

// ─── Device helpers ───────────────────────────────────────────────────────────
const getDeviceColor = (deviceType) => {
  if (!deviceType) return "#9CA3AF";
  const t = deviceType.toUpperCase();
  if (t === "APM") return "#8B5CF6";
  if (t === "EXIT") return "#F59E0B";
  if (t === "CASHIER") return "#10B981";
  if (t === "APP") return "#06B6D4";
  if (t === "HHD") return "#F97316";
  if (t === "BOOKING") return "#EC4899";
  if (t === "VALIDATION") return "#14B8A6";
  if (t === "ENTRY") return "#3B82F6";
  if (t === "POS") return "#84CC16";
  if (t === "VALIDATOR") return "#EF4444";
  return "#6366F1";
};

const getDeviceTypeName = (deviceType) => {
  if (!deviceType) return "—";
  return deviceType.toUpperCase();
};

const DEVICE_TYPE_ICONS = {
  APM: MonitorSmartphone,
  EXIT: LogOut,
  CASHIER: Banknote,
  APP: Smartphone,
  HHD: Tablet,
  BOOKING: CalendarCheck,
  VALIDATION: ShieldCheck,
  ENTRY: LogIn,
  POS: CreditCard,
  VALIDATOR: BadgeCheck,
};

const getDeviceTypeIcon = (deviceType) => {
  if (!deviceType) return CircleDot;
  return DEVICE_TYPE_ICONS[deviceType.toUpperCase()] || CircleDot;
};

const MARKER_ICON_PATHS = {
  APM: '<path d="M18 8V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h8"/><path d="M10 19v-3.96 3.15"/><path d="M7 19h5"/><rect width="6" height="10" x="16" y="12" rx="2"/>',
  EXIT: '<path d="m16 17 5-5-5-5"/><path d="M21 12H9"/><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>',
  CASHIER:
    '<rect width="20" height="12" x="2" y="6" rx="2"/><circle cx="12" cy="12" r="2"/><path d="M6 12h.01M18 12h.01"/>',
  APP: '<rect width="14" height="20" x="5" y="2" rx="2" ry="2"/><path d="M12 18h.01"/>',
  HHD: '<rect width="16" height="20" x="4" y="2" rx="2" ry="2"/><line x1="12" x2="12.01" y1="18" y2="18"/>',
  BOOKING:
    '<path d="M8 2v3"/><path d="M16 2v3"/><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18"/><path d="m9 15 2 2 4-4"/>',
  VALIDATION:
    '<path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"/><path d="m9 12 2 2 4-4"/>',
  ENTRY:
    '<path d="m10 17 5-5-5-5"/><path d="M15 12H3"/><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/>',
  POS: '<rect width="20" height="14" x="2" y="5" rx="2"/><line x1="2" x2="22" y1="10" y2="10"/>',
  VALIDATOR:
    '<path d="M3.85 8.62a4 4 0 0 1 4.78-4.77 4 4 0 0 1 6.74 0 4 4 0 0 1 4.78 4.78 4 4 0 0 1 0 6.74 4 4 0 0 1-4.77 4.78 4 4 0 0 1-6.75 0 4 4 0 0 1-4.78-4.77 4 4 0 0 1 0-6.76Z"/><path d="m9 12 2 2 4-4"/>',
  DEFAULT: '<circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="1"/>',
};

const getMarkerIconPaths = (deviceType) => {
  if (!deviceType) return MARKER_ICON_PATHS.DEFAULT;
  return MARKER_ICON_PATHS[deviceType.toUpperCase()] || MARKER_ICON_PATHS.DEFAULT;
};

const isGeneralGate = (gate) => {
  if (!gate) return false;
  const id = String(gate.gate_id ?? gate.gate ?? "")
    .trim()
    .toUpperCase();
  const name = String(gate.gate_name ?? "")
    .trim()
    .toUpperCase();
  if (id === "UNKNOWN") return true;
  if (!id && name === "UNKNOWN") return true;
  return false;
};

const getGateDisplayName = (gate) =>
  isGeneralGate(gate) ? "General" : gate?.gate_name || gate?.gate_id || gate?.gate || "Gate";

const resolveOpenSection = (activeSection, validIds, fallback) =>
  activeSection && validIds.includes(activeSection) ? activeSection : fallback;

const buildGeneralGateHoverHtml = (devices = []) => {
  return `<div style="font-family:sans-serif;min-width:190px;max-width:250px;">
    <p style="font-size:11px;font-weight:800;color:#111827;margin:0 0 4px 0;">General</p>
    <p style="font-size:11px;color:#374151;line-height:1.5;margin:0;">
      You can't view General's data directly. Open its hierarchy
      to see the data for its devices.
    </p>
  </div>`;
};

// ── OffStreet ⇄ Current Status cross-page sync ──────────────────────────────
// OffStreet page (OffStreetMap.jsx) jab bhi filter ya selection (country/ops/
// location/gate/device) set karta hai, wo isi sessionStorage key mein save
// karta hai. Current Status page load hote hi yahan se padh kar wahi filter
// aur wahi selection (ya agar exact cheez maujood na ho to us country/ops/
// location ka pehla available child) apne aap khol deta hai.
const OFFSTREET_SYNC_KEY = "offstreet_sync_v1";

// ── Reset on hard refresh (not on SPA navigation) ───────────────────────────
// sessionStorage tab band hone tak persist karta hai — refresh (F5) pe bhi
// clear nahi hota. Lekin humein chahiye: refresh ya bilkul naya page load ho
// to filter/selection reset ho kar default "All Locations" par aa jaye, jab
// ke isi tab ke andar ek page se dusre par (bina reload) jaate waqt sync
// pehle jaisa perfect kaam karta rahe.
//
// Navigation Timing API se pata chalta hai ke current document load asal
// mein browser reload tha ya normal navigation — aur yeh value poore
// tab-session mein (agle actual reload tak) same rehti hai, chahe yeh check
// kisi bhi page/component ke mount hone par chale. Isliye "window" par ek
// flag rakh kar sirf ek hi baar (jo bhi page pehle load ho) clear karte hain.
const resetOffstreetSyncOnHardReload = () => {
  if (typeof window === "undefined") return;
  if (window.__offstreetSyncResetChecked) return;
  window.__offstreetSyncResetChecked = true;
  try {
    const navEntries = window.performance?.getEntriesByType?.("navigation");
    const navType =
      navEntries && navEntries[0]
        ? navEntries[0].type
        : window.performance?.navigation?.type === 1
          ? "reload"
          : "navigate"; // legacy fallback
    if (navType === "reload") {
      sessionStorage.removeItem(OFFSTREET_SYNC_KEY);
    }
  } catch {
    // Navigation Timing API unavailable — kuch mat karo, purana behavior chalne do
  }
};
resetOffstreetSyncOnHardReload();

const readOffstreetSync = () => {
  try {
    const raw = sessionStorage.getItem(OFFSTREET_SYNC_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const writeOffstreetSync = (partial) => {
  try {
    const current = readOffstreetSync() || {};
    sessionStorage.setItem(OFFSTREET_SYNC_KEY, JSON.stringify({ ...current, ...partial }));
  } catch {
    // sessionStorage unavailable (e.g. private mode) — sync silently skip ho jaye
  }
};

const getCountryOps = (countryObj) => {
  if (!countryObj) return [];
  if (Array.isArray(countryObj.cities) && countryObj.cities.length) {
    return countryObj.cities.flatMap((city) =>
      (city.ops || []).map((op) => ({
        ...op,
        city_cd: city.city_cd,
        city_name: city.city_name,
      }))
    );
  }
  return countryObj.ops || [];
};

// Real polygon coordinates — flat {lat,lng}[] or nested rings. Empty/null => no real polygon.
const normalizePolygonCoords = (coords) => {
  if (!Array.isArray(coords) || !coords.length) return [];
  let result = coords;
  while (Array.isArray(result[0])) result = result.flat();
  return result.filter((p) => p && typeof p.lat === "number" && typeof p.lng === "number");
};

const parseApiCoords = (coords) => {
  if (!Array.isArray(coords) || !coords.length) return [];
  let result = coords;
  while (Array.isArray(result[0])) result = result.flat();
  return result
    .map((c) => {
      const lat = parseFloat(c?.lat ?? c?.latitude);
      const lng = parseFloat(c?.lng ?? c?.longitude);
      if (isNaN(lat) || isNaN(lng)) return null;
      return { lat, lng };
    })
    .filter(Boolean);
};

const polygonCentroid = (coords) => ({
  lat: coords.reduce((s, p) => s + p.lat, 0) / coords.length,
  lng: coords.reduce((s, p) => s + p.lng, 0) / coords.length,
});

const buildAdaptiveBbox = (pnds, { minPadM = 20, maxPadM = 80, ratio = 0.18 } = {}) => {
  if (!Array.isArray(pnds) || !pnds.length) return [];
  const raw = pnds
    .map((p) => {
      const lat = typeof p.lat === "number" ? p.lat : parseFloat(p.latitude ?? p.lat);
      const lng = typeof p.lng === "number" ? p.lng : parseFloat(p.longitude ?? p.lng);
      if (isNaN(lat) || isNaN(lng)) return null;
      return { lat, lng };
    })
    .filter(Boolean);
  if (!raw.length) return [];
  let minLat = Infinity, maxLat = -Infinity, minLng = Infinity, maxLng = -Infinity;
  raw.forEach((p) => {
    minLat = Math.min(minLat, p.lat);
    maxLat = Math.max(maxLat, p.lat);
    minLng = Math.min(minLng, p.lng);
    maxLng = Math.max(maxLng, p.lng);
  });
  const latSpanM = Math.max((maxLat - minLat) * 111320, 0);
  const latCenter = (minLat + maxLat) / 2;
  const cosLat = Math.cos((latCenter * Math.PI) / 180) || 1;
  const lngSpanM = Math.max((maxLng - minLng) * 111320 * cosLat, 0);
  const spanM = Math.max(latSpanM, lngSpanM);
  const padM = Math.min(maxPadM, Math.max(minPadM, spanM * ratio || minPadM));
  const latDelta = padM / 111320;
  const lngDelta = padM / (111320 * cosLat);
  return [
    { lat: minLat - latDelta, lng: minLng - lngDelta },
    { lat: minLat - latDelta, lng: maxLng + lngDelta },
    { lat: maxLat + latDelta, lng: maxLng + lngDelta },
    { lat: maxLat + latDelta, lng: minLng - lngDelta },
  ];
};

const resolveAreaPolygon = (apiCoords, devices, padOpts) => {
  const api = parseApiCoords(apiCoords);
  if (api.length >= 3) return api;
  return buildAdaptiveBbox(devices, padOpts);
};

const countsFromCountryNode = (countryObj) => {
  const ops = getCountryOps(countryObj);
  let locations = 0,
    gates = 0,
    devices = 0,
    activeDevices = 0;
  ops.forEach((op) => {
    (op.locations || []).forEach((loc) => {
      locations += 1;
      (loc.gates || []).forEach((gate) => {
        gates += 1;
        (gate.devices || []).forEach((d) => {
          devices += 1;
          if (d.device_type) activeDevices += 1;
        });
      });
    });
  });
  return {
    ops: ops.length,
    locations,
    gates,
    devices,
    activeDevices,
    inactiveDevices: devices - activeDevices,
  };
};

const countsFromCityFlat = (city) => {
  const streets = city?.streets || [];
  const gateSet = new Set();
  let devices = 0,
    activeDevices = 0;
  streets.forEach((st) => {
    (st.pnds || []).forEach((p) => {
      devices += 1;
      if (p.device_type) activeDevices += 1;
      gateSet.add(p.gate_id || p.gate_name || "Unknown");
    });
  });
  return {
    locations: streets.length,
    gates: gateSet.size,
    devices,
    activeDevices,
    inactiveDevices: devices - activeDevices,
  };
};

const countsFromStreetFlat = (street) => {
  const pnds = street?.pnds || [];
  const gateSet = new Set();
  let activeDevices = 0;
  pnds.forEach((p) => {
    if (p.device_type) activeDevices += 1;
    gateSet.add(p.gate_id || p.gate_name || "Unknown");
  });
  return {
    gates: gateSet.size,
    devices: pnds.length,
    activeDevices,
    inactiveDevices: pnds.length - activeDevices,
  };
};

const filterPndsByGate = (pnds, gate) => {
  const gateId = gate?.gate_id ?? gate?.gate;
  const gateName = gate?.gate_name;
  const target = String(gateId || gateName || "Unknown").toUpperCase();
  return (pnds || []).filter((p) => {
    const pGateId = String(p.gate_id || p.gate_name || "Unknown").toUpperCase();
    const pGateName = String(p.gate_name || "").toUpperCase();
    return pGateId === target || (gateName && pGateName === String(gateName).toUpperCase());
  });
};

const countsForGateInStreet = (street, gate) => {
  const filtered = filterPndsByGate(street?.pnds, gate);
  const activeDevices = filtered.filter((p) => p.device_type).length;
  return {
    devices: filtered.length,
    activeDevices,
    inactiveDevices: filtered.length - activeDevices,
  };
};

const devicesFromCountryNode = (countryObj) => {
  const list = [];
  getCountryOps(countryObj).forEach((op) =>
    (op.locations || []).forEach((loc) =>
      (loc.gates || []).forEach((gate) => (gate.devices || []).forEach((d) => list.push(d)))
    )
  );
  return list;
};
const devicesFromCityFlat = (city) => (city?.streets || []).flatMap((st) => st.pnds || []);
const devicesFromStreetFlat = (street) => street?.pnds || [];

const STREET_COLORS = [
  "#F59E0B",
  "#10B981",
  "#EC4899",
  "#14B8A6",
  "#F97316",
  "#06B6D4",
  "#84CC16",
  "#A78BFA",
  "#EF4444",
  "#3B82F6",
];

const darkenColor = (hex, amount = 0.35) => {
  if (!hex || typeof hex !== "string" || !hex.startsWith("#")) return hex;
  const h = hex.slice(1);
  const full =
    h.length === 3
      ? h
          .split("")
          .map((c) => c + c)
          .join("")
      : h;
  if (full.length !== 6) return hex;
  const num = parseInt(full, 16);
  let r = (num >> 16) & 255,
    g = (num >> 8) & 255,
    b = num & 255;
  r = Math.max(0, Math.round(r * (1 - amount)));
  g = Math.max(0, Math.round(g * (1 - amount)));
  b = Math.max(0, Math.round(b * (1 - amount)));
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
};

const CURRENCY_OPTIONS = [
  { value: "local", label: "Local Currency" },
  { value: "reporting", label: "Reporting Currency" },
];

const getMeasureParam = (currency) =>
  currency === "reporting" ? "Amount in RC" : "Amount_Collected";

const getDarkMapStyles = (hideLabels = false) => [
  { elementType: "geometry", stylers: [{ color: "#1a1d28" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#1a1d28" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#8b92a8" }] },
  {
    featureType: "administrative.locality",
    elementType: "labels.text.fill",
    stylers: [{ color: "#d0d3de" }],
  },
  { featureType: "poi", elementType: "labels.text.fill", stylers: [{ color: "#6b7280" }] },
  { featureType: "poi.park", elementType: "geometry", stylers: [{ color: "#232938" }] },
  { featureType: "road", elementType: "geometry", stylers: [{ color: "#2c3142" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#0f1419" }] },
  ...(hideLabels ? [{ elementType: "labels", stylers: [{ visibility: "off" }] }] : []),
];

const getLightMapStyles = (hideLabels = false) => [
  { featureType: "poi", elementType: "labels.text.fill", stylers: [{ color: "#757575" }] },
  { featureType: "poi.park", elementType: "geometry", stylers: [{ color: "#e5e7eb" }] },
  { featureType: "road", elementType: "geometry.fill", stylers: [{ color: "#ffffff" }] },
  { featureType: "road", elementType: "geometry.stroke", stylers: [{ color: "#d1d5db" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#dbeafe" }] },
  ...(hideLabels ? [{ elementType: "labels", stylers: [{ visibility: "off" }] }] : []),
];

const MONTH_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const getRawMonth = (month) => {
  if (!month) return MONTH_NAMES[new Date().getMonth()];
  if (month.includes("-")) return month.split("-")[1];
  return month;
};

const buildMonthParam = (month) => {
  if (!month) return null;
  const GROUP_NAMES = ["Full_Year", "FY"];
  if (GROUP_NAMES.includes(month)) return month;
  if (month.includes("-")) return month;
  return month;
};

// ============================================
// AccordionSection — reusable toggle section
// ============================================
const AccordionSection = ({
  icon,
  title,
  badge,
  children,
  defaultOpen = false,
  id,
  openId,
  onToggle,
}) => {
  const isControlled = openId !== undefined && onToggle !== undefined;
  const [localOpen, setLocalOpen] = useState(defaultOpen);
  const open = isControlled
    ? openId instanceof Set
      ? openId.has(id)
      : Array.isArray(openId)
        ? openId.includes(id)
        : openId === id
    : localOpen;
  const toggle = () => {
    if (isControlled) onToggle(id);
    else setLocalOpen((v) => !v);
  };

  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden">
      <button
        onClick={toggle}
        className="w-full flex items-center gap-2.5 px-3.5 py-2.5 hover:bg-secondary/50 transition-colors text-left"
      >
        <div className="w-7 h-7 rounded-lg bg-secondary flex items-center justify-center flex-shrink-0 text-muted-foreground">
          {icon}
        </div>
        <span className="flex-1 text-xs font-semibold text-foreground">{title}</span>
        {badge &&
          (Array.isArray(badge) ? (
            badge.map((b, i) => (
              <span
                key={i}
                className="text-[10px] text-muted-foreground bg-secondary border border-border px-2 py-0.5 rounded-full mr-1"
              >
                {b}
              </span>
            ))
          ) : (
            <span className="text-[10px] text-muted-foreground bg-secondary border border-border px-2 py-0.5 rounded-full mr-1">
              {badge}
            </span>
          ))}
        <ChevronDown
          className={`w-3.5 h-3.5 text-muted-foreground flex-shrink-0 transition-transform duration-200 ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="body"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <div className="border-t border-border p-3.5">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ============================================
// PaymentBreakdownCard
// ============================================
const PaymentBreakdownCard = ({ breakdown }) => {
  if (!breakdown) return null;

  const [openGroups, setOpenGroups] = useState({});

  const toggleGroup = (key) => {
    setOpenGroups((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const groups = [
    {
      key: "app_web",
      label: "App / Web",
      items: [
        { key: "card", label: "Card" },
        { key: "ewallet", label: "eWallet" },
        { key: "web_card", label: "Web Card" },
      ],
    },
    {
      key: "pnd",
      label: "Devices",
      items: [
        { key: "card", label: "Card" },
        { key: "notes", label: "Notes" },
        { key: "coins", label: "Coins" },
        { key: "ewallet", label: "eWallet" },
      ],
    },
  ];

  const totalData = breakdown?.total;

  const DataRow = ({ label, data }) => {
    return (
      <div className="grid grid-cols-[1.8fr_1fr_0.8fr_0.6fr_0.6fr_0.8fr] gap-1 items-center px-3.5 py-2 border-b border-border/50 hover:bg-zinc-100 dark:hover:bg-zinc-800/60 transition-colors">
        <p className="text-[10px] font-medium text-foreground truncate">{label}</p>
        <p className="text-[10px] tabular-nums font-semibold text-foreground">
          {data?.collection ?? "0"}
        </p>
        <p className="text-[10px] tabular-nums text-foreground">{data?.ticket_count ?? "0"}</p>
        <p className="text-[10px] tabular-nums text-foreground">{data?.als ?? "0"}</p>
        <p className="text-[10px] tabular-nums text-foreground">{data?.atv ?? "0"}</p>
        <p className="text-[9px] tabular-nums font-semibold text-muted-foreground">
          {data?.revenue_percentage ?? "0.00%"}
        </p>
      </div>
    );
  };

  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden w-full">
      <div className="border-b border-border">
        <div className="flex items-center gap-2 px-3.5 pt-2.5 pb-1.5">
          <CreditCard className="w-3.5 h-3.5 text-muted-foreground" />
          <span className="text-xs font-medium text-foreground">Payment Breakdown</span>
        </div>
        <div className="grid grid-cols-[1.8fr_1fr_0.8fr_0.6fr_0.6fr_0.8fr] gap-1 px-3.5 py-1.5 bg-zinc-100 dark:bg-zinc-800">
          {["Method", "Revenue", "Tickets", "ALS", "ATV", "%"].map((h) => (
            <p
              key={h}
              className="text-[8px] uppercase tracking-widest text-muted-foreground font-semibold"
            >
              {h}
            </p>
          ))}
        </div>
      </div>

      {groups.map((group) => {
        const groupData = breakdown?.[group.key];
        const groupTotal = groupData?.total;
        const isOpen = !!openGroups[group.key];

        return (
          <div key={group.key} className="border-b border-border last:border-b-0">
            <button
              onClick={() => toggleGroup(group.key)}
              className="w-full grid grid-cols-[1.8fr_1fr_0.8fr_0.6fr_0.6fr_0.8fr] gap-1 items-center px-3.5 py-2.5 
              bg-[#ddebf7] hover:bg-[#d2e3f3] transition-colors text-left"
            >
              <span className="text-[10px] tabular-nums font-bold text-foreground">
                {group.label}
              </span>
              <span className="text-[10px] tabular-nums font-bold text-foreground">
                {groupTotal?.collection ?? "0"}
              </span>
              <span className="text-[10px] tabular-nums font-bold text-foreground">
                {groupTotal?.ticket_count ?? "0"}
              </span>
              <span className="text-[10px] tabular-nums font-bold text-foreground">
                {groupTotal?.als ?? "0"}
              </span>
              <span className="text-[10px] tabular-nums font-bold text-foreground">
                {groupTotal?.atv ?? "0"}
              </span>
              <div className="flex items-center gap-1">
                <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-secondary border border-border text-foreground tabular-nums">
                  {groupTotal?.revenue_percentage ?? "0%"}
                </span>
                <ChevronDown
                  className={`w-3 h-3 text-muted-foreground flex-shrink-0 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
                />
              </div>
            </button>

            <AnimatePresence initial={false}>
              {isOpen && (
                <motion.div
                  key="body"
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.18, ease: "easeInOut" }}
                  className="overflow-hidden"
                >
                  {group.items.map(({ key, label }) => (
                    <DataRow key={key} label={label} data={groupData?.[key]} />
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        );
      })}

      {totalData && (
        <div className="grid grid-cols-[1.8fr_1fr_0.8fr_0.6fr_0.6fr_0.8fr] gap-1 items-center px-3.5 py-2.5 bg-zinc-200 dark:bg-zinc-700">
          <p className="text-[10px] font-black text-foreground uppercase tracking-wide">
            Grand Total
          </p>
          <p className="text-[11px] font-black tabular-nums text-foreground">
            {totalData.collection ?? 0}
          </p>
          <p className="text-[11px] font-black tabular-nums text-foreground">
            {totalData.ticket_count ?? 0}
          </p>
          <p className="text-[11px] font-black tabular-nums text-foreground">
            {totalData.als ?? 0}
          </p>
          <p className="text-[11px] font-black tabular-nums text-foreground">
            {totalData.atv ?? 0}
          </p>
          <p className="text-[10px] font-black tabular-nums text-foreground">
            {totalData.revenue_percentage ?? "100%"}
          </p>
        </div>
      )}
    </div>
  );
};

// ============================================
// CityRevenueSection
// ============================================
const CityRevenueSection = ({
  city,
  filterMonth,
  filterYear,
  filterThreshold,
  onBreakdownLoad,
}) => {
  const now = new Date();
  const displayMonth = filterMonth || MONTH_NAMES[now.getMonth()];
  const displayYear = filterYear || String(now.getFullYear());
  const threshold = filterThreshold || "";

  const [revenueData, setRevenueData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [userClosedPanel, setUserClosedPanel] = useState(false);

  const cityId = city?.city_send_name || city?.city_cd;

  const fetchRevenue = useCallback(async () => {
    if (!cityId) return;
    setLoading(true);
    setError(null);
    setRevenueData(null);
    try {
      const monthParam = buildMonthParam(displayMonth);
      const url = `/get-pnd-collection?city=true&device_id=${cityId}&month=${monthParam}&year=${displayYear}${threshold ? `&threshold=${threshold}` : ""}`;
      const response = await apiCall("get", url, null, null, false, false);

      if (response?.success) {
        const payload =
          response.data?.city_breakdown ?? response.city_breakdown ?? response.data ?? response;
        setRevenueData(payload);

        const belowSet = new Set();
        const noTxSet = new Set();
        (payload.streets || []).forEach((st) => {
          (st.pnds || []).forEach((p) => {
            if (p.below_threshold === true) belowSet.add(String(p.pnd));
            if (p.no_transaction === true) noTxSet.add(String(p.pnd));
          });
        });
        onBreakdownLoad?.({ belowThreshold: belowSet, noTransaction: noTxSet });
      }
    } catch (err) {
      setError(typeof err === "string" ? err : "Failed to fetch revenue");
    } finally {
      setLoading(false);
    }
  }, [cityId, displayMonth, displayYear, threshold, onBreakdownLoad]);

  useEffect(() => {
    fetchRevenue();
  }, [fetchRevenue]);

  return (
    <div>
      {loading && <PaymentTableShimmer groups={3} rowsPerGroup={2} />}
      {!loading && error && (
        <div className="flex flex-col items-center gap-2 py-5">
          <p className="text-xs text-muted-foreground text-center">{error}</p>
          <button
            onClick={fetchRevenue}
            className="text-[10px] font-semibold text-primary hover:underline"
          >
            Retry
          </button>
        </div>
      )}
      {!cityId && !loading && (
        <p className="text-xs text-muted-foreground text-center py-5">
          No city ID available for revenue lookup.
        </p>
      )}
      {!loading && !error && revenueData && revenueData.payment_breakdown && (
        <PaymentBreakdownCard breakdown={revenueData.payment_breakdown} />
      )}
    </div>
  );
};

// ============================================
// StreetRevenueSection
// ============================================
const StreetRevenueSection = ({
  street,
  filterMonth,
  filterYear,
  filterThreshold,
  onSelectPnd,
  onBreakdownLoad,
}) => {
  const now = new Date();
  const displayMonth = filterMonth || MONTH_NAMES[now.getMonth()];
  const displayYear = filterYear || String(now.getFullYear());
  const threshold = filterThreshold || "";

  const [revenueData, setRevenueData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const streetId = street?.street_send_name || street?.street_cd;

  const fetchRevenue = useCallback(async () => {
    if (!streetId) return;
    setLoading(true);
    setError(null);
    setRevenueData(null);
    try {
      const monthParam = buildMonthParam(displayMonth);
      const url = `/get-pnd-collection?street=true&device_id=${streetId}&month=${monthParam}&year=${displayYear}${threshold ? `&threshold=${threshold}` : ""}`;
      const response = await apiCall("get", url, null, null, false, false);

      if (response?.success) {
        setRevenueData(response);

        const belowSet = new Set();
        const noTxSet = new Set();
        (response.street_breakdown || []).forEach((p) => {
          if (p.below_threshold === true) belowSet.add(String(p.pnd));
          if (p.no_transaction === true) noTxSet.add(String(p.pnd));
        });
        onBreakdownLoad?.({ belowThreshold: belowSet, noTransaction: noTxSet });
      } else {
        setError(response?.message || "No revenue data found");
      }
    } catch (err) {
      setError(typeof err === "string" ? err : "Failed to fetch revenue");
    } finally {
      setLoading(false);
    }
  }, [streetId, displayMonth, displayYear, threshold, onBreakdownLoad]);

  useEffect(() => {
    fetchRevenue();
  }, [fetchRevenue]);

  return (
    <div>
      {loading && <StatsGridShimmer count={4} />}
      {!loading && error && (
        <div className="flex flex-col items-center gap-2 py-5">
          <p className="text-xs text-muted-foreground text-center">{error}</p>
          <button
            onClick={fetchRevenue}
            className="text-[10px] font-semibold text-primary hover:underline"
          >
            Retry
          </button>
        </div>
      )}
      {!streetId && !loading && (
        <p className="text-xs text-muted-foreground text-center py-5">
          No street ID available for revenue lookup.
        </p>
      )}
      {!loading && !error && revenueData && (
        <>
          {revenueData.payment_breakdown && (
            <div className="mb-3">
              <PaymentBreakdownCard breakdown={revenueData.payment_breakdown} />
            </div>
          )}
          {Array.isArray(revenueData.street_breakdown) &&
            revenueData.street_breakdown.length > 0 && (
              <div className="rounded-xl border border-border bg-card overflow-hidden">
                <div className="flex items-center gap-2 px-3.5 py-2.5 border-b border-border">
                  <CircleDot className="w-3.5 h-3.5 text-muted-foreground" />
                  <span className="text-xs font-medium text-foreground">Devices Breakdown</span>
                  <span className="ml-auto text-[10px] text-muted-foreground bg-secondary border border-border px-2 py-0.5 rounded-full">
                    {revenueData.street_breakdown.length} devices
                  </span>
                </div>
                <div className="grid grid-cols-[1fr_1fr_1fr_1fr_1fr_auto] gap-2 px-3.5 py-2 bg-secondary/60 border-b border-border">
                  {["Devices", "Revenue", "Tickets", "ALS", "ATV"].map((h) => (
                    <p
                      key={h}
                      className="text-[8px] uppercase tracking-widest text-muted-foreground font-semibold"
                    >
                      {h}
                    </p>
                  ))}
                </div>
                <div className="divide-y divide-border max-h-64 overflow-y-scroll cb-scroll">
                  {revenueData.street_breakdown.map((item, idx) => (
                    <div
                      key={`pnd_row_${item.pnd ?? idx}`}
                      onClick={() => onSelectPnd?.(item)}
                      className="grid grid-cols-[1fr_1fr_1fr_1fr_1fr_auto] gap-2 items-center px-3.5 py-2.5 hover:bg-secondary/40 transition-colors cursor-pointer"
                    >
                      <p className="text-[11px] font-mono font-semibold text-foreground">
                        #{item.pnd}
                      </p>
                      <p className="text-[11px] font-semibold">{item.collection ?? 0}</p>
                      <p className="text-[11px] text-foreground">{item.ticket_count ?? 0}</p>
                      <p className="text-[11px] text-foreground">{item.als ?? 0}</p>
                      <p className="text-[11px] text-foreground">{item.atv ?? 0}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
        </>
      )}
    </div>
  );
};

// ============================================
// PND RevenueSection
// ============================================
const RevenueSection = ({ pnd, filterMonth, filterYear, filterThreshold }) => {
  const now = new Date();
  const displayMonth = filterMonth || MONTH_NAMES[now.getMonth()];
  const displayYear = filterYear || String(now.getFullYear());
  const threshold = filterThreshold || "";

  const [revenueData, setRevenueData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const deviceId = pnd?.pnd_number || pnd?.device_id;

  const fetchRevenue = useCallback(async () => {
    if (!deviceId) return;
    setLoading(true);
    setError(null);
    setRevenueData(null);
    try {
      const monthParam = buildMonthParam(displayMonth)
        ? displayMonth
        : displayMonth.includes("-")
          ? displayMonth
          : `01-${displayMonth}`;

      const url = `/get-pnd-collection?device_id=${deviceId}&month=${monthParam}&year=${displayYear}${threshold ? `&threshold=${threshold}` : ""}`;
      const response = await apiCall("get", url, null, null, false, false);

      if (response?.success) {
        const payload = response.data ?? response;
        if (payload.collection !== null || payload.ticket_count !== null) {
          setRevenueData(payload);
        } else {
          setError("No revenue data found for this period");
        }
      } else {
        setError(response?.message || "No revenue data found");
      }
    } catch (err) {
      setError(typeof err === "string" ? err : "Failed to fetch revenue");
    } finally {
      setLoading(false);
    }
  }, [deviceId, displayMonth, displayYear, threshold]);

  useEffect(() => {
    fetchRevenue();
  }, [fetchRevenue]);

  const extraFields = revenueData
    ? Object.entries(revenueData).filter(
        ([key]) =>
          ![
            "collection",
            "total_collection",
            "total",
            "ticket_count",
            "device_id",
            "month",
            "year",
            "success",
            "monthly_data",
          ].includes(key)
      )
    : [];

  return (
    <div>
      {loading && (
        <div className="space-y-3">
          <PaymentTableShimmer groups={2} rowsPerGroup={2} />
          <PndListShimmer rows={5} />
        </div>
      )}
      {!loading && error && (
        <div className="flex flex-col items-center gap-2 py-5">
          <p className="text-xs text-muted-foreground text-center">{error}</p>
          <button
            onClick={fetchRevenue}
            className="text-[10px] font-semibold text-primary hover:underline"
          >
            Retry
          </button>
        </div>
      )}
      {!deviceId && !loading && (
        <p className="text-xs text-muted-foreground text-center py-5">
          No device ID available for revenue lookup.
        </p>
      )}
      {!loading && !error && revenueData && (
        <div className="grid grid-cols-2 gap-2.5">
          <div className="rounded-lg bg-secondary p-3">
            <p className="text-[9px] uppercase tracking-widest text-muted-foreground mb-1.5">
              Revenue
            </p>
            <p className="text-[22px] font-medium leading-none">
              {revenueData.collection ?? revenueData.total_collection ?? revenueData.total ?? 0}
            </p>
          </div>
          <div className="rounded-lg bg-secondary p-3">
            <p className="text-[9px] uppercase tracking-widest text-muted-foreground mb-1.5">
              Ticket Count
            </p>
            <p className="text-[22px] font-medium text-foreground leading-none">
              {revenueData.ticket_count ?? 0}
            </p>
          </div>
          {extraFields.map(([key, value], idx) => (
            <div key={`rev_${key}_${idx}`} className="rounded-lg bg-secondary p-3">
              <p className="text-[9px] uppercase tracking-widest text-muted-foreground mb-1.5">
                {key.replace(/_/g, " ")}
              </p>
              <p className="text-[22px] font-medium text-foreground leading-none">
                {typeof value === "object" ? JSON.stringify(value) : String(value ?? 0)}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ============================================
// CityStreetsRevenueSummary
// ============================================
const CityStreetsRevenueSummary = ({
  city,
  filterMonth,
  filterYear,
  filterThreshold,
  onSelectStreet,
}) => {
  const now = new Date();
  const displayMonth = filterMonth || MONTH_NAMES[now.getMonth()];
  const displayYear = filterYear || String(now.getFullYear());
  const threshold = filterThreshold || "";

  const [breakdown, setBreakdown] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [streetSearch, setStreetSearch] = useState("");

  const cityId = city?.city_send_name || city?.city_cd;

  const fetchBreakdown = useCallback(async () => {
    if (!cityId) return;
    setLoading(true);
    setError(null);
    setBreakdown(null);
    try {
      const monthParam = buildMonthParam(displayMonth);
      const url = `/get-pnd-collection?city=true&device_id=${cityId}&month=${monthParam}&year=${displayYear}${threshold ? `&threshold=${threshold}` : ""}`;
      const response = await apiCall("get", url, null, null, false, false);
      if (response?.success) {
        const payload =
          response.data?.city_breakdown ?? response.city_breakdown ?? response.data ?? response;
        setBreakdown(payload);
      } else {
        setError(response?.message || "No data");
      }
    } catch {
      setError("Failed to fetch");
    } finally {
      setLoading(false);
    }
  }, [cityId, displayMonth, displayYear, threshold]);

  useEffect(() => {
    fetchBreakdown();
  }, [fetchBreakdown]);

  if (loading) {
    return <PndListShimmer rows={6} withHeader={false} />;
  }

  if (error) {
    return (
      <div className="flex flex-col items-center gap-2 py-5">
        <p className="text-xs text-muted-foreground text-center">{error}</p>
        <button
          onClick={fetchBreakdown}
          className="text-[10px] font-semibold text-primary hover:underline"
        >
          Retry
        </button>
      </div>
    );
  }

  const streets = breakdown?.streets || [];
  if (!streets.length) {
    return (
      <p className="text-xs text-muted-foreground text-center py-5">No streets data available.</p>
    );
  }

  const q = streetSearch.trim().toLowerCase();
  const filteredStreets = q ? streets.filter((s) => s.street?.toLowerCase().includes(q)) : streets;

  return (
    <div className="space-y-3">
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
        <input
          type="text"
          placeholder="Search streets..."
          value={streetSearch}
          onChange={(e) => setStreetSearch(e.target.value)}
          className="w-full pl-8 pr-7 py-1.5 text-xs rounded-lg bg-background border border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary transition-all"
        />
        {streetSearch && (
          <button
            onClick={() => setStreetSearch("")}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          >
            <X className="w-3 h-3" />
          </button>
        )}
      </div>

      {q && (
        <p className="text-[10px] text-muted-foreground px-0.5">
          {filteredStreets.length === 0
            ? `No streets matched "${streetSearch}"`
            : `${filteredStreets.length} of ${streets.length} streets`}
        </p>
      )}

      <div className="max-h-[480px] overflow-y-auto cb-scroll space-y-2 pr-0.5">
        {filteredStreets.length === 0 && q ? (
          <div className="flex flex-col items-center justify-center py-8 gap-2 text-muted-foreground">
            <Search className="w-5 h-5 opacity-30" />
            <p className="text-xs">No results for "{streetSearch}"</p>
          </div>
        ) : (
          filteredStreets.map((street, idx) => (
            <StreetRevenueSummaryRow
              key={`${street.street}_${idx}`}
              street={street}
              cityStreets={city?.streets || []}
              onSelectStreet={onSelectStreet}
            />
          ))
        )}
      </div>
    </div>
  );
};

// ============================================
// OpsRevenueSummaryRow — City panel ke liye (Ops breakdown expandable cards)
// ============================================
const OpsRevenueSummaryRow = ({ opsItem, totalAmount, onSelectOps, open, onToggle }) => {
  const toNum = (v) => Number(String(v ?? 0).replace(/,/g, "")) || 0;
  const formatMoney = (v) =>
    toNum(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const opsAmount = Number(opsItem.total_amount ?? 0);
  const opsNotesCoins = Number(opsItem.total_notes_coins ?? 0);
  const opsLocations = opsItem.locations || [];
  const locDeviceCount = (loc) =>
    (loc.devices || (loc.gates || []).flatMap((g) => g.devices || [])).length;
  const opsDevices = opsLocations.reduce((s, loc) => s + locDeviceCount(loc), 0);

  // Contribution percentage
  const contributionPct = totalAmount > 0 ? (opsAmount / totalAmount) * 100 : 0;

  const handleOpsClick = () => {
    onSelectOps?.(opsItem.raw || opsItem);
    if (!open) onToggle?.();
  };

  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden">
      {/* Header Row */}
      <div className="w-full flex items-center gap-2.5 px-3.5 py-2.5 hover:bg-secondary/50 transition-colors">
        <button
          onClick={(e) => {
            e.stopPropagation();
            onToggle?.();
          }}
          className="flex-shrink-0 p-0.5 rounded hover:opacity-70 transition-opacity"
        >
          <div className="w-2 h-2 rounded-full bg-blue-500" />
        </button>

        <button
          onClick={handleOpsClick}
          className="flex-1 flex items-center gap-2 text-left min-w-0"
        >
          <span className="flex-1 text-xs font-semibold text-foreground truncate">
            {opsItem.ops_name}
          </span>
          <span className="text-[9px] font-mono text-muted-foreground flex-shrink-0">
            {opsItem.ops}
          </span>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <span className="text-[8px] font-bold tabular-nums text-foreground bg-secondary px-2 py-0.5 rounded-full">
              {formatMoney(opsAmount)}
            </span>
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-secondary text-muted-foreground">
              {contributionPct.toFixed(2)}%
            </span>
          </div>
        </button>

        <button
          onClick={(e) => {
            e.stopPropagation();
            onToggle?.();
          }}
          className="flex-shrink-0 p-0.5 rounded hover:opacity-70 transition-opacity"
        >
          <ChevronDown
            className={`w-3.5 h-3.5 text-muted-foreground transition-transform duration-200 ${open ? "rotate-180" : ""}`}
          />
        </button>
      </div>

      {/* Progress Bar */}
      <div className="px-3.5 pb-2">
        <div className="w-full h-1 bg-secondary rounded-full overflow-hidden">
          <div
            className="h-full rounded-full bg-primary transition-all duration-500"
            style={{ width: `${Math.min(contributionPct, 100)}%` }}
          />
        </div>
      </div>

      {/* Expanded Content */}
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="body"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <div className="border-t border-border p-3.5 space-y-3">
              {/* Summary Cards */}
              <div className="grid grid-cols-2 gap-2">
                <div className="rounded-lg bg-primary/10 border border-primary/20 p-2.5">
                  <p className="text-[8px] uppercase tracking-widest text-muted-foreground mb-1">
                    Total Amount
                  </p>
                  <p className="text-lg font-bold tabular-nums leading-none text-primary">
                    {formatMoney(opsAmount)}
                  </p>
                </div>
                <div className="rounded-lg bg-secondary p-2.5">
                  <p className="text-[8px] uppercase tracking-widest text-muted-foreground mb-1">
                    Notes / Coins
                  </p>
                  <p className="text-sm font-bold tabular-nums leading-none text-foreground">
                    {formatMoney(opsNotesCoins)}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div className="rounded-lg bg-secondary p-2.5">
                  <p className="text-[8px] uppercase tracking-widest text-muted-foreground mb-1">
                    Total Locations
                  </p>
                  <p className="text-sm font-bold tabular-nums leading-none text-foreground">
                    {opsLocations.length}
                  </p>
                </div>
                <div className="rounded-lg bg-secondary p-2.5">
                  <p className="text-[8px] uppercase tracking-widest text-muted-foreground mb-1">
                    Total Devices
                  </p>
                  <p className="text-sm font-bold tabular-nums leading-none text-foreground">
                    {opsDevices}
                  </p>
                </div>
              </div>

              {/* Locations Breakdown Table */}
              {opsLocations.length > 0 && (
                <div className="rounded-xl border border-border overflow-hidden">
                  <div className="grid grid-cols-[1.1fr_0.5fr_0.8fr_0.8fr_0.7fr] gap-1.5 px-3 py-2 bg-secondary/60 border-b border-border">
                    {["Location", "Devices", "Amount", "Notes/Coins", "Contrib"].map((h) => (
                      <p
                        key={h}
                        className="text-[8px] uppercase tracking-widest text-muted-foreground font-semibold"
                      >
                        {h}
                      </p>
                    ))}
                  </div>
                  <div className="max-h-52 overflow-y-auto cb-scroll divide-y divide-border">
                    {[...opsLocations]
                      .sort((a, b) => Number(b.total_amount ?? 0) - Number(a.total_amount ?? 0))
                      .map((loc, li) => {
                        const locDevices = locDeviceCount(loc);
                        const locAmount = Number(loc.total_amount ?? 0);
                        const locNotesCoins = Number(loc.total_notes_coins ?? 0);
                        const locContributionPct =
                          opsAmount > 0 ? (locAmount / opsAmount) * 100 : 0;
                        return (
                          <div
                            key={`loc_${loc.location}_${li}`}
                            className="px-3 py-2 hover:bg-secondary/40 transition-colors"
                          >
                            <div className="grid grid-cols-[1.1fr_0.5fr_0.8fr_0.8fr_0.7fr] gap-1.5 items-center">
                              <div className="flex items-center gap-1 min-w-0">
                                <MapPin className="w-2.5 h-2.5 text-emerald-400 flex-shrink-0" />
                                <p className="text-[10px] font-semibold text-foreground truncate">
                                  {loc.location}
                                </p>
                              </div>
                              <p className="text-[10px] text-muted-foreground tabular-nums">
                                {locDevices}
                              </p>
                              <p className="text-[10px] font-semibold tabular-nums text-foreground">
                                {formatMoney(locAmount)}
                              </p>
                              <p className="text-[10px] font-semibold tabular-nums text-foreground">
                                {formatMoney(locNotesCoins)}
                              </p>
                              <div className="space-y-0.5">
                                <p className="text-[9px] font-extrabold tabular-nums text-primary">
                                  {locContributionPct.toFixed(2)}%
                                </p>
                                <div className="w-full h-0.5 bg-secondary rounded-full overflow-hidden">
                                  <div
                                    className="h-full bg-primary rounded-full"
                                    style={{ width: `${Math.min(locContributionPct, 100)}%` }}
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>
              )}

              <button
                onClick={() => onSelectOps?.(opsItem.raw || opsItem)}
                className="w-full text-[10px] font-semibold text-primary hover:underline text-center py-1 flex items-center justify-center gap-1"
              >
                View Ops Detail <ChevronRight className="w-3 h-3" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ============================================
// LocationRevenueSummaryRow — Ops panel ke liye (Locations breakdown expandable cards)
// ============================================
const LocationRevenueSummaryRow = ({
  locationItem,
  totalAmount,
  onSelectStreet,
  open,
  onToggle,
}) => {
  const toNum = (v) => Number(String(v ?? 0).replace(/,/g, "")) || 0;
  const formatMoney = (v) =>
    toNum(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const locAmount = Number(locationItem.total_amount ?? 0);
  const locNotesCoins = Number(locationItem.total_notes_coins ?? 0);
  const locDevices = locationItem.devices || [];

  // Contribution percentage
  const contributionPct = totalAmount > 0 ? (locAmount / totalAmount) * 100 : 0;

  const handleLocationClick = () => {
    onSelectStreet?.({ location: locationItem.location });
    if (!open) onToggle?.();
  };

  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden">
      {/* Header Row */}
      <div className="w-full flex items-center gap-2.5 px-3.5 py-2.5 hover:bg-secondary/50 transition-colors">
        <button
          onClick={(e) => {
            e.stopPropagation();
            onToggle?.();
          }}
          className="flex-shrink-0 p-0.5 rounded hover:opacity-70 transition-opacity"
        >
          <div className="w-2 h-2 rounded-full bg-emerald-500" />
        </button>

        <button
          onClick={handleLocationClick}
          className="flex-1 flex items-center gap-2 text-left min-w-0"
        >
          <span className="flex-1 text-xs font-semibold text-foreground truncate">
            {locationItem.location_name || locationItem.location}
          </span>
          <span className="text-[9px] font-mono text-muted-foreground flex-shrink-0">
            {locDevices.length} devices
          </span>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <span className="text-[8px] font-bold tabular-nums text-foreground bg-secondary px-2 py-0.5 rounded-full">
              {formatMoney(locAmount)}
            </span>
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-secondary text-muted-foreground">
              {contributionPct.toFixed(2)}%
            </span>
          </div>
        </button>

        <button
          onClick={(e) => {
            e.stopPropagation();
            onToggle?.();
          }}
          className="flex-shrink-0 p-0.5 rounded hover:opacity-70 transition-opacity"
        >
          <ChevronDown
            className={`w-3.5 h-3.5 text-muted-foreground transition-transform duration-200 ${open ? "rotate-180" : ""}`}
          />
        </button>
      </div>

      {/* Progress Bar */}
      <div className="px-3.5 pb-2">
        <div className="w-full h-1 bg-secondary rounded-full overflow-hidden">
          <div
            className="h-full rounded-full bg-primary transition-all duration-500"
            style={{ width: `${Math.min(contributionPct, 100)}%` }}
          />
        </div>
      </div>

      {/* Expanded Content */}
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="body"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <div className="border-t border-border p-3.5 space-y-3">
              {/* Summary Cards */}
              <div className="grid grid-cols-2 gap-2">
                <div className="rounded-lg bg-primary/10 border border-primary/20 p-2.5">
                  <p className="text-[8px] uppercase tracking-widest text-muted-foreground mb-1">
                    Total Amount
                  </p>
                  <p className="text-lg font-bold tabular-nums leading-none text-primary">
                    {formatMoney(locAmount)}
                  </p>
                </div>
                <div className="rounded-lg bg-secondary p-2.5">
                  <p className="text-[8px] uppercase tracking-widest text-muted-foreground mb-1">
                    Notes / Coins
                  </p>
                  <p className="text-sm font-bold tabular-nums leading-none text-foreground">
                    {formatMoney(locNotesCoins)}
                  </p>
                </div>
              </div>

              <div className="rounded-lg bg-secondary p-2.5">
                <p className="text-[8px] uppercase tracking-widest text-muted-foreground mb-1">
                  Total Devices
                </p>
                <p className="text-sm font-bold tabular-nums leading-none text-foreground">
                  {locDevices.length}
                </p>
              </div>

              {/* Devices Breakdown Table */}
              {locDevices.length > 0 && (
                <div className="rounded-xl border border-border overflow-hidden">
                  <div className="grid grid-cols-[1.1fr_0.6fr_0.8fr_0.8fr_0.7fr] gap-1.5 px-3 py-2 bg-secondary/60 border-b border-border">
                    {["Device", "Type", "Amount", "Notes/Coins", "Contrib"].map((h) => (
                      <p
                        key={h}
                        className="text-[8px] uppercase tracking-widest text-muted-foreground font-semibold"
                      >
                        {h}
                      </p>
                    ))}
                  </div>
                  <div className="max-h-52 overflow-y-auto cb-scroll divide-y divide-border">
                    {[...locDevices]
                      .sort((a, b) => Number(b.total_amount ?? 0) - Number(a.total_amount ?? 0))
                      .map((device, di) => {
                        const devAmount = Number(device.total_amount ?? 0);
                        const devNotesCoins = Number(device.total_notes_coins ?? 0);
                        const devContributionPct =
                          locAmount > 0 ? (devAmount / locAmount) * 100 : 0;
                        return (
                          <div
                            key={`dev_${device.device_id || device.pnd_number}_${di}`}
                            className="px-3 py-2 hover:bg-secondary/40 transition-colors"
                          >
                            <div className="grid grid-cols-[1.1fr_0.6fr_0.8fr_0.8fr_0.7fr] gap-1.5 items-center">
                              <div className="flex items-center gap-1 min-w-0">
                                <CircleDot
                                  className="w-2.5 h-2.5 flex-shrink-0"
                                  style={{ color: getDeviceColor(device.device_type) }}
                                />
                                <p className="text-[10px] font-semibold text-foreground truncate font-mono">
                                  #{device.device_id || device.pnd_number}
                                </p>
                              </div>
                              <p className="text-[9px] text-muted-foreground truncate">
                                {getDeviceTypeName(device.device_type)}
                              </p>
                              <p className="text-[10px] font-semibold tabular-nums text-foreground">
                                {formatMoney(devAmount)}
                              </p>
                              <p className="text-[10px] font-semibold tabular-nums text-foreground">
                                {formatMoney(devNotesCoins)}
                              </p>
                              <div className="space-y-0.5">
                                <p className="text-[9px] font-extrabold tabular-nums text-primary">
                                  {devContributionPct.toFixed(2)}%
                                </p>
                                <div className="w-full h-0.5 bg-secondary rounded-full overflow-hidden">
                                  <div
                                    className="h-full bg-primary rounded-full"
                                    style={{ width: `${Math.min(devContributionPct, 100)}%` }}
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>
              )}

              <button
                onClick={() => onSelectStreet?.({ location: locationItem.location })}
                className="w-full text-[10px] font-semibold text-primary hover:underline text-center py-1 flex items-center justify-center gap-1"
              >
                View Location Detail <ChevronRight className="w-3 h-3" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ============================================
// DeviceRevenueSummaryRow — Location panel ke liye (Devices breakdown expandable cards)
// ============================================
const DeviceRevenueSummaryRow = ({ device, totalAmount, onSelectPnd, open, onToggle }) => {
  const toNum = (v) => Number(String(v ?? 0).replace(/,/g, "")) || 0;
  const formatMoney = (v) =>
    toNum(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const deviceId = device.device_id || device.pnd_number;
  const deviceColor = getDeviceColor(device.device_type);
  const devAmount = Number(device.total_amount ?? 0);
  const devNotesCoins = Number(device.total_notes_coins ?? 0);
  const transactions = device.transactions || [];

  // Contribution percentage
  const contributionPct = totalAmount > 0 ? (devAmount / totalAmount) * 100 : 0;

  const handleDeviceClick = () => {
    onSelectPnd?.(device);
    if (!open) onToggle?.();
  };

  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden">
      {/* Header Row */}
      <div className="w-full flex items-center gap-2.5 px-3.5 py-2.5 hover:bg-secondary/50 transition-colors">
        <button
          onClick={(e) => {
            e.stopPropagation();
            onToggle?.();
          }}
          className="flex-shrink-0 p-0.5 rounded hover:opacity-70 transition-opacity"
        >
          <div className="w-2 h-2 rounded-full" style={{ background: deviceColor }} />
        </button>

        <button
          onClick={handleDeviceClick}
          className="flex-1 flex items-center gap-2 text-left min-w-0"
        >
          <span className="flex-1 text-xs font-semibold text-foreground truncate font-mono">
            #{deviceId}
          </span>
          {device.device_type && (
            <span
              className="text-[8px] font-bold px-1.5 py-0.5 rounded flex-shrink-0 leading-none"
              style={{ background: `${deviceColor}22`, color: deviceColor }}
            >
              {getDeviceTypeName(device.device_type)}
            </span>
          )}
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <span className="text-[8px] font-bold tabular-nums text-foreground bg-secondary px-2 py-0.5 rounded-full">
              {formatMoney(devAmount)}
            </span>
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-secondary text-muted-foreground">
              {contributionPct.toFixed(2)}%
            </span>
          </div>
        </button>

        <button
          onClick={(e) => {
            e.stopPropagation();
            onToggle?.();
          }}
          className="flex-shrink-0 p-0.5 rounded hover:opacity-70 transition-opacity"
        >
          <ChevronDown
            className={`w-3.5 h-3.5 text-muted-foreground transition-transform duration-200 ${open ? "rotate-180" : ""}`}
          />
        </button>
      </div>

      {/* Progress Bar */}
      <div className="px-3.5 pb-2">
        <div className="w-full h-1 bg-secondary rounded-full overflow-hidden">
          <div
            className="h-full rounded-full bg-primary transition-all duration-500"
            style={{ width: `${Math.min(contributionPct, 100)}%` }}
          />
        </div>
      </div>

      {/* Expanded Content */}
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="body"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <div className="border-t border-border p-3.5 space-y-3">
              {/* Summary Cards */}
              <div className="grid grid-cols-2 gap-2">
                <div className="rounded-lg bg-primary/10 border border-primary/20 p-2.5">
                  <p className="text-[8px] uppercase tracking-widest text-muted-foreground mb-1">
                    Total Amount
                  </p>
                  <p className="text-lg font-bold tabular-nums leading-none text-primary">
                    {formatMoney(devAmount)}
                  </p>
                </div>
                <div className="rounded-lg bg-secondary p-2.5">
                  <p className="text-[8px] uppercase tracking-widest text-muted-foreground mb-1">
                    Notes / Coins
                  </p>
                  <p className="text-sm font-bold tabular-nums leading-none text-foreground">
                    {formatMoney(devNotesCoins)}
                  </p>
                </div>
              </div>

              {device.latest_transdate && (
                <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
                  <Clock className="w-3 h-3" />
                  Latest Status:{" "}
                  <span className="font-medium text-foreground">{device.latest_transdate}</span>
                </div>
              )}

              {/* Components Breakdown Table */}
              {transactions.length > 0 && (
                <div className="rounded-xl border border-border overflow-hidden">
                  <div className="grid grid-cols-[1.1fr_0.8fr_0.8fr_0.7fr] gap-1.5 px-3 py-2 bg-secondary/60 border-b border-border">
                    {["Component", "Amount", "Notes/Coins", "Contrib"].map((h) => (
                      <p
                        key={h}
                        className="text-[8px] uppercase tracking-widest text-muted-foreground font-semibold"
                      >
                        {h}
                      </p>
                    ))}
                  </div>
                  <div className="max-h-52 overflow-y-auto cb-scroll divide-y divide-border">
                    {[...transactions]
                      .sort((a, b) => Number(b.amount ?? 0) - Number(a.amount ?? 0))
                      .map((tx, ti) => {
                        const txAmount = Number(tx.amount ?? 0);
                        const txContributionPct = devAmount > 0 ? (txAmount / devAmount) * 100 : 0;
                        return (
                          <div
                            key={`${tx.trans_id ?? "tx"}_${tx.line_number ?? ti}`}
                            className="px-3 py-2 hover:bg-secondary/40 transition-colors"
                          >
                            <div className="grid grid-cols-[1.1fr_0.8fr_0.8fr_0.7fr] gap-1.5 items-center">
                              <p className="text-[10px] font-semibold text-foreground truncate">
                                {tx.dev_name || "—"}
                              </p>
                              <p className="text-[10px] font-semibold tabular-nums text-foreground">
                                {formatMoney(txAmount)}
                              </p>
                              <p className="text-[10px] font-semibold tabular-nums text-foreground">
                                {tx.notes_coins ?? 0}
                              </p>
                              <div className="space-y-0.5">
                                <p className="text-[9px] font-extrabold tabular-nums text-primary">
                                  {txContributionPct.toFixed(2)}%
                                </p>
                                <div className="w-full h-0.5 bg-secondary rounded-full overflow-hidden">
                                  <div
                                    className="h-full bg-primary rounded-full"
                                    style={{ width: `${Math.min(txContributionPct, 100)}%` }}
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>
              )}

              <button
                onClick={() => onSelectPnd?.(device)}
                className="w-full text-[10px] font-semibold text-primary hover:underline text-center py-1 flex items-center justify-center gap-1"
              >
                View Device Detail <ChevronRight className="w-3 h-3" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ============================================
// TransactionRevenueSummaryRow — Device panel ke liye (Transactions breakdown expandable cards)
// ============================================
const TransactionRevenueSummaryRow = ({ tx, totalAmount, open, onToggle }) => {
  const toNum = (v) => Number(String(v ?? 0).replace(/,/g, "")) || 0;
  const formatMoney = (v) =>
    toNum(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const txAmount = Number(tx.amount ?? 0);
  const txNotesCoins = Number(tx.notes_coins ?? 0);

  // Contribution percentage
  const contributionPct = totalAmount > 0 ? (txAmount / totalAmount) * 100 : 0;

  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden">
      {/* Header Row */}
      <div className="w-full flex items-center gap-2.5 px-3.5 py-2.5 hover:bg-secondary/50 transition-colors">
        <button
          onClick={(e) => {
            e.stopPropagation();
            onToggle?.();
          }}
          className="flex-shrink-0 p-0.5 rounded hover:opacity-70 transition-opacity"
        >
          <div className="w-2 h-2 rounded-full bg-amber-500" />
        </button>

        <button
          onClick={() => onToggle?.()}
          className="flex-1 flex items-center gap-2 text-left min-w-0"
        >
          <span className="flex-1 text-xs font-semibold text-foreground truncate">
            {tx.dev_name || "—"}
          </span>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <span className="text-[8px] font-bold tabular-nums text-foreground bg-secondary px-2 py-0.5 rounded-full">
              {formatMoney(txAmount)}
            </span>
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-secondary text-muted-foreground">
              {contributionPct.toFixed(2)}%
            </span>
          </div>
        </button>

        <button
          onClick={(e) => {
            e.stopPropagation();
            onToggle?.();
          }}
          className="flex-shrink-0 p-0.5 rounded hover:opacity-70 transition-opacity"
        >
          <ChevronDown
            className={`w-3.5 h-3.5 text-muted-foreground transition-transform duration-200 ${open ? "rotate-180" : ""}`}
          />
        </button>
      </div>

      {/* Progress Bar */}
      <div className="px-3.5 pb-2">
        <div className="w-full h-1 bg-secondary rounded-full overflow-hidden">
          <div
            className="h-full rounded-full bg-primary transition-all duration-500"
            style={{ width: `${Math.min(contributionPct, 100)}%` }}
          />
        </div>
      </div>

      {/* Expanded Content */}
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="body"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <div className="border-t border-border p-3.5 space-y-3">
              {/* Summary Cards */}
              <div className="grid grid-cols-2 gap-2">
                <div className="rounded-lg bg-primary/10 border border-primary/20 p-2.5">
                  <p className="text-[8px] uppercase tracking-widest text-muted-foreground mb-1">
                    Amount
                  </p>
                  <p className="text-lg font-bold tabular-nums leading-none text-primary">
                    {formatMoney(txAmount)}
                  </p>
                </div>
                <div className="rounded-lg bg-secondary p-2.5">
                  <p className="text-[8px] uppercase tracking-widest text-muted-foreground mb-1">
                    Notes / Coins
                  </p>
                  <p className="text-sm font-bold tabular-nums leading-none text-foreground">
                    {txNotesCoins}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div className="rounded-lg bg-secondary p-2.5">
                  <p className="text-[8px] uppercase tracking-widest text-muted-foreground mb-1">
                    Transaction ID
                  </p>
                  <p className="text-sm font-bold tabular-nums leading-none text-foreground truncate">
                    {tx.trans_id || "—"}
                  </p>
                </div>
                <div className="rounded-lg bg-secondary p-2.5">
                  <p className="text-[8px] uppercase tracking-widest text-muted-foreground mb-1">
                    Date
                  </p>
                  <p className="text-sm font-bold tabular-nums leading-none text-foreground">
                    {tx.transdate || "—"}
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ============================================
// StreetRevenueSummaryRow
// ============================================
const StreetRevenueSummaryRow = ({ street, cityStreets, onSelectStreet }) => {
  const [open, setOpen] = useState(false);

  const isNoTransaction = street.no_transaction === true;
  const contributionPct = parseFloat(street.contribution) || 0;

  const collection = street.collection ?? "0";
  const ticketCount = street.ticket_count ?? "0";
  const contribution = street.contribution ?? "0.00%";
  const als =
    street.als !== null && street.als !== undefined && street.als !== "" ? String(street.als) : "—";
  const atv =
    street.atv !== null && street.atv !== undefined && street.atv !== "" ? String(street.atv) : "—";

  const noTranCount = street.no_tran_pnd_count ?? 0;
  const belowThrCount = street.below_threshold_pnd_count ?? 0;
  const pnds = street.pnds || [];

  const fullStreet = cityStreets.find(
    (s) =>
      s.street_name === street.street ||
      s.street_send_name === street.street ||
      s.street_cd === street.street
  );

  return (
    <div
      className={`rounded-xl border overflow-hidden transition-colors ${
        isNoTransaction ? "border-orange-500/30 bg-orange-500/5" : "border-border bg-card"
      }`}
    >
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2.5 px-3.5 py-2.5 hover:bg-secondary/50 transition-colors text-left"
      >
        <div
          className={`w-2 h-2 rounded-full flex-shrink-0 ${
            isNoTransaction ? "bg-orange-500" : "bg-emerald-500"
          }`}
        />
        <span className="flex-1 text-xs font-semibold text-foreground truncate">
          {street.street}
        </span>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <span className="text-[8px] font-bold tabular-nums text-foreground bg-secondary px-2 py-0.5 rounded-full">
            {collection}
          </span>
          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-secondary text-muted-foreground">
            {contribution}
          </span>
        </div>
        <ChevronDown
          className={`w-3.5 h-3.5 text-muted-foreground flex-shrink-0 transition-transform duration-200 ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>

      <div className="px-3.5 pb-2">
        <div className="w-full h-1 bg-secondary rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-500 ${
              isNoTransaction ? "bg-orange-500" : "bg-primary"
            }`}
            style={{ width: `${Math.min(contributionPct, 100)}%` }}
          />
        </div>
      </div>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="body"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <div className="border-t border-border p-3.5 space-y-3">
              <div className="space-y-2">
                <div className="rounded-lg bg-primary/10 border border-primary/20 p-2.5">
                  <p className="text-[8px] uppercase tracking-widest text-muted-foreground mb-1">
                    Revenue
                  </p>
                  <p className="text-lg font-bold tabular-nums leading-none text-primary">
                    {collection}
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="rounded-lg bg-secondary p-2.5">
                    <p className="text-[8px] uppercase tracking-widest text-muted-foreground mb-1">
                      Tickets
                    </p>
                    <p className="text-sm font-bold tabular-nums leading-none text-foreground">
                      {ticketCount}
                    </p>
                  </div>
                  <div className="rounded-lg bg-secondary p-2.5">
                    <p className="text-[8px] uppercase tracking-widest text-muted-foreground mb-1">
                      Contribution
                    </p>
                    <p className="text-sm font-bold tabular-nums leading-none text-foreground">
                      {contribution}
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="rounded-lg bg-secondary p-2.5">
                    <p className="text-[8px] uppercase tracking-widest text-muted-foreground mb-1">
                      ALS
                    </p>
                    <p className="text-sm font-bold tabular-nums leading-none text-foreground">
                      {als}
                    </p>
                  </div>
                  <div className="rounded-lg bg-secondary p-2.5">
                    <p className="text-[8px] uppercase tracking-widest text-muted-foreground mb-1">
                      ATV
                    </p>
                    <p className="text-sm font-bold tabular-nums leading-none text-foreground">
                      {atv}
                    </p>
                  </div>
                </div>
              </div>

              {(noTranCount > 0 || belowThrCount > 0) && (
                <div className="flex gap-2 flex-wrap">
                  {noTranCount > 0 && (
                    <div className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-orange-500/10 border border-orange-500/25">
                      <Ban className="w-3 h-3 text-orange-500 flex-shrink-0" />
                      <span className="text-[10px] font-semibold text-orange-500">
                        {noTranCount} No Transaction
                      </span>
                    </div>
                  )}
                  {belowThrCount > 0 && (
                    <div className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-red-500/10 border border-red-500/25">
                      <AlertTriangle className="w-3 h-3 text-red-500 flex-shrink-0" />
                      <span className="text-[10px] font-semibold text-red-500">
                        {belowThrCount} Below Threshold
                      </span>
                    </div>
                  )}
                </div>
              )}

              {pnds.length > 0 && (
                <div className="rounded-xl border border-border overflow-hidden">
                  <div className="grid grid-cols-[1.4fr_1fr_0.7fr_0.7fr_0.7fr_0.8fr] gap-1.5 px-3 py-2 bg-secondary/60 border-b border-border">
                    {["Devices", "Revenue", "Tickets", "ALS", "ATV", "Contribution"].map((h) => (
                      <p
                        key={h}
                        className="text-[8px] uppercase tracking-widest text-muted-foreground font-semibold"
                      >
                        {h}
                      </p>
                    ))}
                  </div>
                  <div className="max-h-52 overflow-y-auto cb-scroll divide-y divide-border">
                    {pnds.map((pnd, pi) => {
                      const pndPct = parseFloat(pnd.contribution) || 0;
                      const pndNoTran = pnd.no_transaction === true;
                      const pndBelowThr = pnd.below_threshold === true;
                      const pndAls =
                        pnd.als !== null && pnd.als !== undefined && pnd.als !== ""
                          ? String(pnd.als)
                          : "—";
                      const pndAtv =
                        pnd.atv !== null && pnd.atv !== undefined && pnd.atv !== ""
                          ? String(pnd.atv)
                          : "—";

                      return (
                        <div
                          key={`pnd_${pnd.pnd}_${pi}`}
                          className={`px-3 py-2.5 transition-colors ${
                            pndNoTran
                              ? "bg-orange-500/5"
                              : pndBelowThr
                                ? "bg-red-500/5"
                                : "hover:bg-secondary/40"
                          }`}
                        >
                          <div className="grid grid-cols-[1.4fr_1fr_0.7fr_0.7fr_0.7fr_0.8fr] gap-1.5 items-center">
                            <div className="flex items-center gap-1 min-w-0">
                              {pndNoTran && (
                                <Ban className="w-2.5 h-2.5 text-orange-500 flex-shrink-0" />
                              )}
                              {pndBelowThr && (
                                <AlertTriangle className="w-2.5 h-2.5 text-red-500    flex-shrink-0" />
                              )}
                              <p className="text-[10px] font-mono font-semibold text-foreground truncate">
                                #{pnd.pnd}
                              </p>
                            </div>
                            <p
                              className={`text-[10px] font-semibold tabular-nums truncate ${pndNoTran ? "text-orange-500" : "text-foreground"}`}
                            >
                              {pnd.collection ?? "0"}
                            </p>
                            <p className="text-[10px] text-muted-foreground tabular-nums">
                              {pnd.ticket_count ?? "0"}
                            </p>
                            <p className="text-[10px] text-foreground tabular-nums">{pndAls}</p>
                            <p className="text-[10px] text-foreground tabular-nums">{pndAtv}</p>
                            <div className="space-y-1">
                              <p
                                className={`text-[9px] font-bold tabular-nums ${pndPct >= 30 ? "text-primary" : "text-muted-foreground"}`}
                              >
                                {pnd.contribution ?? "0%"}
                              </p>
                              <div className="w-full h-0.5 bg-secondary rounded-full overflow-hidden">
                                <div
                                  className={`h-full rounded-full ${pndNoTran ? "bg-orange-500" : pndBelowThr ? "bg-red-500" : "bg-primary"}`}
                                  style={{ width: `${Math.min(pndPct, 100)}%` }}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {fullStreet && (
                <button
                  onClick={() => onSelectStreet?.(fullStreet)}
                  className="w-full text-[10px] font-semibold text-primary hover:underline text-center py-1 flex items-center justify-center gap-1"
                >
                  View Street Detail <ChevronRight className="w-3 h-3" />
                </button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ============================================
// DateFilterPanel
// ============================================
const DateFilterPanel = ({
  onApply,
  onReset,
  onClose,
  topPos,
  activeMonth,
  activeYear,
  activeThreshold,
  activeCurrency,
  initialGroup = "",
  initialMonth = "",
  initialDay = null,
}) => {
  const [monthGroups, setMonthGroups] = useState([]);
  const [yearOptions, setYearOptions] = useState([]);
  const _currentYear = String(new Date().getFullYear());
  const [selYear, setSelYear] = useState(
    activeYear
      ? { value: activeYear, label: activeYear }
      : { value: _currentYear, label: _currentYear }
  );
  const [selGroup, setSelGroup] = useState(initialGroup);
  const [selMonth, setSelMonth] = useState(initialMonth);
  const [selDay, setSelDay] = useState(initialDay);
  const [threshold, setThreshold] = useState(activeThreshold || "");
  const [currency, setCurrency] = useState(
    activeCurrency
      ? CURRENCY_OPTIONS.find((c) => c.value === activeCurrency) || CURRENCY_OPTIONS[0]
      : CURRENCY_OPTIONS[0]
  );
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [expandedMonths, setExpandedMonths] = useState(new Set());
  const [optLoading, setOptLoading] = useState(true);
  const [optError, setOptError] = useState(null);
  const [dataLoaded, setDataLoaded] = useState(false);

  // ── Drag & Resize state ──────────────────────────────────────────────────────
  const panelRef = useRef(null);
  const [panelPos, setPanelPos] = useState({ top: topPos, left: 328 });
  const [panelHeight, setPanelHeight] = useState(null);
  const isDragging = useRef(false);
  const isResizing = useRef(false);
  const dragStart = useRef({ x: 0, y: 0, top: 0, left: 0 });
  const resizeStart = useRef({ y: 0, height: 0 });

  const handleDragMouseDown = (e) => {
    if (e.target.closest("button")) return;
    isDragging.current = true;
    dragStart.current = {
      x: e.clientX,
      y: e.clientY,
      top: panelPos.top,
      left: panelPos.left,
    };
    e.preventDefault();
  };

  const handleResizeMouseDown = (e) => {
    isResizing.current = true;
    resizeStart.current = {
      y: e.clientY,
      height: panelRef.current?.offsetHeight || 400,
    };
    e.preventDefault();
    e.stopPropagation();
  };

  useEffect(() => {
    const onMove = (e) => {
      if (isDragging.current) {
        const dx = e.clientX - dragStart.current.x;
        const dy = e.clientY - dragStart.current.y;
        setPanelPos({
          top: Math.max(8, dragStart.current.top + dy),
          left: Math.max(8, dragStart.current.left + dx),
        });
      }
      if (isResizing.current) {
        const dy = e.clientY - resizeStart.current.y;
        const newH = Math.min(
          window.innerHeight - 80,
          Math.max(200, resizeStart.current.height + dy)
        );
        setPanelHeight(newH);
      }
    };
    const onUp = () => {
      isDragging.current = false;
      isResizing.current = false;
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
  }, []);

  useEffect(() => {
    setPanelPos({ top: topPos, left: 328 });
  }, [topPos]);

  useEffect(() => {
    if (dataLoaded) return;
    const load = async () => {
      setOptLoading(true);
      setOptError(null);
      try {
        const res = await apiCall("get", "/get-months-years", null, null, false, false);
        if (res?.success && res?.data) {
          setMonthGroups(res.data.months || []);
          const yearList = (res.data.years || [])
            .map((y) => ({ value: y.name, label: y.name }))
            .reverse();
          setYearOptions(yearList);
          setDataLoaded(true);
        } else {
          setOptError("Could not load options");
        }
      } catch {
        setOptError("Could not load options");
      } finally {
        setOptLoading(false);
      }
    };
    load();
  }, [dataLoaded]);

  useEffect(() => {
    if (!dataLoaded || monthGroups.length === 0) return;
    if (initialGroup && initialMonth) {
      setExpandedGroups((prev) => new Set([...prev, initialGroup]));
      setExpandedMonths((prev) => new Set([...prev, initialMonth]));
      return;
    }
    const currentDate = new Date();
    const currentMonthName = MONTH_NAMES[currentDate.getMonth()];
    const currentYearValue = String(currentDate.getFullYear());
    const fullYearGroup = monthGroups.find((g) => g.name === "Full_Year");
    if (fullYearGroup && fullYearGroup.children) {
      const currentMonthObj = fullYearGroup.children.find((m) => m.name === currentMonthName);
      if (currentMonthObj) {
        setSelGroup("Full_Year");
        setSelMonth(currentMonthName);
        setSelDay({ value: currentMonthName, label: currentMonthName });
        setSelYear({ value: currentYearValue, label: currentYearValue });
        setExpandedGroups(new Set(["Full_Year"]));
        setExpandedMonths(new Set([currentMonthName]));
      }
    }
  }, [dataLoaded, monthGroups, initialGroup, initialMonth]);

  const toggleGroup = (groupName) => {
    setExpandedGroups((prev) => {
      const n = new Set(prev);
      n.has(groupName) ? n.delete(groupName) : n.add(groupName);
      return n;
    });
  };
  const toggleMonth = (monthName) => {
    setExpandedMonths((prev) => {
      const n = new Set(prev);
      n.has(monthName) ? n.delete(monthName) : n.add(monthName);
      return n;
    });
  };
  const handleGroupSelect = (group) => {
    setSelGroup(group.name);
    setSelMonth(group.name);
    setSelDay({ value: group.name, label: group.name === "Full_Year" ? "Full Year" : "FY" });
    toggleGroup(group.name);
  };
  const handleMonthSelect = (groupName, month) => {
    setSelGroup(groupName);
    setSelMonth(month.name);
    setSelDay({ value: month.name, label: month.name });
    if (month.children && month.children.length > 0) {
      if (!expandedMonths.has(month.name)) toggleMonth(month.name);
    }
  };
  const handleDaySelect = (groupName, monthName, day) => {
    setSelGroup(groupName);
    setSelMonth(monthName);
    setSelDay({ value: day.name, label: day.name });
  };

  const isComplete = !!selMonth && !!selYear;
  const previewLabel = isComplete ? `${selDay.label} ${selYear.value}` : null;

  const handleApply = () => {
    if (!isComplete) return;
    onApply({
      month: selDay.value,
      year: selYear.value,
      type: selGroup,
      displayMonth: selMonth || selDay.label,
      threshold: threshold || undefined,
      currency: currency?.value,
    });
  };
  const handleReset = () => {
    setSelGroup("");
    setSelMonth("");
    setSelDay(null);
    setSelYear(null);
    setThreshold("");
    setCurrency(CURRENCY_OPTIONS[0]);
    onReset();
  };

  return (
    <motion.div
      ref={panelRef}
      initial={{ opacity: 0, x: -6, scale: 0.97 }}
      animate={{ opacity: 1, x: 0, scale: 1 }}
      exit={{ opacity: 0, x: -6, scale: 0.97 }}
      transition={{ duration: 0.16 }}
      style={{
        position: "fixed",
        left: `${panelPos.left}px`,
        top: `${panelPos.top}px`,
        zIndex: 9999,
        width: "280px",
        boxShadow: "0 8px 40px rgba(0,0,0,0.28)",
        height: panelHeight ? `${panelHeight}px` : "auto",
        maxHeight: panelHeight ? "none" : "60vh",
        display: "flex",
        flexDirection: "column",
      }}
      className="rounded-xl border border-border bg-card overflow-hidden"
    >
      <div
        className="flex items-center justify-between px-4 py-3 border-b border-border bg-sidebar flex-shrink-0"
        onMouseDown={handleDragMouseDown}
        style={{ cursor: "move", userSelect: "none" }}
      >
        <div className="flex items-center gap-2">
          <CalendarRange className="w-4 h-4 text-primary" />
          <span className="text-xs font-bold text-foreground tracking-tight">Filter</span>
        </div>
        <button
          onMouseDown={(e) => e.stopPropagation()}
          onClick={onClose}
          className="text-muted-foreground hover:text-foreground transition-colors"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto cb-scroll p-4 space-y-2">
        {optLoading && (
          <div className="flex items-center justify-center gap-2 py-6">
            <Loader2 className="w-4 h-4 animate-spin text-primary" />
            <span className="text-xs text-muted-foreground">Loading...</span>
          </div>
        )}
        {!optLoading && optError && (
          <p className="text-xs text-red-500 text-center py-2">{optError}</p>
        )}
        {!optLoading && !optError && (
          <>
            <div>
              <p className="text-[7px] uppercase tracking-widest text-muted-foreground mb-1.5 font-semibold">
                Currency
              </p>
              <ReactSelect
                options={CURRENCY_OPTIONS}
                value={currency}
                onChange={setCurrency}
                placeholder="Select currency..."
              />
            </div>

            <div>
              <p className="text-[7px] uppercase tracking-widest text-muted-foreground mb-1.5 font-semibold">
                Year
              </p>
              <ReactSelect
                options={yearOptions}
                value={selYear}
                onChange={setSelYear}
                placeholder="Select year..."
              />
            </div>
            <div>
              <p className="text-[7px] uppercase tracking-widest text-muted-foreground mb-1.5 font-semibold">
                Month / Period
              </p>
              <div className="border border-border rounded-lg overflow-hidden bg-background max-h-26 overflow-y-auto cb-scroll">
                {monthGroups.map((group) => {
                  const isGroupExpanded = expandedGroups.has(group.name);
                  const isGroupSelected = selGroup === group.name && selMonth === group.name;
                  return (
                    <div key={group.name}>
                      <button
                        onClick={() => handleGroupSelect(group)}
                        className={`w-full flex items-center gap-2 px-3 py-2 text-left transition-colors border-b border-border ${isGroupSelected ? "bg-primary/10" : "hover:bg-muted/50"}`}
                      >
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleGroup(group.name);
                          }}
                          className="flex-shrink-0 text-muted-foreground"
                        >
                          {isGroupExpanded ? (
                            <ChevronDown className="w-3.5 h-3.5" />
                          ) : (
                            <ChevronRight className="w-3.5 h-3.5" />
                          )}
                        </button>
                        <span className="text-xs font-semibold text-foreground flex-1 text-left">
                          {group.name === "Full_Year" ? "Full Year" : "Fiscal Year (FY)"}
                        </span>
                        {isGroupSelected && (
                          <Check className="w-3 h-3 text-primary flex-shrink-0" />
                        )}
                      </button>
                      {isGroupExpanded &&
                        (group.children || []).map((month) => {
                          const isMonthExpanded = expandedMonths.has(month.name);
                          const isMonthSelected =
                            selGroup === group.name &&
                            selMonth === month.name &&
                            selDay?.value === month.name;
                          const hasDays = month.children && month.children.length > 0;
                          return (
                            <div key={month.name}>
                              <button
                                onClick={() => handleMonthSelect(group.name, month)}
                                className={`w-full flex items-center gap-2 pl-7 pr-3 py-1.5 text-left transition-colors border-b border-border/50 ${isMonthSelected ? "bg-primary/8 text-primary" : "hover:bg-muted/40"}`}
                              >
                                {hasDays ? (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleMonth(month.name);
                                    }}
                                    className="flex-shrink-0 text-muted-foreground"
                                  >
                                    {isMonthExpanded ? (
                                      <ChevronDown className="w-3 h-3" />
                                    ) : (
                                      <ChevronRight className="w-3 h-3" />
                                    )}
                                  </button>
                                ) : (
                                  <div className="w-3" />
                                )}
                                <span
                                  className={`text-xs flex-1 text-left ${isMonthSelected ? "font-semibold text-primary" : "text-foreground"}`}
                                >
                                  {month.name}
                                </span>
                                {isMonthSelected && (
                                  <Check className="w-3 h-3 ml-auto text-primary flex-shrink-0" />
                                )}
                              </button>
                              {hasDays && isMonthExpanded && (
                                <div className="bg-muted/20">
                                  {(month.children || []).map((day) => {
                                    const isDaySelected =
                                      selGroup === group.name &&
                                      selMonth === month.name &&
                                      selDay?.value === day.name;
                                    return (
                                      <button
                                        key={day.name}
                                        onClick={() => handleDaySelect(group.name, month.name, day)}
                                        className={`w-full flex items-center gap-2 pl-14 pr-3 py-1.5 text-left transition-colors border-b border-border/30 ${isDaySelected ? "bg-primary/10 text-primary" : "hover:bg-muted/50"}`}
                                      >
                                        <span
                                          className={`text-[11px] font-mono ${isDaySelected ? "font-semibold text-primary" : "text-foreground"}`}
                                        >
                                          {day.name}
                                        </span>
                                        {isDaySelected && (
                                          <Check className="w-3 h-3 ml-auto text-primary flex-shrink-0" />
                                        )}
                                      </button>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        })}
                    </div>
                  );
                })}
              </div>
            </div>
            {previewLabel && (
              <div className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg bg-primary/10 border border-primary/20">
                <Check className="w-3 h-3 text-primary flex-shrink-0" />
                <span className="text-[10px] text-primary font-semibold">{previewLabel}</span>
                {threshold && (
                  <span className="text-[10px] text-primary font-semibold ml-1">
                    • Threshold: {threshold}
                  </span>
                )}
                {currency && (
                  <span className="text-[10px] text-primary font-semibold ml-1">
                    • {currency.label}
                  </span>
                )}
              </div>
            )}
            <div className="flex gap-2 pt-1">
              <button
                onClick={handleReset}
                className="flex-1 py-1.5 text-xs font-semibold rounded-lg border border-border text-muted-foreground hover:bg-secondary transition-colors"
              >
                Reset
              </button>
              <button
                disabled={!isComplete}
                onClick={handleApply}
                className={`flex-1 py-1.5 text-xs font-semibold rounded-lg transition-colors ${isComplete ? "bg-primary text-white hover:bg-primary/90" : "bg-muted text-muted-foreground cursor-not-allowed"}`}
              >
                Apply
              </button>
            </div>
          </>
        )}
      </div>

      <div
        onMouseDown={handleResizeMouseDown}
        className="flex-shrink-0 flex items-center justify-center border-t border-border hover:bg-primary/10 transition-colors"
        style={{ height: "10px", cursor: "ns-resize" }}
      >
        <div className="w-8 h-[3px] rounded-full bg-border" />
      </div>
    </motion.div>
  );
};

// ============================================
// GoogleMapComponent
// ============================================
const GoogleMapComponent = ({
  citiesData = [],
  countries = [],
  cityBoxes = [],
  permsReady = true,
  selectedCountry,
  selectedCityGeo,
  selectedCity,
  selectedStreet,
  selectedGate,
  selectedPnd,
  onCountryClick,
  onPndClick,
  onCityClick,
  onCityGeoClick,
  onStreetClick,
  onGateClick,
  isDarkMode,
  belowThresholdPnds = new Set(),
  noTransactionPnds = new Set(),
  shimmerMode = "overview",
  panelWidth = 0,
}) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const polygonsRef = useRef([]);
  const markersRef = useRef([]);
  const animationFrameRef = useRef(null);
  const highlightedMarkerRef = useRef(null);
  const markerMapRef = useRef({});
  const [mapReady, setMapReady] = useState(false);
  const gateHoverInfoWindowRef = useRef(null);
  const labelsVisibleRef = useRef(true);
  const [mapLoading, setMapLoading] = useState(true);

  const getPndKey = (pnd) => `${pnd.latitude}_${pnd.longitude}`;

  useEffect(() => {
    if (!document.getElementById("google-maps-script")) {
      const script = document.createElement("script");
      script.id = "google-maps-script";
      script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAP_KEY}&libraries=geometry`;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
  }, []);

    useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;
    const init = () => {
      if (!window.google) {
        setTimeout(init, 100);
        return;
      }
      mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
        center: { lat: 24.0, lng: 45.0 },
        zoom: 6,
        styles: isDarkMode
          ? getDarkMapStyles(!labelsVisibleRef.current)
          : getLightMapStyles(!labelsVisibleRef.current),
        mapTypeId: window.google.maps.MapTypeId.ROADMAP,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: true,
        zoomControl: false,
      });

      const map = mapInstanceRef.current;

      // ── Toggle button hover style + Labels popup animation ──
      if (!document.getElementById("gm-view-toggle-style")) {
        const style = document.createElement("style");
        style.id = "gm-view-toggle-style";
        style.textContent = `
          .gm-view-toggle-btn { transition: background 0.18s ease, color 0.18s ease, box-shadow 0.18s ease, transform 0.12s ease; }
          .gm-view-toggle-btn:hover:not(.gm-view-toggle-active) {
            background: ${isDarkMode ? "rgba(255,255,255,0.10)" : "rgba(15,23,42,0.06)"} !important;
          }
          .gm-labels-popup {
            transition: opacity 0.25s ease, transform 0.25s ease;
          }
          .gm-labels-popup.gm-hidden {
            opacity: 0;
            transform: translateY(6px) scale(0.95);
            pointer-events: none;
          }
        `;
        document.head.appendChild(style);
      }

      // ── Outer vertical stack: Labels (upar) + Default/Satellite (neeche) ──
      const outerWrapper = document.createElement("div");
      outerWrapper.style.cssText = `
        display: flex; flex-direction: column; align-items: center; gap: 6px;
        margin: 12px;
        font-family: 'Inter', Roboto, Arial, sans-serif; user-select: none;
      `;

      // ── Labels wrapper (initially HIDDEN) ──
      const labelsWrapper = document.createElement("div");
      labelsWrapper.className = "gm-labels-popup gm-hidden";
      labelsWrapper.style.cssText = `
        display: flex; gap: 2px; padding: 3px;
        background: ${isDarkMode ? "rgba(31,41,55,0.95)" : "rgba(255,255,255,0.97)"};
        backdrop-filter: blur(6px);
        border: 1px solid ${isDarkMode ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.06)"};
        border-radius: 999px; box-shadow: 0 2px 8px rgba(0,0,0,0.18);
        font-size: 12px;
      `;

      // ── Default / Satellite wrapper ──
      const wrapper = document.createElement("div");
      wrapper.style.cssText = `
        display: flex; gap: 2px; padding: 3px;
        background: ${isDarkMode ? "rgba(31,41,55,0.95)" : "rgba(255,255,255,0.97)"};
        backdrop-filter: blur(6px);
        border: 1px solid ${isDarkMode ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.06)"};
        border-radius: 999px; box-shadow: 0 2px 8px rgba(0,0,0,0.18);
        font-size: 13px;
      `;

      // ── Auto-hide timer for Labels button ──
      let labelsHideTimer = null;
      const showLabelsBtnTemporarily = () => {
        labelsWrapper.classList.remove("gm-hidden");
        if (labelsHideTimer) clearTimeout(labelsHideTimer);
        labelsHideTimer = setTimeout(() => {
          labelsWrapper.classList.add("gm-hidden");
        }, 2500);
      };
      labelsWrapper.addEventListener("mouseenter", () => {
        if (labelsHideTimer) clearTimeout(labelsHideTimer);
      });
      labelsWrapper.addEventListener("mouseleave", () => {
        if (labelsHideTimer) clearTimeout(labelsHideTimer);
        labelsHideTimer = setTimeout(() => {
          labelsWrapper.classList.add("gm-hidden");
        }, 1500);
      });

      // ── Default/Satellite button banao ──
      const makeBtn = (label, type) => {
        const btn = document.createElement("div");
        btn.textContent = label;
        btn.dataset.type = type;
        btn.className = "gm-view-toggle-btn";
        btn.style.cssText = `
          padding: 7px 16px; cursor: pointer; white-space: nowrap; border-radius: 999px;
          font-weight: 500; letter-spacing: 0.01em;
          color: ${isDarkMode ? "#cbd5e1" : "#475569"};
        `;
        btn.addEventListener("click", () => {
          map.setMapTypeId(type);
          updateActive(type);
          showLabelsBtnTemporarily();
        });
        return btn;
      };

      const defaultBtn = makeBtn("Default View", window.google.maps.MapTypeId.ROADMAP);
      const satelliteBtn = makeBtn("Satellite View", "hybrid");

      const updateActive = (activeType) => {
        [defaultBtn, satelliteBtn].forEach((btn) => {
          const isActive = btn.dataset.type === activeType;
          btn.classList.toggle("gm-view-toggle-active", isActive);
          btn.style.background = isActive ? "var(--primary)" : "transparent";
          btn.style.color = isActive
            ? "var(--primary-foreground)"
            : isDarkMode
              ? "#cbd5e1"
              : "#475569";
          btn.style.fontWeight = isActive ? "600" : "500";
          btn.style.boxShadow = isActive ? "0 1px 4px rgba(73,127,253,0.4)" : "none";
        });
      };
      updateActive(window.google.maps.MapTypeId.ROADMAP);

      // ── Labels toggle button (initially hidden) ──
      const labelsBtn = document.createElement("div");
      labelsBtn.textContent = "Labels";
      labelsBtn.className = "gm-view-toggle-btn";
      labelsBtn.style.cssText = `
        padding: 5px 14px; cursor: pointer; white-space: nowrap; border-radius: 999px;
        font-weight: 500; letter-spacing: 0.01em;
        color: ${isDarkMode ? "#cbd5e1" : "#475569"};
      `;

      const applyMapStyles = () => {
        map.setOptions({
          styles: isDarkMode
            ? getDarkMapStyles(!labelsVisibleRef.current)
            : getLightMapStyles(!labelsVisibleRef.current),
        });
      };

      const updateLabelsBtnStyle = () => {
        const isOn = labelsVisibleRef.current;
        labelsBtn.classList.toggle("gm-view-toggle-active", isOn);
        labelsBtn.style.background = isOn ? "var(--primary)" : "transparent";
        labelsBtn.style.color = isOn
          ? "var(--primary-foreground)"
          : isDarkMode
            ? "#cbd5e1"
            : "#475569";
        labelsBtn.style.fontWeight = isOn ? "600" : "500";
        labelsBtn.style.boxShadow = isOn ? "0 1px 4px rgba(73,127,253,0.4)" : "none";
      };
      updateLabelsBtnStyle();

      labelsBtn.addEventListener("click", () => {
        labelsVisibleRef.current = !labelsVisibleRef.current;
        applyMapStyles();
        updateLabelsBtnStyle();
        showLabelsBtnTemporarily();
      });

      // ── Assemble ──
      labelsWrapper.appendChild(labelsBtn);
      wrapper.appendChild(defaultBtn);
      wrapper.appendChild(satelliteBtn);
      outerWrapper.appendChild(labelsWrapper); // upar (initially hidden)
      outerWrapper.appendChild(wrapper); // neeche

      map.controls[window.google.maps.ControlPosition.BOTTOM_CENTER].push(outerWrapper);
    };
    init();
  }, [isDarkMode]);

    useEffect(() => {
    if (mapInstanceRef.current)
      mapInstanceRef.current.setOptions({
        styles: isDarkMode
          ? getDarkMapStyles(!labelsVisibleRef.current)
          : getLightMapStyles(!labelsVisibleRef.current),
      });
  }, [isDarkMode]);

  const createMarkerIcon = useCallback(
    (deviceType, color, isSelected = false, isNoTransaction = false) => {
      const size = isSelected ? 50 : 38;
      const opacity = isSelected ? "1" : "0.85";
      const slashLine = isNoTransaction
        ? `<line x1="10" y1="14" x2="54" y2="46" stroke="#EF4444" stroke-width="4.5" stroke-linecap="round"/>`
        : "";
      const iconPaths = getMarkerIconPaths(deviceType);
      const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 64 64">
      <path d="M32 3C20 3 10 13 10 25c0 18 22 36 22 36s22-18 22-36C54 13 44 3 32 3z" fill="${color}" opacity="${opacity}"/>
      <circle cx="32" cy="25" r="11" fill="white" opacity="${opacity}"/>
      <g transform="translate(25,18) scale(0.583)" fill="none" stroke="${color}" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" opacity="${opacity}">
        ${iconPaths}
      </g>
      ${slashLine}
    </svg>`;
      return {
        url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
        scaledSize: new window.google.maps.Size(size, size),
        anchor: new window.google.maps.Point(size / 2, size),
      };
    },
    []
  );

  const sortByPrincipalAxis = useCallback((coords) => {
    if (coords.length <= 2) return coords;
    const cx = coords.reduce((s, c) => s + c.lng, 0) / coords.length;
    const cy = coords.reduce((s, c) => s + c.lat, 0) / coords.length;
    let xx = 0,
      xy = 0,
      yy = 0;
    coords.forEach((c) => {
      const dx = c.lng - cx;
      const dy = c.lat - cy;
      xx += dx * dx;
      xy += dx * dy;
      yy += dy * dy;
    });
    const angle = Math.atan2(2 * xy, xx - yy) / 2;
    const ax = Math.cos(angle);
    const ay = Math.sin(angle);
    return [...coords].sort((a, b) => {
      const pa = (a.lng - cx) * ax + (a.lat - cy) * ay;
      const pb = (b.lng - cx) * ax + (b.lat - cy) * ay;
      return pa - pb;
    });
  }, []);

  const createRoadPolygon = useCallback(
    (points, offsetAmount) => {
      const sorted = sortByPrincipalAxis(points);
      const leftSide = [];
      const rightSide = [];
      sorted.forEach((point, index) => {
        const prev = sorted[index - 1] || point;
        const next = sorted[index + 1] || point;
        const dx = next.lng - prev.lng;
        const dy = next.lat - prev.lat;
        const safeLength = Math.sqrt(dx * dx + dy * dy) || 1;
        const nx = -dy / safeLength;
        const ny = dx / safeLength;
        leftSide.push({ lat: point.lat + ny * offsetAmount, lng: point.lng + nx * offsetAmount });
        rightSide.push({ lat: point.lat - ny * offsetAmount, lng: point.lng - nx * offsetAmount });
      });
      return [...leftSide, ...rightSide.reverse()];
    },
    [sortByPrincipalAxis]
  );

  const computeBoundingBox = (points) => {
    if (!points.length) return null;
    let minLat = Infinity,
      maxLat = -Infinity,
      minLng = Infinity,
      maxLng = -Infinity;
    points.forEach((p) => {
      minLat = Math.min(minLat, p.lat);
      maxLat = Math.max(maxLat, p.lat);
      minLng = Math.min(minLng, p.lng);
      maxLng = Math.max(maxLng, p.lng);
    });
    return { minLat, maxLat, minLng, maxLng };
  };

  const expandBoundingBox = (bbox, meters = 150) => {
    if (!bbox) return null;
    const latCenter = (bbox.minLat + bbox.maxLat) / 2;
    const latDelta = meters / 111320;
    const lngDelta = meters / (111320 * Math.cos((latCenter * Math.PI) / 180));
    return {
      minLat: bbox.minLat - latDelta,
      maxLat: bbox.maxLat + latDelta,
      minLng: bbox.minLng - lngDelta,
      maxLng: bbox.maxLng + lngDelta,
    };
  };

  const bboxToLatLngArray = (bbox) => {
    if (!bbox) return [];
    return [
      { lat: bbox.minLat, lng: bbox.minLng },
      { lat: bbox.minLat, lng: bbox.maxLng },
      { lat: bbox.maxLat, lng: bbox.maxLng },
      { lat: bbox.maxLat, lng: bbox.minLng },
    ];
  };

  const capBboxSize = (bbox, maxMeters) => {
    if (!bbox || !maxMeters) return bbox;
    const latCenter = (bbox.minLat + bbox.maxLat) / 2;
    const lngCenter = (bbox.minLng + bbox.maxLng) / 2;
    const heightMeters = (bbox.maxLat - bbox.minLat) * 111320;
    const widthMeters =
      (bbox.maxLng - bbox.minLng) * 111320 * Math.cos((latCenter * Math.PI) / 180);
    const largest = Math.max(heightMeters, widthMeters, 1);
    if (largest <= maxMeters) return bbox;
    const scale = maxMeters / largest;
    const latDelta = ((bbox.maxLat - bbox.minLat) * scale) / 2;
    const lngDelta = ((bbox.maxLng - bbox.minLng) * scale) / 2;
    return {
      minLat: latCenter - latDelta,
      maxLat: latCenter + latDelta,
      minLng: lngCenter - lngDelta,
      maxLng: lngCenter + lngDelta,
    };
  };

  const buildBboxRect = useCallback((pnds, expandMeters = 300, maxMeters = null) => {
    const valid = pnds.filter(
      (p) =>
        p.latitude &&
        p.longitude &&
        !isNaN(parseFloat(p.latitude)) &&
        !isNaN(parseFloat(p.longitude))
    );
    if (!valid.length) return null;
    const raw = valid.map((p) => ({ lat: parseFloat(p.latitude), lng: parseFloat(p.longitude) }));
    const bbox = capBboxSize(computeBoundingBox(raw), maxMeters);
    return bboxToLatLngArray(expandBoundingBox(bbox, expandMeters)).map(
      (p) => new window.google.maps.LatLng(p.lat, p.lng)
    );
  }, []);

  const smoothFlyTo = useCallback(
    (map, targetBounds, targetZoom, onComplete, rightPaddingPx = 0) => {
      if (!map || !window.google) return;
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
      const startCenter = map.getCenter();
      const startZoom = map.getZoom();

      // targetZoom null/undefined => bounds se ideal zoom (content cut na ho).
      const autoZoom = targetZoom == null;
      let endZoom = targetZoom;
      if (autoZoom) {
        const sw = targetBounds.getSouthWest();
        const ne = targetBounds.getNorthEast();
        const latDiff = Math.max(Math.abs(ne.lat() - sw.lat()), 0.0005);
        const lngDiff = Math.max(Math.abs(ne.lng() - sw.lng()), 0.0005);
        const maxDiff = Math.max(latDiff, lngDiff);
        endZoom = Math.round(Math.log2(360 / maxDiff)) - 1;
        endZoom = Math.min(18, Math.max(4, endZoom));
      } else {
        endZoom = targetZoom || 15;
      }

      let endCenter = targetBounds.getCenter();
      // When autoZoom, final frame uses fitBounds (handles panel padding itself).
      // Only apply lng shift for fixed-zoom flights.
      if (rightPaddingPx && !autoZoom) {
        const lngShift = ((rightPaddingPx / 2) * 360) / (256 * Math.pow(2, endZoom));
        endCenter = new window.google.maps.LatLng(endCenter.lat(), endCenter.lng() + lngShift);
      }
      const midZoom = Math.min(startZoom, endZoom) - 1.5;
      const duration = 1200;
      let startTime = null;
      const ease = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
      const step = (ts) => {
        if (!startTime) startTime = ts;
        const rawT = Math.min((ts - startTime) / duration, 1);
        const t = ease(rawT);
        map.panTo({
          lat: startCenter.lat() + (endCenter.lat() - startCenter.lat()) * t,
          lng: startCenter.lng() + (endCenter.lng() - startCenter.lng()) * t,
        });
        map.setZoom(
          rawT < 0.4
            ? startZoom + (midZoom - startZoom) * ease(rawT / 0.4)
            : midZoom + (endZoom - midZoom) * ease((rawT - 0.4) / 0.6)
        );
        if (rawT < 1) {
          animationFrameRef.current = requestAnimationFrame(step);
        } else {
          if (autoZoom) {
            // fitBounds so content fills visible area between sidebar and detail panel
            const pad = 48;
            const panelPad = Math.max(0, Math.round((rightPaddingPx || 0) * 0.92));
            map.fitBounds(targetBounds, {
              top: pad,
              bottom: pad,
              left: pad + 14,
              right: pad + panelPad,
            });
          } else {
            map.panTo(endCenter);
            map.setZoom(endZoom);
          }
          onComplete?.();
        }
      };
      animationFrameRef.current = requestAnimationFrame(step);
    },
    []
  );

  const clearAll = useCallback(() => {
    polygonsRef.current.forEach((p) => p?.setMap?.(null));
    polygonsRef.current = [];
    markersRef.current.forEach((m) => m?.setMap?.(null));
    markersRef.current = [];
    markerMapRef.current = {};
    highlightedMarkerRef.current = null;
    gateHoverInfoWindowRef.current?.close();
  }, []);

  // Polygon border pe halka glow/shadow — OffStreetMap jaisa stacked dark strokes
  const addPolygonGlow = useCallback((map, paths, zIndexBase = 1) => {
    if (!map || !window.google) return [];
    const glowLayers = [
      { weight: 20, opacity: 0.06 },
      { weight: 16, opacity: 0.1 },
      { weight: 12, opacity: 0.16 },
      { weight: 8, opacity: 0.24 },
      { weight: 5, opacity: 0.34 },
    ];
    return glowLayers.map(
      (layer, i) =>
        new window.google.maps.Polygon({
          paths,
          strokeColor: "#000000",
          strokeOpacity: layer.opacity,
          strokeWeight: layer.weight,
          fillOpacity: 0,
          map,
          zIndex: zIndexBase - (glowLayers.length - i),
          clickable: false,
        })
    );
  }, []);

  const highlightMarker = useCallback(
    (pnd) => {
      if (!pnd || !window.google) return;
      const key = getPndKey(pnd);
      Object.values(markerMapRef.current).forEach((m) => {
        m.setIcon(
          createMarkerIcon(
            m.__pndDeviceType,
            m.__deviceColor || getDeviceColor(m.__pndDeviceType),
            false,
            m.__isNoTransaction || false
          )
        );
        m.setZIndex(100);
      });
      const m = markerMapRef.current[key];
      if (m) {
        m.setIcon(
          createMarkerIcon(
            pnd.device_type,
            m.__deviceColor || getDeviceColor(pnd.device_type),
            true,
            m.__isNoTransaction || false
          )
        );
        m.setZIndex(300);
        highlightedMarkerRef.current = m;
      }
    },
    [createMarkerIcon]
  );

  const addPndMarker = useCallback(
    (pnd, index, map) => {
      const lat = parseFloat(pnd.latitude);
      const lng = parseFloat(pnd.longitude);
      if (isNaN(lat) || isNaN(lng)) return;
      const pndNumStr = String(pnd.pnd_number);
      const isBelowThreshold = belowThresholdPnds.has(pndNumStr);
      const isNoTransaction = noTransactionPnds.has(pndNumStr);
      const deviceColor =
        !isNoTransaction && isBelowThreshold ? "#EF4444" : getDeviceColor(pnd.device_type);
      const pndKey = getPndKey(pnd);
      const marker = new window.google.maps.Marker({
        position: { lat, lng },
        map,
        icon: createMarkerIcon(pnd.device_type, deviceColor, false, isNoTransaction),
        title: pnd.device_type,
        zIndex: 100 + index,
      });
      marker.__pndKey = pndKey;
      marker.__pndDeviceType = pnd.device_type;
      marker.__deviceColor = deviceColor;
      marker.__isNoTransaction = isNoTransaction;
      markerMapRef.current[pndKey] = marker;
      marker.addListener("mouseover", () => {
        if (highlightedMarkerRef.current !== marker)
          marker.setIcon(createMarkerIcon(pnd.device_type, deviceColor, true, isNoTransaction));
      });
      marker.addListener("mouseout", () => {
        if (highlightedMarkerRef.current !== marker)
          marker.setIcon(createMarkerIcon(pnd.device_type, deviceColor, false, isNoTransaction));
      });
      marker.addListener("click", () => {
        highlightMarker(pnd);
        onPndClick(pnd);
      });
      markersRef.current.push(marker);
    },
    [belowThresholdPnds, noTransactionPnds, createMarkerIcon, highlightMarker, onPndClick]
  );

  const drawAllCities = useCallback(
    (countryFilter) => {
      const map = mapInstanceRef.current;
      if (!map || !window.google) return;
      const rawList = countryFilter
        ? citiesData.filter((c) => c.country_code === countryFilter)
        : citiesData;
      // Real cities jinke ops assign nahi (sirf city-level marker "COUNTRY::CITY")
      // — ops-boxes inhe cover nahi karte, isliye alag se draw karte hain.
      const cityBoxList = countryFilter
        ? cityBoxes.filter((c) => c.country_code === countryFilter)
        : cityBoxes;
      // Jis city ka apna city-level box already dikh raha hai, uske andar ke
      // ops yahan dobara nahi dikhate (duplicate) — wo sirf city ke andar
      // drill karne par (drawCityGeoView) dikhenge.
      const cityBoxCityCds = new Set(cityBoxList.map((c) => c.city_cd));
      const list = rawList.filter((c) => !cityBoxCityCds.has(c.parent_city_cd));
      // NOTE: list/cityBoxList dono empty ho sakte hain jab country sirf
      // country-level assign ho (koi city/ops accessible na ho) — is case mein
      // bhi map ko clear/redraw karna zaroori hai, warna purana view stuck reh jata hai.
      setMapLoading(true);
      clearAll();
      const globalBounds = new window.google.maps.LatLngBounds();
      let hasAny = false;
      const CITY_BOX_COLOR = "#10B981";

      // Country polygon + niche city/ops polygons (API ya default)
      if (countryFilter) {
        const countryNode = countries.find((c) => String(c.country) === String(countryFilter));
        const allDevs = countryNode
          ? devicesFromCountryNode(countryNode).filter(
              (d) =>
                d.latitude &&
                d.longitude &&
                !isNaN(parseFloat(d.latitude)) &&
                !isNaN(parseFloat(d.longitude))
            )
          : [];
        const countryPoly = resolveAreaPolygon(countryNode?.coordinates, allDevs, {
          minPadM: 80,
          maxPadM: 400,
          ratio: 0.2,
        });
        if (countryPoly.length >= 3) {
          hasAny = true;
          countryPoly.forEach((p) => globalBounds.extend(p));
          polygonsRef.current.push(...addPolygonGlow(map, countryPoly));
          polygonsRef.current.push(
            new window.google.maps.Polygon({
              paths: countryPoly,
              strokeColor: "#4338CA",
              strokeOpacity: 0.7,
              strokeWeight: 2.5,
              fillColor: "#4338CA",
              fillOpacity: 0.06,
              map,
              zIndex: 0,
              clickable: false,
            })
          );
        }
      }

      list.forEach((city) => {
        const allPnds = (city.streets || []).flatMap((s) => s.pnds || []);
        const valid = allPnds.filter(
          (p) =>
            p.latitude &&
            p.longitude &&
            !isNaN(parseFloat(p.latitude)) &&
            !isNaN(parseFloat(p.longitude))
        );
        if (!valid.length) return;
        valid.forEach((p) =>
          globalBounds.extend({ lat: parseFloat(p.latitude), lng: parseFloat(p.longitude) })
        );
        hasAny = true;
        const color = STREET_COLORS[list.indexOf(city) % STREET_COLORS.length];
        // Country view: city/ops polygon — API ya default bbox
        const polyCoords = resolveAreaPolygon(city.coordinates, valid, {
          minPadM: 40,
          maxPadM: 200,
          ratio: 0.18,
        });
        let centLat, centLng;
        if (polyCoords.length >= 3) {
          polyCoords.forEach((p) => globalBounds.extend(p));
          const darkColor = darkenColor(color, 0.35);
          polygonsRef.current.push(...addPolygonGlow(map, polyCoords));
          const polygon = new window.google.maps.Polygon({
            paths: polyCoords,
            strokeColor: darkColor,
            strokeOpacity: 0.95,
            strokeWeight: 2.5,
            fillColor: color,
            fillOpacity: 0.15,
            map,
            zIndex: 1,
            clickable: true,
          });
          polygon.addListener("click", () => onCityClick?.(city));
          polygonsRef.current.push(polygon);
          ({ lat: centLat, lng: centLng } = polygonCentroid(polyCoords));
        } else {
          centLat = valid.reduce((s, p) => s + parseFloat(p.latitude), 0) / valid.length;
          centLng = valid.reduce((s, p) => s + parseFloat(p.longitude), 0) / valid.length;
        }
        const darkLabel = darkenColor(color, 0.35);
        const name = (city.city_name || "City").slice(0, 14);
        const pndCnt = city.total_pnds || valid.length;
        const labelSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="90" height="36" viewBox="0 0 90 36">
        <rect x="0" y="0" width="90" height="36" rx="8" fill="${darkLabel}" opacity="0.95"/>
        <text x="45" y="13" font-family="sans-serif" font-size="9" font-weight="700" fill="white" text-anchor="middle" dominant-baseline="central">${name}</text>
        <text x="45" y="26" font-family="sans-serif" font-size="8" fill="white" opacity="0.9" text-anchor="middle" dominant-baseline="central">${pndCnt} Devices</text>
      </svg>`;
        const labelMarker = new window.google.maps.Marker({
          position: { lat: centLat, lng: centLng },
          map,
          icon: {
            url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(labelSvg)}`,
            scaledSize: new window.google.maps.Size(90, 36),
            anchor: new window.google.maps.Point(45, 18),
          },
          title: city.city_name,
          zIndex: 50,
        });
        labelMarker.addListener("click", () => onCityClick?.(city));
        markersRef.current.push(labelMarker);
      });

      cityBoxList.forEach((cityBox) => {
        const valid = (cityBox.devices || []).filter(
          (d) =>
            d.latitude &&
            d.longitude &&
            !isNaN(parseFloat(d.latitude)) &&
            !isNaN(parseFloat(d.longitude))
        );
        if (!valid.length) return;
        valid.forEach((d) =>
          globalBounds.extend({ lat: parseFloat(d.latitude), lng: parseFloat(d.longitude) })
        );
        hasAny = true;
        // Country view: city polygon — API ya devices se default bbox
        const polyCoords = resolveAreaPolygon(cityBox.coordinates, valid, {
          minPadM: 40,
          maxPadM: 200,
          ratio: 0.18,
        });
        let centLat, centLng;
        if (polyCoords.length >= 3) {
          polyCoords.forEach((p) => globalBounds.extend(p));
          const darkCity = darkenColor(CITY_BOX_COLOR, 0.35);
          polygonsRef.current.push(...addPolygonGlow(map, polyCoords));
          const polygon = new window.google.maps.Polygon({
            paths: polyCoords,
            strokeColor: darkCity,
            strokeOpacity: 0.95,
            strokeWeight: 2.5,
            fillColor: CITY_BOX_COLOR,
            fillOpacity: 0.15,
            map,
            zIndex: 1,
            clickable: true,
          });
          polygon.addListener("click", () => onCityGeoClick?.(cityBox));
          polygonsRef.current.push(polygon);
          ({ lat: centLat, lng: centLng } = polygonCentroid(polyCoords));
        } else {
          centLat = valid.reduce((s, d) => s + parseFloat(d.latitude), 0) / valid.length;
          centLng = valid.reduce((s, d) => s + parseFloat(d.longitude), 0) / valid.length;
        }
        const name = (cityBox.city_name || "City").slice(0, 14);
        const opsCnt = cityBox.total_ops ?? (cityBox.ops || []).length;
        const labelSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="90" height="36" viewBox="0 0 90 36">
        <rect x="0" y="0" width="90" height="36" rx="8" fill="${CITY_BOX_COLOR}" opacity="0.9"/>
        <text x="45" y="13" font-family="sans-serif" font-size="9" font-weight="700" fill="white" text-anchor="middle" dominant-baseline="central">${name}</text>
        <text x="45" y="26" font-family="sans-serif" font-size="8" fill="white" opacity="0.9" text-anchor="middle" dominant-baseline="central">${opsCnt} Ops</text>
      </svg>`;
        const labelMarker = new window.google.maps.Marker({
          position: { lat: centLat, lng: centLng },
          map,
          icon: {
            url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(labelSvg)}`,
            scaledSize: new window.google.maps.Size(90, 36),
            anchor: new window.google.maps.Point(45, 18),
          },
          title: cityBox.city_name,
          zIndex: 50,
        });
        labelMarker.addListener("click", () => onCityGeoClick?.(cityBox));
        markersRef.current.push(labelMarker);
      });

      // Country ke andar koi accessible city/ops nahi mila (sirf country-level
      // marker assign hai) — khud country ka box dikha dete hain taake map
      // khaali/purana view pe stuck na rahe.
      if (!hasAny && countryFilter) {
        const countryNode = countries.find((c) => String(c.country) === String(countryFilter));
        const valid = countryNode
          ? devicesFromCountryNode(countryNode).filter(
              (d) =>
                d.latitude &&
                d.longitude &&
                !isNaN(parseFloat(d.latitude)) &&
                !isNaN(parseFloat(d.longitude))
            )
          : [];
        if (valid.length) {
          valid.forEach((d) =>
            globalBounds.extend({ lat: parseFloat(d.latitude), lng: parseFloat(d.longitude) })
          );
          hasAny = true;
          const realCoords = normalizePolygonCoords(countryNode?.coordinates);
          let centLat, centLng;
          if (realCoords.length) {
            realCoords.forEach((p) => globalBounds.extend(p));
            polygonsRef.current.push(...addPolygonGlow(map, realCoords));
            const polygon = new window.google.maps.Polygon({
              paths: realCoords,
              strokeColor: darkenColor("#9CA3AF", 0.35),
              strokeOpacity: 0.7,
              strokeWeight: 2.5,
              fillColor: "#9CA3AF",
              fillOpacity: 0.1,
              map,
              zIndex: 1,
              clickable: false,
            });
            polygonsRef.current.push(polygon);
            ({ lat: centLat, lng: centLng } = polygonCentroid(realCoords));
          } else {
            centLat = valid.reduce((s, d) => s + parseFloat(d.latitude), 0) / valid.length;
            centLng = valid.reduce((s, d) => s + parseFloat(d.longitude), 0) / valid.length;
          }
          const labelSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="170" height="34" viewBox="0 0 170 34">
          <rect x="0" y="0" width="170" height="34" rx="8" fill="#9CA3AF" opacity="0.92"/>
          <text x="85" y="17" font-family="sans-serif" font-size="9" font-weight="700" fill="white" text-anchor="middle" dominant-baseline="central">No accessible cities/ops</text>
        </svg>`;
          markersRef.current.push(
            new window.google.maps.Marker({
              position: { lat: centLat, lng: centLng },
              map,
              icon: {
                url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(labelSvg)}`,
                scaledSize: new window.google.maps.Size(170, 34),
                anchor: new window.google.maps.Point(85, 17),
              },
              title: countryNode?.country_name,
              zIndex: 50,
              clickable: false,
            })
          );
        }
      }

      if (hasAny) {
        map.fitBounds(globalBounds, {
          top: 80,
          bottom: 80,
          left: 96,
          right: 80 + Math.round((panelWidth || 0) * 0.92),
        });
        setTimeout(() => {
          const z = map.getZoom();
          if (z > 16) map.setZoom(16);
          setMapLoading(false);
        }, 500);
      } else {
        setMapLoading(false);
      }
    },
    [
      citiesData,
      cityBoxes,
      countries,
      clearAll,
      addPolygonGlow,
      onCityClick,
      onCityGeoClick,
      panelWidth,
    ]
  );

  const drawCityGeoView = useCallback(
    (cityGeo) => {
      const map = mapInstanceRef.current;
      if (!map || !window.google || !cityGeo) return;
      setMapLoading(true);
      clearAll();
      const opsInCity = citiesData.filter(
        (c) => c.parent_city_cd === cityGeo.city_cd && c.country_code === cityGeo.country_code
      );
      const globalBounds = new window.google.maps.LatLngBounds();
      let hasAny = false;

      const cityGeoRealCoords = normalizePolygonCoords(cityGeo?.coordinates);
      if (cityGeoRealCoords.length) {
        hasAny = true;
        cityGeoRealCoords.forEach((p) => globalBounds.extend(p));
        polygonsRef.current.push(...addPolygonGlow(map, cityGeoRealCoords));
        polygonsRef.current.push(
          new window.google.maps.Polygon({
            paths: cityGeoRealCoords,
            strokeColor: "#4338CA",
            strokeOpacity: 0.55,
            strokeWeight: 2,
            fillColor: "#4338CA",
            fillOpacity: 0.06,
            map,
            zIndex: 0,
            clickable: false,
          })
        );
      }

      opsInCity.forEach((city, idx) => {
        const allPnds = (city.streets || []).flatMap((s) => s.pnds || []);
        const valid = allPnds.filter(
          (p) =>
            p.latitude &&
            p.longitude &&
            !isNaN(parseFloat(p.latitude)) &&
            !isNaN(parseFloat(p.longitude))
        );
        if (!valid.length) return;
        valid.forEach((p) =>
          globalBounds.extend({ lat: parseFloat(p.latitude), lng: parseFloat(p.longitude) })
        );
        hasAny = true;
        const color = STREET_COLORS[idx % STREET_COLORS.length];
        const realCoords = normalizePolygonCoords(city.coordinates);
        let centLat, centLng;
        if (realCoords.length) {
          realCoords.forEach((p) => globalBounds.extend(p));
          const darkColor = darkenColor(color, 0.35);
          polygonsRef.current.push(...addPolygonGlow(map, realCoords));
          const polygon = new window.google.maps.Polygon({
            paths: realCoords,
            strokeColor: darkColor,
            strokeOpacity: 0.95,
            strokeWeight: 2.5,
            fillColor: color,
            fillOpacity: 0.15,
            map,
            zIndex: 1,
            clickable: true,
          });
          polygon.addListener("click", () => onCityClick?.(city));
          polygonsRef.current.push(polygon);
          ({ lat: centLat, lng: centLng } = polygonCentroid(realCoords));
        } else {
          centLat = valid.reduce((s, p) => s + parseFloat(p.latitude), 0) / valid.length;
          centLng = valid.reduce((s, p) => s + parseFloat(p.longitude), 0) / valid.length;
        }
        const darkLabel = darkenColor(color, 0.35);
        const name = (city.city_name || "City").slice(0, 14);
        const pndCnt = city.total_pnds || valid.length;
        const labelSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="90" height="36" viewBox="0 0 90 36">
        <rect x="0" y="0" width="90" height="36" rx="8" fill="${darkLabel}" opacity="0.95"/>
        <text x="45" y="13" font-family="sans-serif" font-size="9" font-weight="700" fill="white" text-anchor="middle" dominant-baseline="central">${name}</text>
        <text x="45" y="26" font-family="sans-serif" font-size="8" fill="white" opacity="0.9" text-anchor="middle" dominant-baseline="central">${pndCnt} Devices</text>
      </svg>`;
        const labelMarker = new window.google.maps.Marker({
          position: { lat: centLat, lng: centLng },
          map,
          icon: {
            url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(labelSvg)}`,
            scaledSize: new window.google.maps.Size(90, 36),
            anchor: new window.google.maps.Point(45, 18),
          },
          title: city.city_name,
          zIndex: 50,
        });
        labelMarker.addListener("click", () => onCityClick?.(city));
        markersRef.current.push(labelMarker);
      });

      if (!hasAny) {
        const cityBox =
          cityBoxes.find(
            (c) => c.city_cd === cityGeo.city_cd && c.country_code === cityGeo.country_code
          ) || cityGeo;
        const valid = (cityBox.devices || []).filter(
          (d) =>
            d.latitude &&
            d.longitude &&
            !isNaN(parseFloat(d.latitude)) &&
            !isNaN(parseFloat(d.longitude))
        );
        if (valid.length) {
          valid.forEach((d) =>
            globalBounds.extend({ lat: parseFloat(d.latitude), lng: parseFloat(d.longitude) })
          );
          hasAny = true;
          const name = (cityGeo.city_name || "City").slice(0, 18);
          const labelSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="150" height="34" viewBox="0 0 150 34">
          <rect x="0" y="0" width="150" height="34" rx="8" fill="#10B981" opacity="0.92"/>
          <text x="75" y="17" font-family="sans-serif" font-size="9" font-weight="700" fill="white" text-anchor="middle" dominant-baseline="central">${name} · No accessible ops</text>
        </svg>`;
          const centLat = valid.reduce((s, d) => s + parseFloat(d.latitude), 0) / valid.length;
          const centLng = valid.reduce((s, d) => s + parseFloat(d.longitude), 0) / valid.length;
          markersRef.current.push(
            new window.google.maps.Marker({
              position: { lat: centLat, lng: centLng },
              map,
              icon: {
                url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(labelSvg)}`,
                scaledSize: new window.google.maps.Size(150, 34),
                anchor: new window.google.maps.Point(75, 17),
              },
              title: cityGeo.city_name,
              zIndex: 50,
              clickable: false,
            })
          );
        }
      }

      if (hasAny) {
        map.fitBounds(globalBounds, {
          top: 80,
          bottom: 80,
          left: 96,
          right: 80 + Math.round((panelWidth || 0) * 0.92),
        });
        setTimeout(() => {
          const z = map.getZoom();
          if (z > 16) map.setZoom(16);
          setMapLoading(false);
        }, 500);
      } else {
        setMapLoading(false);
      }
    },
    [citiesData, cityBoxes, clearAll, addPolygonGlow, onCityClick, panelWidth]
  );

  const buildFixedBoxRect = useCallback((centerLat, centerLng, sizeMeters) => {
    if (!window.google) return null;
    const latDelta = sizeMeters / 2 / 111320;
    const lngDelta = sizeMeters / 2 / (111320 * Math.cos((centerLat * Math.PI) / 180));
    return bboxToLatLngArray({
      minLat: centerLat - latDelta,
      maxLat: centerLat + latDelta,
      minLng: centerLng - lngDelta,
      maxLng: centerLng + lngDelta,
    }).map((p) => new window.google.maps.LatLng(p.lat, p.lng));
  }, []);

  const drawAllCountries = useCallback(() => {
    const map = mapInstanceRef.current;
    if (!map || !window.google) return;
    setMapLoading(true);
    clearAll();
    if (!countries.length) {
      // Koi accessible country nahi (permission null/empty) — purana view clear
      // kar ke loading band karo, warna shimmer hamesha ke liye chipak jayega.
      setMapLoading(false);
      return;
    }
    const globalBounds = new window.google.maps.LatLngBounds();
    let hasAny = false;
    const MUTED_COLOR = "#9CA3AF";

    countries.forEach((country, idx) => {
      // Position ke liye raw (unfiltered) device coordinates chahiye — country
      // level access ke case mein bhi box dikhna chahiye, chahe ops assign na hon.
      const valid = devicesFromCountryNode(country).filter(
        (d) =>
          d.latitude &&
          d.longitude &&
          !isNaN(parseFloat(d.latitude)) &&
          !isNaN(parseFloat(d.longitude))
      );
      if (!valid.length) return;
      valid.forEach((d) =>
        globalBounds.extend({ lat: parseFloat(d.latitude), lng: parseFloat(d.longitude) })
      );
      hasAny = true;

      // Directly assigned countries clickable/colored; visible-only (via
      // "All Locations" or an accessible descendant) countries — dimmed, non-clickable.
      const accessible = country._accessible !== false;
      const color = accessible ? STREET_COLORS[idx % STREET_COLORS.length] : MUTED_COLOR;
      // API polygon only — no default fixed-size box
      const realCoords = normalizePolygonCoords(country.coordinates);
      let centLat, centLng;
      if (realCoords.length) {
        realCoords.forEach((p) => globalBounds.extend(p));
        const darkColor = darkenColor(color, 0.35);
        polygonsRef.current.push(...addPolygonGlow(map, realCoords));
        const polygon = new window.google.maps.Polygon({
          paths: realCoords,
          strokeColor: darkColor,
          strokeOpacity: accessible ? 0.95 : 0.6,
          strokeWeight: 2.5,
          fillColor: color,
          fillOpacity: accessible ? 0.15 : 0.1,
          map,
          zIndex: 1,
          clickable: accessible,
        });
        if (accessible) polygon.addListener("click", () => onCountryClick?.(country.country));
        polygonsRef.current.push(polygon);
        ({ lat: centLat, lng: centLng } = polygonCentroid(realCoords));
      } else {
        centLat = valid.reduce((s, d) => s + parseFloat(d.latitude), 0) / valid.length;
        centLng = valid.reduce((s, d) => s + parseFloat(d.longitude), 0) / valid.length;
      }
      const name = (country.country_name || "Country").slice(0, 18);
      const opsCnt = country.total_ops ?? getCountryOps(country).length;
      const deviceCnt = country.total_devices ?? valid.length;
      const darkLabel = darkenColor(color, 0.35);
      const labelSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="120" height="38" viewBox="0 0 120 38">
        <rect x="0" y="0" width="120" height="38" rx="8" fill="${darkLabel}" opacity="0.95"/>
        <text x="60" y="14" font-family="sans-serif" font-size="10" font-weight="700" fill="white" text-anchor="middle" dominant-baseline="central">${name}</text>
        <text x="60" y="27" font-family="sans-serif" font-size="8" fill="white" opacity="0.9" text-anchor="middle" dominant-baseline="central">${opsCnt} ops · ${deviceCnt} devices</text>
      </svg>`;
      const labelMarker = new window.google.maps.Marker({
        position: { lat: centLat, lng: centLng },
        map,
        icon: {
          url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(labelSvg)}`,
          scaledSize: new window.google.maps.Size(120, 38),
          anchor: new window.google.maps.Point(60, 19),
        },
        title: country.country_name,
        zIndex: 50,
        clickable: accessible,
      });
      if (accessible) labelMarker.addListener("click", () => onCountryClick?.(country.country));
      markersRef.current.push(labelMarker);
    });

    if (hasAny) {
      map.fitBounds(globalBounds, {
        top: 80,
        bottom: 80,
        left: 96,
        right: 80 + Math.round((panelWidth || 0) * 0.92),
      });
      setTimeout(() => {
        const z = map.getZoom();
        if (z > 14) map.setZoom(14);
        setMapLoading(false);
      }, 500);
    } else {
      setMapLoading(false);
    }
  }, [countries, clearAll, addPolygonGlow, onCountryClick, panelWidth]);

  const drawCityView = useCallback(
    (city) => {
      const map = mapInstanceRef.current;
      if (!map || !window.google || !city) return;
      setMapLoading(true);
      clearAll();
      const allPnds = (city.streets || []).flatMap((s) => s.pnds || []);
      const validAll = allPnds.filter(
        (p) =>
          p.latitude &&
          p.longitude &&
          !isNaN(parseFloat(p.latitude)) &&
          !isNaN(parseFloat(p.longitude))
      );
      if (!validAll.length) {
        setMapLoading(false);
        return;
      }
      const cityBounds = new window.google.maps.LatLngBounds();
      validAll.forEach((p) =>
        cityBounds.extend({ lat: parseFloat(p.latitude), lng: parseFloat(p.longitude) })
      );
      // Ops-level API polygon only (no default bbox)
      const cityRealCoords = normalizePolygonCoords(city.coordinates);
      if (cityRealCoords.length) {
        cityRealCoords.forEach((p) => cityBounds.extend(p));
        polygonsRef.current.push(...addPolygonGlow(map, cityRealCoords));
        polygonsRef.current.push(
          new window.google.maps.Polygon({
            paths: cityRealCoords,
            strokeColor: "#4338CA",
            strokeOpacity: 0.9,
            strokeWeight: 2,
            fillColor: "#6366F1",
            fillOpacity: 0.06,
            map,
            zIndex: 1,
            clickable: false,
          })
        );
      }
      (city.streets || []).forEach((street, si) => {
        const sPnds = (street.pnds || []).filter(
          (p) =>
            p.latitude &&
            p.longitude &&
            !isNaN(parseFloat(p.latitude)) &&
            !isNaN(parseFloat(p.longitude))
        );
        if (!sPnds.length) return;
        const sColor = STREET_COLORS[si % STREET_COLORS.length];
        const sDark = darkenColor(sColor, 0.35);
        const coords = sPnds.map((p) => ({
          lat: parseFloat(p.latitude),
          lng: parseFloat(p.longitude),
        }));
        // Location API polygon only — no createRoadPolygon default
        const realCoords = normalizePolygonCoords(street.coordinates);
        let sLat, sLng;
        if (realCoords.length) {
          realCoords.forEach((p) => cityBounds.extend(p));
          polygonsRef.current.push(...addPolygonGlow(map, realCoords, 2));
          const streetPolygon = new window.google.maps.Polygon({
            paths: realCoords,
            strokeColor: sDark,
            strokeOpacity: 1,
            strokeWeight: 2.5,
            fillColor: sColor,
            fillOpacity: 0.28,
            map,
            zIndex: 2,
            clickable: true,
          });
          streetPolygon.addListener("mouseover", () => {
            streetPolygon.setOptions({ fillOpacity: 0.5, strokeWeight: 3.5 });
          });
          streetPolygon.addListener("mouseout", () => {
            streetPolygon.setOptions({ fillOpacity: 0.28, strokeWeight: 2.5 });
          });
          streetPolygon.addListener("click", () => {
            onStreetClick?.(street);
          });
          polygonsRef.current.push(streetPolygon);
          ({ lat: sLat, lng: sLng } = polygonCentroid(realCoords));
        } else {
          sLat = coords.reduce((s, c) => s + c.lat, 0) / coords.length;
          sLng = coords.reduce((s, c) => s + c.lng, 0) / coords.length;
        }
        const sShort = (street.street_name || "Street").slice(0, 16);
        const sLabelSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="80" height="22" viewBox="0 0 80 22">
        <rect x="0" y="0" width="80" height="22" rx="5" fill="${sDark}" opacity="0.95"/>
        <text x="40" y="11" font-family="sans-serif" font-size="8" font-weight="700" fill="white" text-anchor="middle" dominant-baseline="central">${sShort}</text>
      </svg>`;
        const sLabelMarker = new window.google.maps.Marker({
          position: { lat: sLat, lng: sLng },
          map,
          icon: {
            url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(sLabelSvg)}`,
            scaledSize: new window.google.maps.Size(80, 22),
            anchor: new window.google.maps.Point(40, 11),
          },
          title: street.street_name,
          zIndex: 60,
          clickable: true,
        });
        sLabelMarker.addListener("click", () => {
          onStreetClick?.(street);
        });
        markersRef.current.push(sLabelMarker);
        sPnds.forEach((pnd, pi) => addPndMarker(pnd, pi, map));
      });
      const sw = cityBounds.getSouthWest();
      const ne = cityBounds.getNorthEast();
      const latSpan = Math.max(Math.abs(ne.lat() - sw.lat()), 0.0003);
      const lngSpan = Math.max(Math.abs(ne.lng() - sw.lng()), 0.0003);
      const padLat = Math.max(latSpan * 0.18, 0.00012);
      const padLng = Math.max(lngSpan * 0.18, 0.00012);
      smoothFlyTo(
        map,
        new window.google.maps.LatLngBounds(
          new window.google.maps.LatLng(sw.lat() - padLat, sw.lng() - padLng),
          new window.google.maps.LatLng(ne.lat() + padLat, ne.lng() + padLng)
        ),
        null,
        () => setMapLoading(false),
        panelWidth
      );
    },
    [clearAll, addPolygonGlow, addPndMarker, smoothFlyTo, onStreetClick, panelWidth]
  );

  const drawStreetView = useCallback(
    (street) => {
      const map = mapInstanceRef.current;
      if (!map || !window.google || !street) return;
      setMapLoading(true);
      clearAll();
      const valid = (street.pnds || []).filter(
        (p) =>
          p.latitude &&
          p.longitude &&
          !isNaN(parseFloat(p.latitude)) &&
          !isNaN(parseFloat(p.longitude))
      );
      if (!valid.length) {
        setMapLoading(false);
        return;
      }
      const bounds = new window.google.maps.LatLngBounds();
      valid.forEach((p) =>
        bounds.extend({ lat: parseFloat(p.latitude), lng: parseFloat(p.longitude) })
      );
      // Include gate / street polygon rings so the full parking footprint drives zoom
      valid.forEach((p) => {
        const coords = normalizePolygonCoords(p.gate_coordinates);
        coords.forEach((c) => {
          if (c && typeof c.lat === "number" && typeof c.lng === "number") bounds.extend(c);
        });
      });
      if (street?.coordinates) {
        normalizePolygonCoords(street.coordinates).forEach((c) => {
          if (c && typeof c.lat === "number" && typeof c.lng === "number") bounds.extend(c);
        });
      }

      const byGate = new Map();
      valid.forEach((p) => {
        const key = p.gate_id || p.gate_name || "Unknown";
        if (!byGate.has(key))
          byGate.set(key, { gate_id: p.gate_id, gate_name: p.gate_name, pnds: [] });
        byGate.get(key).pnds.push(p);
      });

      // Location-level API polygon once (shared backdrop) — no default gate polygons from device points
      const streetRealCoords = normalizePolygonCoords(street.coordinates);
      if (streetRealCoords.length) {
        streetRealCoords.forEach((p) => bounds.extend(p));
        polygonsRef.current.push(...addPolygonGlow(map, streetRealCoords));
        polygonsRef.current.push(
          new window.google.maps.Polygon({
            paths: streetRealCoords,
            strokeColor: "#4338CA",
            strokeOpacity: 0.9,
            strokeWeight: 2,
            fillColor: "#6366F1",
            fillOpacity: 0.06,
            map,
            zIndex: 1,
            clickable: false,
          })
        );
      }

      Array.from(byGate.values()).forEach((gateGroup, gi) => {
        const gColor = STREET_COLORS[gi % STREET_COLORS.length];
        const gDark = darkenColor(gColor, 0.35);
        const coords = gateGroup.pnds.map((p) => ({
          lat: parseFloat(p.latitude),
          lng: parseFloat(p.longitude),
        }));
        // Gate API polygon only (gate_coordinates on devices if present)
        const gateReal = normalizePolygonCoords(
          gateGroup.coordinates || gateGroup.pnds?.[0]?.gate_coordinates
        );
        let gLat,
          gLng,
          gatePolygon = null;
        if (gateReal.length) {
          gateReal.forEach((p) => bounds.extend(p));
          polygonsRef.current.push(...addPolygonGlow(map, gateReal));
          gatePolygon = new window.google.maps.Polygon({
            paths: gateReal,
            strokeColor: gDark,
            strokeOpacity: 0.95,
            strokeWeight: 3,
            fillColor: gColor,
            fillOpacity: 0.28,
            map,
            zIndex: 2,
            clickable: true,
          });
          ({ lat: gLat, lng: gLng } = polygonCentroid(gateReal));
        } else {
          gLat = coords.reduce((s, c) => s + c.lat, 0) / coords.length;
          gLng = coords.reduce((s, c) => s + c.lng, 0) / coords.length;
        }

        const isGeneral = isGeneralGate(gateGroup);
        const showGeneralHover = () => {
          if (!isGeneral) return;
          if (!gateHoverInfoWindowRef.current) {
            gateHoverInfoWindowRef.current = new window.google.maps.InfoWindow({
              disableAutoPan: true,
            });
          }
          gateHoverInfoWindowRef.current.setContent(buildGeneralGateHoverHtml(gateGroup.pnds));
          gateHoverInfoWindowRef.current.setPosition({ lat: gLat, lng: gLng });
          gateHoverInfoWindowRef.current.open(map);
        };
        const hideGeneralHover = () => {
          gateHoverInfoWindowRef.current?.close();
        };

        if (gatePolygon) {
          gatePolygon.addListener("mouseover", () => {
            gatePolygon.setOptions({ fillOpacity: 0.5, strokeWeight: 3.5 });
            showGeneralHover();
          });
          gatePolygon.addListener("mouseout", () => {
            gatePolygon.setOptions({ fillOpacity: 0.28, strokeWeight: 3 });
            hideGeneralHover();
          });
          gatePolygon.addListener("click", () => {
            hideGeneralHover();
            if (isGeneral) onStreetClick?.(street);
            else onGateClick?.(gateGroup);
          });
          polygonsRef.current.push(gatePolygon);
        }

        const gShort = getGateDisplayName(gateGroup).slice(0, 16);
        const gLabelSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="82" height="22" viewBox="0 0 82 22">
        <rect x="0" y="0" width="82" height="22" rx="5" fill="${gDark}" opacity="0.95"/>
        <text x="41" y="11" font-family="sans-serif" font-size="8" font-weight="700" fill="white" text-anchor="middle" dominant-baseline="central">${gShort}</text>
      </svg>`;
        const gLabelMarker = new window.google.maps.Marker({
          position: { lat: gLat, lng: gLng },
          map,
          icon: {
            url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(gLabelSvg)}`,
            scaledSize: new window.google.maps.Size(82, 22),
            anchor: new window.google.maps.Point(41, 11),
          },
          title: getGateDisplayName(gateGroup),
          zIndex: 55,
          clickable: true,
        });
        gLabelMarker.addListener("mouseover", showGeneralHover);
        gLabelMarker.addListener("mouseout", hideGeneralHover);
        gLabelMarker.addListener("click", () => {
          hideGeneralHover();
          if (isGeneral) onStreetClick?.(street);
          else onGateClick?.(gateGroup);
        });
        markersRef.current.push(gLabelMarker);
      });

      valid.forEach((pnd, i) => addPndMarker(pnd, i, map));
      const sw = bounds.getSouthWest();
      const ne = bounds.getNorthEast();
      const latSpan = Math.max(Math.abs(ne.lat() - sw.lat()), 0.00025);
      const lngSpan = Math.max(Math.abs(ne.lng() - sw.lng()), 0.00025);
      const padLat = Math.max(latSpan * 0.2, 0.0001);
      const padLng = Math.max(lngSpan * 0.2, 0.0001);
      smoothFlyTo(
        map,
        new window.google.maps.LatLngBounds(
          new window.google.maps.LatLng(sw.lat() - padLat, sw.lng() - padLng),
          new window.google.maps.LatLng(ne.lat() + padLat, ne.lng() + padLng)
        ),
        null,
        () => setMapLoading(false),
        panelWidth
      );
    },
    [clearAll, addPolygonGlow, addPndMarker, smoothFlyTo, panelWidth, onGateClick, onStreetClick]
  );

  const drawGateView = useCallback(
    (street, gate) => {
      const map = mapInstanceRef.current;
      if (!map || !window.google || !street || !gate) return;
      setMapLoading(true);
      clearAll();
      const gateId = gate.gate_id ?? gate.gate;
      const gateName = gate.gate_name;
      const target = String(gateId || gateName || "Unknown").toUpperCase();
      const valid = (street.pnds || []).filter((p) => {
        if (
          !p.latitude ||
          !p.longitude ||
          isNaN(parseFloat(p.latitude)) ||
          isNaN(parseFloat(p.longitude))
        )
          return false;
        const pGateId = String(p.gate_id || p.gate_name || "Unknown").toUpperCase();
        const pGateName = String(p.gate_name || "").toUpperCase();
        return pGateId === target || (gateName && pGateName === String(gateName).toUpperCase());
      });
      if (!valid.length) {
        setMapLoading(false);
        return;
      }
      const bounds = new window.google.maps.LatLngBounds();
      valid.forEach((p) =>
        bounds.extend({ lat: parseFloat(p.latitude), lng: parseFloat(p.longitude) })
      );
      const coords = valid.map((p) => ({
        lat: parseFloat(p.latitude),
        lng: parseFloat(p.longitude),
      }));

      const streetRealCoords = normalizePolygonCoords(street.coordinates);
      if (streetRealCoords.length) {
        streetRealCoords.forEach((p) => bounds.extend(p));
        polygonsRef.current.push(...addPolygonGlow(map, streetRealCoords));
        polygonsRef.current.push(
          new window.google.maps.Polygon({
            paths: streetRealCoords,
            strokeColor: "#4338CA",
            strokeOpacity: 0.55,
            strokeWeight: 2,
            fillColor: "#4338CA",
            fillOpacity: 0.06,
            map,
            zIndex: 0,
            clickable: false,
          })
        );
      }

      const realCoords = normalizePolygonCoords(gate.coordinates || valid[0]?.gate_coordinates);
      let gLat, gLng;
      if (realCoords.length) {
        realCoords.forEach((p) => bounds.extend(p));
        polygonsRef.current.push(...addPolygonGlow(map, realCoords));
        polygonsRef.current.push(
          new window.google.maps.Polygon({
            paths: realCoords,
            strokeColor: "#3730A3",
            strokeOpacity: 0.95,
            strokeWeight: 3,
            fillColor: "#6366F1",
            fillOpacity: 0.28,
            map,
            zIndex: 1,
            clickable: false,
          })
        );
        ({ lat: gLat, lng: gLng } = polygonCentroid(realCoords));
      } else {
        gLat = coords.reduce((s, c) => s + c.lat, 0) / coords.length;
        gLng = coords.reduce((s, c) => s + c.lng, 0) / coords.length;
      }
      const gShort = getGateDisplayName(gate).toString().slice(0, 18);
      const gLabelSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="90" height="24" viewBox="0 0 90 24">
      <rect x="0" y="0" width="90" height="24" rx="6" fill="#6366F1" opacity="0.92"/>
      <text x="45" y="12" font-family="sans-serif" font-size="9" font-weight="700" fill="white" text-anchor="middle" dominant-baseline="central">${gShort}</text>
    </svg>`;
      markersRef.current.push(
        new window.google.maps.Marker({
          position: { lat: gLat, lng: gLng },
          map,
          icon: {
            url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(gLabelSvg)}`,
            scaledSize: new window.google.maps.Size(90, 24),
            anchor: new window.google.maps.Point(45, -6),
          },
          title: gateName,
          zIndex: 55,
          clickable: false,
        })
      );
      valid.forEach((pnd, i) => addPndMarker(pnd, i, map));
      const sw = bounds.getSouthWest();
      const ne = bounds.getNorthEast();
      const latSpan = Math.max(Math.abs(ne.lat() - sw.lat()), 0.0002);
      const lngSpan = Math.max(Math.abs(ne.lng() - sw.lng()), 0.0002);
      const padLat = Math.max(latSpan * 0.22, 0.00008);
      const padLng = Math.max(lngSpan * 0.22, 0.00008);
      smoothFlyTo(
        map,
        new window.google.maps.LatLngBounds(
          new window.google.maps.LatLng(sw.lat() - padLat, sw.lng() - padLng),
          new window.google.maps.LatLng(ne.lat() + padLat, ne.lng() + padLng)
        ),
        null,
        () => setMapLoading(false),
        panelWidth
      );
    },
    [clearAll, addPolygonGlow, addPndMarker, smoothFlyTo, panelWidth]
  );

  const drawSinglePnd = useCallback(
    (pnd) => {
      const map = mapInstanceRef.current;
      if (!map || !window.google || !pnd?.latitude || !pnd?.longitude) return;
      setMapLoading(true);
      clearAll();
      const lat = parseFloat(pnd.latitude);
      const lng = parseFloat(pnd.longitude);
      if (isNaN(lat) || isNaN(lng)) {
        setMapLoading(false);
        return;
      }
      const dc = getDeviceColor(pnd.device_type);
      markersRef.current.push(
        new window.google.maps.Marker({
          position: { lat, lng },
          map,
          icon: createMarkerIcon(pnd.device_type, dc, true),
          animation: window.google.maps.Animation.DROP,
          zIndex: 200,
        })
      );
      smoothFlyTo(
        map,
        new window.google.maps.LatLngBounds({ lat, lng }, { lat, lng }),
        17,
        () => setMapLoading(false),
        panelWidth
      );
    },
    [clearAll, createMarkerIcon, smoothFlyTo, panelWidth]
  );

  useEffect(() => {
    if (mapReady) return;
    const startedAt = Date.now();
    const MAX_PERMS_WAIT_MS = 5000;
    const check = () => {
      const waitedTooLong = Date.now() - startedAt > MAX_PERMS_WAIT_MS;
      if (!window.google || !mapInstanceRef.current || (!permsReady && !waitedTooLong)) {
        setTimeout(check, 200);
        return;
      }
      setMapReady(true);
    };
    check();
  }, [citiesData, drawAllCountries, permsReady, mapReady]);

  useEffect(() => {
    if (!mapReady) return;
    if (selectedCity && !selectedStreet && !selectedPnd) {
      drawCityView(selectedCity);
      return;
    }
    if (!selectedCity && !selectedStreet && !selectedPnd) {
      if (selectedCityGeo) {
        drawCityGeoView(selectedCityGeo);
        return;
      }
      if (selectedCountry) drawAllCities(selectedCountry.country);
      else drawAllCountries();
    }
  }, [
    mapReady,
    selectedCountry,
    selectedCityGeo,
    selectedCity,
    selectedStreet,
    selectedPnd,
    drawAllCountries,
    drawAllCities,
    drawCityGeoView,
    drawCityView,
  ]);

  const prevStreetGateRef = useRef(null);
  useEffect(() => {
    if (!mapReady || !selectedStreet || selectedPnd) return;
    const gateKey = selectedGate
      ? (selectedGate.gate_id ?? selectedGate.gate ?? selectedGate.gate_name)
      : null;
    const combinedKey = `${selectedStreet.street_cd}::${gateKey ?? ""}`;
    if (prevStreetGateRef.current === combinedKey) return;
    prevStreetGateRef.current = combinedKey;
    if (selectedGate) drawGateView(selectedStreet, selectedGate);
    else drawStreetView(selectedStreet);
  }, [mapReady, selectedStreet, selectedGate, selectedPnd, drawStreetView, drawGateView]);

  useEffect(() => {
    if (!mapReady || !selectedPnd) return;
    const hasMarkers = Object.keys(markerMapRef.current).length > 0;
    if (hasMarkers) {
      setMapLoading(true);
      highlightMarker(selectedPnd);
      const t = setTimeout(() => setMapLoading(false), 600);
      return () => clearTimeout(t);
    } else {
      drawSinglePnd(selectedPnd);
    }
  }, [mapReady, selectedPnd, highlightMarker, drawSinglePnd]);

  useEffect(() => {
    return () => {
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
    };
  }, []);

  useEffect(() => {
    if (mapReady) {
      prevStreetGateRef.current = null;
    }
  }, [mapReady]);

  return (
    <div style={{ position: "relative", width: "100%", height: "100%" }}>
      <div ref={mapRef} className="w-full h-full" />
      <MapShimmer visible={mapLoading} isDarkMode={isDarkMode} mode={shimmerMode} />
    </div>
  );
};

// ============================================
// OverviewCards – unified card grid for all panels
// ============================================
const OverviewCards = ({ cards }) => {
  if (!cards || !cards.length) return null;

  // Soft pastel fill cards with subtle colored border, colored label, bold dark value
  const OVERVIEW_CARD_COLORS = {
    blue: { bg: "bg-blue-500/[0.04] border-blue-500/10", text: "text-blue-400 dark:text-blue-300" },
    emerald: {
      bg: "bg-emerald-500/[0.04] border-emerald-500/10",
      text: "text-emerald-400 dark:text-emerald-300",
    },
    amber: {
      bg: "bg-amber-500/[0.04] border-amber-500/10",
      text: "text-amber-400 dark:text-amber-300",
    },
    cyan: { bg: "bg-cyan-500/[0.04] border-cyan-500/10", text: "text-cyan-400 dark:text-cyan-300" },
    violet: {
      bg: "bg-violet-500/[0.04] border-violet-500/10",
      text: "text-violet-400 dark:text-violet-300",
    },
    green: {
      bg: "bg-green-500/[0.04] border-green-500/10",
      text: "text-green-400 dark:text-green-300",
    },
    orange: {
      bg: "bg-orange-500/[0.04] border-orange-500/10",
      text: "text-orange-400 dark:text-orange-300",
    },
    pink: { bg: "bg-pink-500/[0.04] border-pink-500/10", text: "text-pink-400 dark:text-pink-300" },
    red: { bg: "bg-red-500/[0.04] border-red-500/10", text: "text-red-400 dark:text-red-300" },
    teal: { bg: "bg-teal-500/[0.04] border-teal-500/10", text: "text-teal-400 dark:text-teal-300" },
    indigo: {
      bg: "bg-indigo-500/[0.04] border-indigo-500/10",
      text: "text-indigo-400 dark:text-indigo-300",
    },
    rose: { bg: "bg-rose-500/[0.04] border-rose-500/10", text: "text-rose-400 dark:text-rose-300" },
    sky: { bg: "bg-sky-500/[0.04] border-sky-500/10", text: "text-sky-400 dark:text-sky-300" },
    yellow: {
      bg: "bg-yellow-500/[0.04] border-yellow-500/10",
      text: "text-yellow-400 dark:text-yellow-300",
    },
    fuchsia: {
      bg: "bg-fuchsia-500/[0.04] border-fuchsia-500/10",
      text: "text-fuchsia-400 dark:text-fuchsia-300",
    },
    lime: { bg: "bg-lime-500/[0.04] border-lime-500/10", text: "text-lime-400 dark:text-lime-300" },
  };

  return (
    <div className="grid grid-cols-2 gap-2.5">
      {cards.map(({ label, value, color = "blue" }) => {
        const c = OVERVIEW_CARD_COLORS[color] || OVERVIEW_CARD_COLORS.blue;
        return (
          <div key={label} className={`rounded-xl border p-3.5 shadow-sm ${c.bg}`}>
            <p className={`text-[9px] uppercase tracking-widest mb-1.5 font-semibold ${c.text}`}>
              {label}
            </p>
            <p className="text-[20px] font-bold leading-none text-foreground">{value}</p>
          </div>
        );
      })}
    </div>
  );
};

// ============================================
// CityStreetSidebar (unchanged)
// ============================================
const CityStreetSidebar = ({
  rawData = [],
  citiesData = [],
  overview = null,
  selectedRegion,
  selectedCountry,
  selectedCityGeo,
  selectedCity,
  selectedStreet,
  selectedGate,
  selectedPnd,
  onSelectCountry,
  onSelectRegion,
  onSelectCityGeo,
  onSelectCity,
  onSelectStreet,
  onSelectPnd,
  onSelectGate,
  activeFilters,
  onFiltersChange,
  onToggle,
}) => {
  const [expanded, setExpanded] = useState({
    regions: new Set(),
    countries: new Set(),
    cities: new Set(),
    ops: new Set(),
    locations: new Set(),
    gates: new Set(),
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [filterPanelTop, setFilterPanelTop] = useState(12);
  const filterBtnRef = useRef(null);
  const treeScrollRef = useRef(null);
  const [generalGateHover, setGeneralGateHover] = useState(null);

  const hasActiveFilter = !!(activeFilters?.month && activeFilters?.year);

  useEffect(() => {
    if (!rawData.length) return;
    const regionKeys = rawData
      .filter((node) => Array.isArray(node.children) && node.children.length > 0)
      .map((node) => node.country);
    if (regionKeys.length > 0) {
      setExpanded((prev) => ({ ...prev, regions: new Set([...prev.regions, ...regionKeys]) }));
    }
  }, [rawData]);

  useEffect(() => {
    if (showFilters && filterBtnRef.current) {
      const rect = filterBtnRef.current.getBoundingClientRect();
      setFilterPanelTop(Math.min(Math.max(rect.top, 12), window.innerHeight - 402));
    }
  }, [showFilters]);

  const toggle = (level, key) => {
    setExpanded((prev) => {
      const newSet = new Set(prev[level]);
      newSet.has(key) ? newSet.delete(key) : newSet.add(key);
      return { ...prev, [level]: newSet };
    });
  };

  useEffect(() => {
    if (!rawData.length) return;
    if (!selectedCountry && !selectedCityGeo && !selectedCity && !selectedStreet && !selectedPnd)
      return;

    const targetCountryCode = selectedCountry?.country ?? null;
    let targetOpsCode = selectedCity?.city_cd ?? null;
    let targetCityCode = selectedCityGeo?.city_cd ?? null;
    let targetLocCode = null;
    if (selectedStreet?.street_cd) {
      const [opsPart, ...locParts] = String(selectedStreet.street_cd).split("_");
      targetLocCode = locParts.join("_") || null;
      if (!targetOpsCode) targetOpsCode = opsPart;
    }
    const gateRef =
      selectedGate ||
      (selectedPnd ? { gate_id: selectedPnd.gate_id, gate_name: selectedPnd.gate_name } : null);

    for (const region of rawData) {
      const countries = region.children || [];
      for (const country of countries) {
        const cities = country.cities || [];
        let matchedCity = targetCityCode
          ? cities.find((c) => String(c.city_cd) === String(targetCityCode))
          : null;
        let matchedOp = null;
        if (!matchedCity && targetOpsCode) {
          for (const c of cities) {
            const foundOp = (c.ops || []).find((o) => String(o.ops) === String(targetOpsCode));
            if (foundOp) {
              matchedCity = c;
              matchedOp = foundOp;
              break;
            }
          }
        } else if (matchedCity && targetOpsCode) {
          matchedOp =
            (matchedCity.ops || []).find((o) => String(o.ops) === String(targetOpsCode)) || null;
        }
        const countryIsTarget =
          targetCountryCode && String(country.country) === String(targetCountryCode);

        if (!matchedOp && !matchedCity && !countryIsTarget) continue;

        const regionKey = region.country;
        const countryKey = country.country;
        const cityKey = matchedCity ? `${countryKey}_${matchedCity.city_cd}` : null;
        const opsKey = matchedCity && matchedOp ? `${cityKey}_${matchedOp.ops}` : null;

        let locKey = null,
          gateKey = null,
          matchedGates = [],
          matchedGateIdx = -1;
        if (matchedOp && targetLocCode) {
          const loc = (matchedOp.locations || []).find(
            (l) => String(l.location) === String(targetLocCode)
          );
          if (loc) {
            locKey = `${opsKey}_${loc.location}`;
            if (gateRef) {
              matchedGates = loc.gates || [];
              matchedGateIdx = matchedGates.findIndex((g) =>
                isGeneralGate(gateRef)
                  ? isGeneralGate(g)
                  : String(g.gate_id ?? "") === String(gateRef.gate_id ?? gateRef.gate ?? "")
              );
              if (matchedGateIdx >= 0)
                gateKey = `${locKey}_${matchedGates[matchedGateIdx].gate_id}_${matchedGateIdx}`;
            }
          }
        }

        setExpanded((prev) => ({
          regions: new Set([...prev.regions, regionKey]),
          countries: new Set([...prev.countries, countryKey]),
          cities: cityKey ? new Set([...prev.cities, cityKey]) : prev.cities,
          ops: opsKey ? new Set([...prev.ops, opsKey]) : prev.ops,
          locations: locKey ? new Set([...prev.locations, locKey]) : prev.locations,
          gates: gateKey ? new Set([...prev.gates, gateKey]) : prev.gates,
        }));

        let scrollId = null;
        if (selectedPnd && matchedGateIdx >= 0) {
          const devices = matchedGates[matchedGateIdx].devices || [];
          const dIdx = devices.findIndex(
            (d) =>
              String(d.device_id) === String(selectedPnd.device_id) &&
              d.device_name === selectedPnd.device_name
          );
          scrollId =
            dIdx >= 0
              ? `sidebar-node-pnd-${devices[dIdx].device_id}_${devices[dIdx].device_name}_${dIdx}`
              : `sidebar-node-gate-${gateKey}`;
        } else if (gateKey) scrollId = `sidebar-node-gate-${gateKey}`;
        else if (locKey) scrollId = `sidebar-node-loc-${locKey}`;
        else if (opsKey) scrollId = `sidebar-node-ops-${opsKey}`;
        else if (cityKey) scrollId = `sidebar-node-city-${cityKey}`;
        else scrollId = `sidebar-node-country-${countryKey}`;

        if (scrollId) {
          setTimeout(() => {
            const container = treeScrollRef.current;
            const el = document.getElementById(scrollId);
            if (container && el) {
              const containerRect = container.getBoundingClientRect();
              const elRect = el.getBoundingClientRect();
              const delta =
                elRect.top - containerRect.top - container.clientHeight / 2 + elRect.height / 2;
              container.scrollTo({ top: container.scrollTop + delta, behavior: "smooth" });
            }
          }, 260);
        }
        return;
      }
    }
  }, [
    rawData,
    selectedCountry?.country,
    selectedCityGeo?.city_cd,
    selectedCity?.city_cd,
    selectedStreet?.street_cd,
    selectedGate?.gate_id ?? selectedGate?.gate ?? selectedGate?.gate_name,
    selectedPnd?.device_id,
  ]);

  const q = searchQuery.trim().toLowerCase();

  const deviceMatches = (device) =>
    !q ||
    device.device_name?.toLowerCase().includes(q) ||
    device.device_id?.toLowerCase().includes(q) ||
    device.device_type?.toLowerCase().includes(q);

  const gateMatches = (gate) =>
    !q ||
    gate.gate_name?.toLowerCase().includes(q) ||
    gate.gate_id?.toLowerCase().includes(q) ||
    (gate.devices || []).some(deviceMatches);

  const locationMatches = (loc) =>
    !q || loc.location?.toLowerCase().includes(q) || (loc.gates || []).some(gateMatches);

  const opsMatches = (op) =>
    !q ||
    op.ops_name?.toLowerCase().includes(q) ||
    op.ops?.toLowerCase().includes(q) ||
    (op.locations || []).some(locationMatches);

  const cityMatches = (city) =>
    !q ||
    city.city_name?.toLowerCase().includes(q) ||
    city.city_cd?.toLowerCase().includes(q) ||
    (city.ops || []).some(opsMatches);

  const countryMatches = (country) =>
    !q ||
    country.country_name?.toLowerCase().includes(q) ||
    country.country?.toLowerCase().includes(q) ||
    (country.cities || []).some(cityMatches);

  const regionMatches = (node) => {
    if (!q) return true;
    if (node.children?.length) {
      return (
        node.country_name?.toLowerCase().includes(q) ||
        node.country?.toLowerCase().includes(q) ||
        node.children.some(countryMatches)
      );
    }
    return countryMatches(node);
  };

  const filteredTop = rawData.filter(regionMatches);

  const flatCountries = useMemo(
    () =>
      rawData.flatMap((item) =>
        item.children?.length ? item.children : item.ops || item.cities ? [item] : []
      ),
    [rawData]
  );

  // Header stat tiles — asli (unfiltered) API overview totals dikhate hain,
  // sidebar tree ke permission-filtered data se nahi (jo 0 aa sakta hai).
  const totalOps =
    overview?.total_ops_count ?? flatCountries.reduce((s, c) => s + getCountryOps(c).length, 0);
  const totalLocations =
    overview?.total_locations_count ??
    flatCountries.reduce(
      (s, c) => s + getCountryOps(c).reduce((os, op) => os + (op.locations || []).length, 0),
      0
    );
  const totalGates = flatCountries.reduce(
    (s, c) =>
      s +
      getCountryOps(c).reduce(
        (os, op) => os + (op.locations || []).reduce((ls, loc) => ls + (loc.gates || []).length, 0),
        0
      ),
    0
  );
  const totalDevices =
    overview?.total_devices_count ??
    flatCountries.reduce(
      (s, c) =>
        s +
        getCountryOps(c).reduce(
          (os, op) =>
            os +
            (op.locations || []).reduce(
              (ls, loc) =>
                ls + (loc.gates || []).reduce((gs, gate) => gs + (gate.devices || []).length, 0),
              0
            ),
          0
        ),
      0
    );

  const highlight = (text) => {
    if (!q || !text) return String(text ?? "");
    const str = String(text);
    const idx = str.toLowerCase().indexOf(q);
    if (idx === -1) return str;
    return (
      <span>
        {str.slice(0, idx)}
        <mark
          style={{
            background: "#6366F133",
            color: "inherit",
            borderRadius: "2px",
            padding: "0 1px",
          }}
        >
          {str.slice(idx, idx + q.length)}
        </mark>
        {str.slice(idx + q.length)}
      </span>
    );
  };

  const handleDeviceClick = (device, gate, op, loc, countryObj) => {
    const flatCity = citiesData.find((c) => c.city_cd === op.ops);
    const flatStreet = flatCity?.streets?.find((s) => s.street_cd === `${op.ops}_${loc.location}`);
    const pnd = {
      pnd_number: device.device_id,
      device_id: device.device_id,
      device_type: device.device_type || null,
      device_name: device.device_name,
      gate_name: gate?.gate_name,
      gate_id: gate?.gate_id,
      latitude: device.latitude,
      longitude: device.longitude,
    };
    if (flatCity) onSelectCity(flatCity);
    if (flatStreet) onSelectStreet(flatStreet);
    onSelectPnd(pnd);
  };

  const handleGateClick = (gate, op, loc, countryObj) => {
    const flatCity = citiesData.find((c) => c.city_cd === op.ops);
    const flatStreet = flatCity?.streets?.find((s) => s.street_cd === `${op.ops}_${loc.location}`);
    const gateObj = {
      gate: gate?.gate_id || gate?.gate_name,
      gate_id: gate?.gate_id,
      gate_name: gate?.gate_name,
      contribution_pct: null,
    };
    if (flatCity) onSelectCity(flatCity);
    if (flatStreet) onSelectStreet(flatStreet);
    onSelectPnd?.(null);
    onSelectGate?.(gateObj);
  };

  const handleLocationClick = (op, loc) => {
    const flatCity = citiesData.find((c) => c.city_cd === op.ops);
    const flatStreet = flatCity?.streets?.find((s) => s.street_cd === `${op.ops}_${loc.location}`);
    if (flatCity) onSelectCity(flatCity);
    if (flatStreet) {
      onSelectStreet(flatStreet);
      onSelectPnd(null);
    }
  };

  const handleOpsClick = (op) => {
    const flatCity = citiesData.find((c) => c.city_cd === op.ops);
    if (flatCity) {
      onSelectCity(flatCity);
      onSelectStreet(null);
      onSelectPnd(null);
    }
  };

  const handleCityGeoClick = (city, countryObj) => {
    onSelectCityGeo?.({
      ...city,
      country_code: countryObj?.country,
      country_name: countryObj?.country_name,
    });
  };

  if (!rawData.length) {
    return (
      <div className="h-full flex items-center justify-center bg-sidebar p-4">
        <p className="text-sm text-muted-foreground">No data available</p>
      </div>
    );
  }

  const renderCountry = (countryObj) => {
    const countryKey = countryObj.country;
    const countryOpen = expanded.countries.has(countryKey);
    const filteredCities = (countryObj.cities || []).filter(cityMatches);

    const countryDeviceCount =
      countryObj.total_devices ??
      getCountryOps(countryObj).reduce(
        (s, op) =>
          s +
          (op.locations || []).reduce(
            (ls, loc) =>
              ls + (loc.gates || []).reduce((gs, gate) => gs + (gate.devices || []).length, 0),
            0
          ),
        0
      );
    const countryOpsCount = countryObj.total_ops ?? getCountryOps(countryObj).length;

    return (
      <div key={countryKey} id={`sidebar-node-country-${countryKey}`}>
        <button
          onClick={() => {
            if (countryObj._accessible === false) return;
            const willOpen = !countryOpen;
            toggle("countries", countryKey);
            if (willOpen) onSelectCountry?.(countryObj);
          }}
          className={`w-full flex items-center gap-2 px-3 py-2.5 hover:bg-sidebar-accent/50 transition-colors text-left border-b border-sidebar-border/50 ${
            countryObj._accessible === false ? "opacity-50" : ""
          }`}
        >
          {countryOpen ? (
            <ChevronDown className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
          ) : (
            <ChevronRight className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
          )}
          <Globe className="w-3.5 h-3.5 text-primary flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-xs font-bold text-sidebar-foreground truncate">
              {highlight(countryObj.country_name)}
            </p>
            <p className="text-[9px] text-muted-foreground">
              {countryOpsCount} ops · {countryDeviceCount} devices
            </p>
          </div>
          <span className="text-[9px] font-mono text-muted-foreground flex-shrink-0 bg-secondary px-1.5 py-0.5 rounded">
            +{countryObj.country}
          </span>
        </button>

        <AnimatePresence initial={false}>
          {countryOpen && (
            <motion.div
              key="country-body"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.18 }}
              className="overflow-hidden"
            >
              {filteredCities.flatMap((cityObj) => {
                const cityKey = `${countryKey}_${cityObj.city_cd}`;
                const filteredOps = (cityObj.ops || []).filter(opsMatches);

                return filteredOps.map((op) => {
                  const opsKey = `${cityKey}_${op.ops}`;
                  const opsOpen = expanded.ops.has(opsKey);
                  const isSelectedOps = selectedCity?.city_cd === op.ops;
                  const filteredLocs = (op.locations || []).filter(locationMatches);

                  const opsDeviceCount = (op.locations || []).reduce(
                    (ls, loc) =>
                      ls +
                      (loc.gates || []).reduce((gs, gate) => gs + (gate.devices || []).length, 0),
                    0
                  );

                  return (
                    <div
                      key={opsKey}
                      id={`sidebar-node-ops-${opsKey}`}
                      className="border-b border-sidebar-border/30"
                    >
                      <button
                        onClick={() => {
                          const willOpen = !opsOpen;
                          toggle("ops", opsKey);
                          if (willOpen) handleOpsClick(op);
                        }}
                        className={`w-full flex items-center gap-2 pl-9 pr-3 py-2 transition-colors text-left ${
                          isSelectedOps
                            ? "bg-primary/10 border-l-2 border-primary"
                            : "hover:bg-sidebar-accent/40"
                        }`}
                      >
                        {opsOpen ? (
                          <ChevronDown className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                        ) : (
                          <ChevronRight className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                        )}
                        <Building2 className="w-3 h-3 text-blue-400 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-[11px] font-semibold text-sidebar-foreground truncate">
                            {highlight(op.ops_name)}
                          </p>
                          <p className="text-[9px] text-muted-foreground">
                            {filteredLocs.length} locations · {opsDeviceCount} devices
                          </p>
                        </div>
                        <span className="text-[8px] font-mono text-muted-foreground bg-secondary px-1.5 py-0.5 rounded flex-shrink-0">
                          {op.ops}
                        </span>
                      </button>

                      <AnimatePresence initial={false}>
                        {opsOpen && (
                          <motion.div
                            key="ops-body"
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.16 }}
                            className="overflow-hidden"
                          >
                            {filteredLocs.map((loc) => {
                              const locKey = `${opsKey}_${loc.location}`;
                              const locOpen = expanded.locations.has(locKey);
                              const isSelectedStreet =
                                selectedStreet?.street_cd === `${op.ops}_${loc.location}`;
                              const filteredGates = (loc.gates || []).filter(gateMatches);
                              const locDeviceCount = (loc.gates || []).reduce(
                                (gs, gate) => gs + (gate.devices || []).length,
                                0
                              );
                              const locOnlyGeneralGate =
                                filteredGates.length <= 1 && filteredGates.every(isGeneralGate);

                              return (
                                <div key={locKey} id={`sidebar-node-loc-${locKey}`}>
                                  <button
                                    onClick={() => {
                                      const willOpen = !locOpen;
                                      toggle("locations", locKey);
                                      if (willOpen) handleLocationClick(op, loc);
                                    }}
                                    className={`w-full flex items-center gap-2 pl-12 pr-3 py-1.5 transition-colors text-left ${
                                      isSelectedStreet
                                        ? "bg-primary/8 border-l-2 border-primary/60"
                                        : "hover:bg-sidebar-accent/25"
                                    }`}
                                  >
                                    {locOpen ? (
                                      <ChevronDown className="w-2.5 h-2.5 text-muted-foreground flex-shrink-0" />
                                    ) : (
                                      <ChevronRight className="w-2.5 h-2.5 text-muted-foreground flex-shrink-0" />
                                    )}
                                    <MapPin className="w-2.5 h-2.5 text-emerald-400 flex-shrink-0" />
                                    <div className="flex-1 min-w-0">
                                      <p className="text-[10px] font-semibold text-primary truncate">
                                        {highlight(loc.location)}
                                      </p>
                                      <p className="text-[8px] text-muted-foreground">
                                        {locOnlyGeneralGate
                                          ? `${locDeviceCount} devices`
                                          : `${filteredGates.length} gates · ${locDeviceCount} devices`}
                                      </p>
                                    </div>
                                  </button>

                                  <AnimatePresence initial={false}>
                                    {locOpen && (
                                      <motion.div
                                        key="loc-body"
                                        initial={{ height: 0, opacity: 0 }}
                                        animate={{ height: "auto", opacity: 1 }}
                                        exit={{ height: 0, opacity: 0 }}
                                        transition={{ duration: 0.12 }}
                                        className="overflow-hidden"
                                      >
                                        {(() => {
                                          const onlyGeneralGate =
                                            filteredGates.length <= 1 &&
                                            filteredGates.every(isGeneralGate);

                                          if (onlyGeneralGate) {
                                            const allDevices = filteredGates.flatMap((gate) =>
                                              (gate.devices || [])
                                                .filter(deviceMatches)
                                                .map((device) => ({ device, gate }))
                                            );
                                            return (
                                              <div className="pl-16 pr-2 py-1 space-y-0.5">
                                                {allDevices.map(({ device, gate }, di) => {
                                                  const dColor = getDeviceColor(device.device_type);
                                                  const dType = getDeviceTypeName(
                                                    device.device_type
                                                  );
                                                  const hasCoords =
                                                    device.latitude && device.longitude;
                                                  const isPndSelected =
                                                    selectedPnd?.device_id === device.device_id &&
                                                    selectedPnd?.device_name === device.device_name;

                                                  return (
                                                    <button
                                                      key={`${device.device_id}_${device.device_name}_${di}`}
                                                      id={`sidebar-node-pnd-${device.device_id}_${device.device_name}_${di}`}
                                                      onClick={() =>
                                                        handleDeviceClick(
                                                          device,
                                                          gate,
                                                          op,
                                                          loc,
                                                          countryObj
                                                        )
                                                      }
                                                      className={`w-full flex items-center gap-1.5 px-2 py-1.5 rounded-md transition-all text-left ${
                                                        isPndSelected
                                                          ? "bg-primary/10 border border-primary/20"
                                                          : "hover:bg-sidebar-accent/40"
                                                      }`}
                                                    >
                                                      <CircleDot
                                                        className="w-2 h-2 flex-shrink-0"
                                                        style={{
                                                          color: hasCoords ? dColor : "#9CA3AF",
                                                        }}
                                                      />
                                                      <span
                                                        className="text-[7px] font-bold px-1 py-0.5 rounded flex-shrink-0 leading-none"
                                                        style={{
                                                          background: `${dColor}22`,
                                                          color: dColor,
                                                        }}
                                                      >
                                                        {dType}
                                                      </span>
                                                      <div className="flex-1 min-w-0">
                                                        <p className="text-[9px] font-mono font-medium text-sidebar-foreground truncate">
                                                          {highlight(device.device_id)}
                                                        </p>
                                                        <p className="text-[8px] text-muted-foreground truncate">
                                                          {highlight(device.device_name)}
                                                        </p>
                                                      </div>
                                                      {hasCoords && (
                                                        <Navigation className="w-2 h-2 text-primary flex-shrink-0" />
                                                      )}
                                                    </button>
                                                  );
                                                })}
                                              </div>
                                            );
                                          }

                                          return filteredGates.map((gate, gi) => {
                                            const gateKey = `${locKey}_${gate.gate_id}_${gi}`;
                                            const gateOpen = expanded.gates.has(gateKey);
                                            const filteredDevices = (gate.devices || []).filter(
                                              deviceMatches
                                            );

                                            return (
                                              <div
                                                key={gateKey}
                                                id={`sidebar-node-gate-${gateKey}`}
                                              >
                                                <button
                                                  onClick={() => {
                                                    const willOpen = !gateOpen;
                                                    toggle("gates", gateKey);
                                                    if (willOpen && !isGeneralGate(gate))
                                                      handleGateClick(gate, op, loc, countryObj);
                                                  }}
                                                  onMouseEnter={(e) => {
                                                    if (!isGeneralGate(gate)) return;
                                                    const rect =
                                                      e.currentTarget.getBoundingClientRect();
                                                    setGeneralGateHover({
                                                      devices: gate.devices || [],
                                                      top: rect.top,
                                                      left: rect.right,
                                                    });
                                                  }}
                                                  onMouseLeave={() => setGeneralGateHover(null)}
                                                  className={`w-full flex items-center gap-2 pl-16 pr-3 py-1.5 hover:bg-sidebar-accent/20 transition-colors text-left ${
                                                    selectedStreet?.street_cd ===
                                                      `${op.ops}_${loc.location}` &&
                                                    selectedGate?.gate ===
                                                      (gate.gate_id || gate.gate_name)
                                                      ? "bg-primary/10 border-y border-primary/20"
                                                      : ""
                                                  }`}
                                                >
                                                  {gateOpen ? (
                                                    <ChevronDown className="w-2.5 h-2.5 text-muted-foreground flex-shrink-0" />
                                                  ) : (
                                                    <ChevronRight className="w-2.5 h-2.5 text-muted-foreground flex-shrink-0" />
                                                  )}
                                                  <Layers className="w-2.5 h-2.5 text-violet-400 flex-shrink-0" />
                                                  <div className="flex-1 min-w-0">
                                                    <p className="text-[9px] font-semibold text-sidebar-foreground truncate">
                                                      {highlight(getGateDisplayName(gate))}
                                                    </p>
                                                    <p className="text-[8px] text-muted-foreground">
                                                      {(gate.devices || []).length} devices
                                                    </p>
                                                  </div>
                                                  {gate.gate_id && !isGeneralGate(gate) && (
                                                    <span className="text-[8px] font-mono text-muted-foreground flex-shrink-0 bg-secondary px-1.5 py-0.5 rounded">
                                                      {highlight(gate.gate_id)}
                                                    </span>
                                                  )}
                                                </button>

                                                <AnimatePresence initial={false}>
                                                  {gateOpen && (
                                                    <motion.div
                                                      key="gate-body"
                                                      initial={{ height: 0, opacity: 0 }}
                                                      animate={{ height: "auto", opacity: 1 }}
                                                      exit={{ height: 0, opacity: 0 }}
                                                      transition={{ duration: 0.1 }}
                                                      className="overflow-hidden"
                                                    >
                                                      <div className="pl-[72px] pr-2 py-1 space-y-0.5">
                                                        {filteredDevices.map((device, di) => {
                                                          const dColor = getDeviceColor(
                                                            device.device_type
                                                          );
                                                          const dType = getDeviceTypeName(
                                                            device.device_type
                                                          );
                                                          const hasCoords =
                                                            device.latitude && device.longitude;
                                                          const isPndSelected =
                                                            selectedPnd?.device_id ===
                                                              device.device_id &&
                                                            selectedPnd?.device_name ===
                                                              device.device_name;

                                                          return (
                                                            <button
                                                              key={`${device.device_id}_${device.device_name}_${di}`}
                                                              id={`sidebar-node-pnd-${device.device_id}_${device.device_name}_${di}`}
                                                              onClick={() =>
                                                                handleDeviceClick(
                                                                  device,
                                                                  gate,
                                                                  op,
                                                                  loc,
                                                                  countryObj
                                                                )
                                                              }
                                                              className={`w-full flex items-center gap-1.5 px-2 py-1.5 rounded-md transition-all text-left ${
                                                                isPndSelected
                                                                  ? "bg-primary/10 border border-primary/20"
                                                                  : "hover:bg-sidebar-accent/40"
                                                              }`}
                                                            >
                                                              <CircleDot
                                                                className="w-2 h-2 flex-shrink-0"
                                                                style={{
                                                                  color: hasCoords
                                                                    ? dColor
                                                                    : "#9CA3AF",
                                                                }}
                                                              />
                                                              <span
                                                                className="text-[7px] font-bold px-1 py-0.5 rounded flex-shrink-0 leading-none"
                                                                style={{
                                                                  background: `${dColor}22`,
                                                                  color: dColor,
                                                                }}
                                                              >
                                                                {dType}
                                                              </span>
                                                              <div className="flex-1 min-w-0">
                                                                <p className="text-[9px] font-mono font-medium text-sidebar-foreground truncate">
                                                                  {highlight(device.device_id)}
                                                                </p>
                                                                <p className="text-[8px] text-muted-foreground truncate">
                                                                  {highlight(device.device_name)}
                                                                </p>
                                                              </div>
                                                              {hasCoords && (
                                                                <Navigation className="w-2 h-2 text-primary flex-shrink-0" />
                                                              )}
                                                            </button>
                                                          );
                                                        })}
                                                      </div>
                                                    </motion.div>
                                                  )}
                                                </AnimatePresence>
                                              </div>
                                            );
                                          });
                                        })()}
                                      </motion.div>
                                    )}
                                  </AnimatePresence>
                                </div>
                              );
                            })}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                });
              })}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  return (
    <div className="h-full flex flex-col bg-sidebar overflow-hidden border-r border-sidebar-border">
      <div className="p-4 border-b border-sidebar-border">
        <div className="flex items-center gap-2.5 mb-3">
          <div className="w-8 h-8 rounded-lg bg-primary/20 flex items-center justify-center flex-shrink-0">
            <Globe className="w-4 h-4 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-sm font-bold text-sidebar-foreground tracking-tight">
              Off Street Explorer
            </h1>
            <p className="text-[10px] text-muted-foreground uppercase tracking-widest">
              Parking Hierarchy
            </p>
          </div>
          <button
            onClick={onToggle}
            className="p-1.5 rounded-lg border border-border text-muted-foreground hover:text-foreground hover:bg-secondary transition-all flex-shrink-0"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
        </div>

        <div className="grid grid-cols-3 gap-2">
          {[
            { label: "Ops", value: totalOps },
            { label: "Locations", value: totalLocations },
            { label: "Devices", value: totalDevices },
          ].map(({ label, value }) => (
            <div key={label} className="rounded-lg bg-card border border-border p-2">
              <p className="text-[9px] uppercase tracking-wider text-muted-foreground">{label}</p>
              <p className="text-base font-bold text-foreground">{value}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="px-3 py-2.5 border-b border-sidebar-border">
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
            <input
              type="text"
              placeholder="Search ops, location, device..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-8 pr-7 py-1.5 text-xs rounded-lg bg-background border border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary transition-all"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </div>
          <button
            ref={filterBtnRef}
            onClick={() => setShowFilters((v) => !v)}
            className={`relative flex-shrink-0 p-1.5 rounded-lg border transition-all ${
              hasActiveFilter
                ? "bg-primary/15 border-primary/40 text-primary"
                : "bg-background border-border text-muted-foreground hover:bg-secondary hover:text-foreground"
            }`}
          >
            <SlidersHorizontal className="w-3.5 h-3.5" />
            {hasActiveFilter && (
              <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-primary" />
            )}
          </button>
        </div>
        {hasActiveFilter && (
          <div className="flex items-center gap-1.5 mt-1.5 px-2 py-1 rounded-lg bg-primary/8 border border-primary/15">
            <Calendar className="w-3 h-3 text-primary flex-shrink-0" />
            <span className="text-[9px] text-primary font-semibold truncate">
              {activeFilters.month} {activeFilters.year}
              {activeFilters.threshold && ` • Threshold: ${activeFilters.threshold}`}
              {activeFilters.currency &&
                ` • ${activeFilters.currency === "reporting" ? "Reporting" : "Local"} Currency`}
            </span>
            <button
              onClick={() => onFiltersChange({})}
              className="ml-auto text-muted-foreground hover:text-foreground"
            >
              <X className="w-2.5 h-2.5" />
            </button>
          </div>
        )}
        {q && (
          <p className="text-[10px] text-muted-foreground mt-1 px-0.5">
            {filteredTop.length === 0
              ? "No results found"
              : `${filteredTop.length} result${filteredTop.length === 1 ? "" : "s"} matched`}
          </p>
        )}
      </div>

      <AnimatePresence>
        {showFilters && (
          <DateFilterPanel
            topPos={filterPanelTop}
            activeMonth={activeFilters?.month}
            activeYear={activeFilters?.year}
            activeThreshold={activeFilters?.threshold}
            activeCurrency={activeFilters?.currency}
            initialGroup={activeFilters?.type || ""}
            initialMonth={activeFilters?.displayMonth || activeFilters?.month || ""}
            initialDay={
              activeFilters?.month
                ? { value: activeFilters.month, label: activeFilters.month }
                : null
            }
            onApply={(f) => {
              onFiltersChange(f);
              setShowFilters(false);
            }}
            onReset={() => {
              onFiltersChange({});
              setShowFilters(false);
            }}
            onClose={() => setShowFilters(false)}
          />
        )}
      </AnimatePresence>

      <div ref={treeScrollRef} className="flex-1 overflow-y-auto cb-scroll pb-10">
        {filteredTop.length === 0 && q ? (
          <div className="flex flex-col items-center justify-center py-10 gap-2 text-muted-foreground">
            <Search className="w-6 h-6 opacity-30" />
            <p className="text-xs">No results for "{searchQuery}"</p>
          </div>
        ) : (
          filteredTop.map((node) => {
            const isWrapper = Array.isArray(node.children);

            if (!isWrapper) return renderCountry(node);

            const regionKey = node.country;
            const regionOpen = expanded.regions.has(regionKey);
            const filteredChildren = (node.children || []).filter(countryMatches);

            const regionOpsCount = filteredChildren.reduce(
              (s, c) => s + getCountryOps(c).length,
              0
            );
            const regionDeviceCount = filteredChildren.reduce(
              (s, c) =>
                s +
                getCountryOps(c).reduce(
                  (os, op) =>
                    os +
                    (op.locations || []).reduce(
                      (ls, loc) =>
                        ls +
                        (loc.gates || []).reduce((gs, gate) => gs + (gate.devices || []).length, 0),
                      0
                    ),
                  0
                ),
              0
            );

            return (
              <div key={regionKey}>
                <button
                  onClick={() => {
                    if (node._accessible === false) return;
                    const willOpen = !regionOpen;
                    toggle("regions", regionKey);
                    if (willOpen) onSelectRegion?.(node);
                  }}
                  className={`w-full flex items-center gap-2 px-3 py-3 hover:bg-sidebar-accent/60 transition-colors text-left border-b border-sidebar-border bg-sidebar-accent/20 ${
                    node._accessible === false ? "opacity-50" : ""
                  }`}
                >
                  {regionOpen ? (
                    <ChevronDown className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                  ) : (
                    <ChevronRight className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                  )}
                  <Globe className="w-3.5 h-3.5 text-amber-400 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-bold text-sidebar-foreground truncate">
                      {highlight(node.country_name)}
                    </p>
                    <p className="text-[9px] text-muted-foreground">
                      {filteredChildren.length} countries · {regionOpsCount} ops ·{" "}
                      {regionDeviceCount} devices
                    </p>
                  </div>
                </button>

                <AnimatePresence initial={false}>
                  {regionOpen && (
                    <motion.div
                      key="region-body"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      {filteredChildren.map((child) => renderCountry(child))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })
        )}
      </div>

      {generalGateHover && (
        <div
          className="fixed z-[999] rounded-lg border border-border bg-card shadow-xl px-3 py-2.5 pointer-events-none"
          style={{
            top: Math.min(generalGateHover.top, window.innerHeight - 120),
            left: generalGateHover.left + 8,
            minWidth: 200,
            maxWidth: 260,
          }}
        >
          <p className="text-[10px] font-extrabold text-foreground mb-1">General</p>
          <p className="text-[9px] text-muted-foreground leading-relaxed">
            You can't view General's data directly. Open its hierarchy to see the data for its
            devices.
          </p>
        </div>
      )}
    </div>
  );
};

// ============================================
// useOffStreetMapData — new API hook
// ============================================
const useOffStreetMapData = (locationId, year, month, type, currency, extraParams = {}) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const { gate: gateId, device_id: deviceId } = extraParams || {};

  const fetch = useCallback(async () => {
    const isDeviceType = type === "device";
    if (isDeviceType) {
      if (!locationId || !gateId || !deviceId || !year || !month) return;
    } else {
      if (!locationId || !year || !month) return;
    }

    setLoading(true);
    setError(null);
    setData(null);
    try {
      const measure = getMeasureParam(currency);
      let url = `/off-street-map?location=${locationId}&year=${year}&month=${month}&type=${type}&measure=${encodeURIComponent(measure)}`;
      if ((type === "gate" || isDeviceType) && gateId) {
        url += `&gate=${encodeURIComponent(gateId)}`;
      }
      if (isDeviceType && deviceId) {
        url += `&device_id=${encodeURIComponent(deviceId)}`;
      }
      const res = await apiCall("get", url, null, null, false, false);
      if (res?.success) setData(res);
      else setError(res?.message || "Failed to load data");
    } catch (e) {
      setError(e?.message || "Failed to load data");
    } finally {
      setLoading(false);
    }
  }, [locationId, year, month, type, currency, gateId, deviceId]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  return { data, loading, error, refetch: fetch };
};

// ============================================
// DeviceTypeBreakdownCards
// ============================================
const DeviceTypeBreakdownCards = ({ devices = [] }) => {
  const counts = useMemo(() => {
    const c = {};
    devices.forEach((d) => {
      const t = d.device_type ? String(d.device_type).toUpperCase() : "UNASSIGNED";
      c[t] = (c[t] || 0) + 1;
    });
    return c;
  }, [devices]);

  const availableTypes = Object.keys(counts).sort((a, b) => counts[b] - counts[a]);

  if (!availableTypes.length) {
    return <p className="text-xs text-muted-foreground text-center py-4">No devices found</p>;
  }

  return (
    <div className="grid grid-cols-3 gap-2">
      {availableTypes.map((type) => {
        const color = getDeviceColor(type === "UNASSIGNED" ? null : type);
        const Icon = getDeviceTypeIcon(type === "UNASSIGNED" ? null : type);
        return (
          <div
            key={type}
            title={type}
            className="group relative rounded-xl border p-2.5 flex flex-col items-center justify-center gap-1 cursor-default overflow-hidden"
            style={{ borderColor: `${color}40`, background: `${color}0d` }}
          >
            <Icon className="w-4 h-4" style={{ color }} />
            <p className="text-lg font-bold leading-none text-foreground">{counts[type]}</p>

            <div
              className="absolute inset-x-0 bottom-0 flex items-center justify-center py-1
                         bg-popover/95 border-t opacity-0 translate-y-1
                         group-hover:opacity-100 group-hover:translate-y-0
                         transition-all duration-150 z-10"
              style={{ borderColor: `${color}40` }}
            >
              <span
                className="text-[8px] font-bold uppercase tracking-widest px-1 text-center"
                style={{ color }}
              >
                {type}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const DeviceTypeBreakdownSection = ({ devices, openSection, handleToggleSection }) => (
  <AccordionSection
    id="deviceTypeBreakdown"
    openId={openSection}
    onToggle={handleToggleSection}
    icon={<CircleDot className="w-3.5 h-3.5" />}
    title="Device Type Breakdown"
    badge={`Total: ${devices.length}`}
  >
    <DeviceTypeBreakdownCards devices={devices} />
  </AccordionSection>
);

// ============================================
// HierarchyOverviewCards (deprecated, use OverviewCards)
// ============================================
const HierarchyOverviewCards = ({ cards }) => {
  // Just a wrapper to use OverviewCards for consistency
  return <OverviewCards cards={cards} />;
};

// ============================================
// PaymentBreakdownTable — PAID / FREE / RENT A CAR
// ============================================
const PaymentBreakdownTable = ({ paymentBreakdown }) => {
  const [openGroups, setOpenGroups] = useState({});
  if (!paymentBreakdown) return null;

  const STATUS_ORDER = ["PAID", "FREE", "RENT A CAR"];
  const STATUS_COLORS = { PAID: "#10B981", FREE: "#6366F1", "RENT A CAR": "#F59E0B" };
  const STATUS_DISPLAY = { PAID: "PAID", FREE: "MEMBER", "RENT A CAR": "RENT A CAR" };

  const fmt = (v) => (v === null || v === undefined ? "0" : v);

  const toggle = (key) => setOpenGroups((p) => ({ ...p, [key]: !p[key] }));

  const COLS = "grid-cols-[2fr_1fr_1fr_0.7fr_0.7fr_0.6fr]";

  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden">
      <div
        className={`grid ${COLS} gap-1 px-3.5 py-1.5 bg-secondary/60 border-b border-border sticky top-0 z-10`}
      >
        {["Method", "Revenue", "Tickets", "ALS", "ATV", "Contrib"].map((h) => (
          <p
            key={h}
            className="text-[8px] uppercase tracking-widest text-muted-foreground font-semibold"
          >
            {h}
          </p>
        ))}
      </div>

      <div className="max-h-72 overflow-y-auto cb-scroll">
        {STATUS_ORDER.map((status) => {
          const grp = paymentBreakdown[status];
          if (!grp) return null;
          const color = STATUS_COLORS[status] || "#6366F1";
          const isOpen = !!openGroups[status];
          const subRows = Object.entries(
            Array.isArray(grp.breakdown) ? {} : grp.breakdown || {}
          ).sort((a, b) => {
            const aVal = parseFloat(a[1]?.contribution_pct) || 0;
            const bVal = parseFloat(b[1]?.contribution_pct) || 0;
            return bVal - aVal;
          });

          return (
            <div key={status} className="border-b border-border last:border-b-0">
              <button
                onClick={() => subRows.length > 0 && toggle(status)}
                className={`w-full grid ${COLS} gap-1 items-center px-3.5 py-2.5 transition-colors text-left hover:bg-secondary/40`}
                style={{ background: `${color}18` }}
              >
                <div className="flex items-center gap-1.5 min-w-0">
                  <div
                    className="w-2 h-2 rounded-full flex-shrink-0"
                    style={{ background: color }}
                  />
                  <span className="text-[11px] font-extrabold text-foreground truncate tracking-wide">
                    {STATUS_DISPLAY[status] || status}
                  </span>

                  {subRows.length > 0 && (
                    <ChevronDown
                      className={`w-3 h-3 flex-shrink-0 transition-transform duration-150 ${isOpen ? "rotate-180" : ""}`}
                      style={{ color }}
                    />
                  )}
                </div>
                <span className="text-[11px] font-extrabold tabular-nums text-foreground">
                  {fmt(grp.revenue)}
                </span>
                <span className="text-[11px] font-extrabold tabular-nums text-foreground">
                  {fmt(grp.ticket_count)}
                </span>
                <span className="text-[11px] font-extrabold tabular-nums text-foreground">
                  {fmt(grp.als)}
                </span>
                <span className="text-[11px] font-extrabold tabular-nums text-foreground">
                  {fmt(grp.atv)}
                </span>
                <span className="text-[9px] font-extrabold tabular-nums text-muted-foreground">
                  {grp.contribution_pct || "—"}
                </span>
              </button>

              <AnimatePresence initial={false}>
                {isOpen && subRows.length > 0 && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.18, ease: "easeInOut" }}
                    className="overflow-hidden"
                  >
                    {subRows.map(([method, val]) => {
                      const isParent = !method.includes("::");
                      const isParentOpen = !!openGroups[`${status}_${method}`];

                      const children = isParent
                        ? subRows.filter(([m]) => m.startsWith(method + " ::"))
                        : [];

                      const isChild = method.includes("::");
                      if (isChild) return null;

                      return (
                        <div key={method}>
                          <div
                            onClick={() =>
                              children.length > 0 &&
                              setOpenGroups((p) => ({
                                ...p,
                                [`${status}_${method}`]: !p[`${status}_${method}`],
                              }))
                            }
                            className={`grid ${COLS} gap-1 items-center pl-6 pr-3.5 py-1.5 border-t border-border/40 transition-colors ${children.length > 0 ? "cursor-pointer hover:bg-secondary/30" : ""}`}
                          >
                            <div className="flex items-center gap-1.5 min-w-0">
                              {children.length > 0 && (
                                <ChevronDown
                                  className={`w-3 h-3 flex-shrink-0 transition-transform duration-150 ${isParentOpen ? "rotate-180" : ""}`}
                                  style={{ color }}
                                />
                              )}
                              <p className="text-[10px] font-bold text-foreground truncate">
                                {method}
                              </p>
                            </div>
                            <p className="text-[10px] font-bold tabular-nums text-foreground">
                              {fmt(val?.revenue)}
                            </p>
                            <p className="text-[10px] font-bold tabular-nums text-foreground">
                              {fmt(val?.ticket_count)}
                            </p>
                            <p className="text-[10px] font-bold tabular-nums text-foreground">
                              {fmt(val?.als)}
                            </p>
                            <p className="text-[10px] font-bold tabular-nums text-foreground">
                              {fmt(val?.atv)}
                            </p>
                            <p className="text-[10px] font-bold tabular-nums text-muted-foreground">
                              {fmt(val?.contribution_pct)}
                            </p>
                          </div>

                          <AnimatePresence initial={false}>
                            {isParentOpen && children.length > 0 && (
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: "auto", opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.15, ease: "easeInOut" }}
                                className="overflow-hidden"
                              >
                                {children.map(([childMethod, childVal]) => (
                                  <div
                                    key={childMethod}
                                    className={`grid ${COLS} gap-1 items-center pl-10 pr-3.5 py-1.5 border-t border-border/30 hover:bg-secondary/20 transition-colors`}
                                  >
                                    <p className="text-[9px] text-muted-foreground truncate">
                                      {childMethod}
                                    </p>
                                    <p className="text-[9px] tabular-nums text-muted-foreground">
                                      {fmt(childVal?.revenue)}
                                    </p>
                                    <p className="text-[9px] tabular-nums text-muted-foreground">
                                      {fmt(childVal?.ticket_count)}
                                    </p>
                                    <p className="text-[9px] tabular-nums text-muted-foreground">
                                      {fmt(childVal?.als)}
                                    </p>
                                    <p className="text-[9px] tabular-nums text-muted-foreground">
                                      {fmt(childVal?.atv)}
                                    </p>
                                    <p className="text-[9px] tabular-nums text-muted-foreground">
                                      {fmt(childVal?.contribution_pct)}
                                    </p>
                                  </div>
                                ))}
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      );
                    })}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>

      {paymentBreakdown.grand_total && (
        <div
          className={`grid ${COLS} gap-1 items-center px-3.5 py-2.5 bg-secondary border-t-2 border-border sticky bottom-0`}
        >
          <p className="text-[10px] font-black text-foreground uppercase tracking-wide">
            Grand Total
          </p>
          <p className="text-[10px] font-black tabular-nums text-foreground">
            {fmt(paymentBreakdown.grand_total.revenue)}
          </p>
          <p className="text-[10px] font-black tabular-nums text-foreground">
            {fmt(paymentBreakdown.grand_total.ticket_count)}
          </p>
          <p className="text-[10px] font-black tabular-nums text-foreground">
            {fmt(paymentBreakdown.grand_total.als)}
          </p>
          <p className="text-[10px] font-black tabular-nums text-foreground">
            {fmt(paymentBreakdown.grand_total.atv)}
          </p>
          <p className="text-[10px] font-black tabular-nums text-foreground">
            {fmt(paymentBreakdown.grand_total.contribution_pct)}
          </p>
        </div>
      )}
    </div>
  );
};

// ============================================
// OpsBreakdownTable
// ============================================
const OpsBreakdownTable = ({ opsBreakdown, onSelectOps }) => {
  if (!opsBreakdown?.length)
    return <p className="text-xs text-muted-foreground text-center py-4">No ops data</p>;

  const fmt = (v) => (v === null || v === undefined ? "0" : v);

  const isCountryWrapped = opsBreakdown.some((item) => Array.isArray(item.ops));

  const allOps = [];
  if (isCountryWrapped) {
    opsBreakdown.forEach((country) => {
      if (country.ops && Array.isArray(country.ops)) {
        country.ops.forEach((op) => {
          allOps.push({
            ...op,
            country_name: country.country_name,
            country: country.country,
          });
        });
      }
    });
  } else {
    opsBreakdown.forEach((op) => allOps.push({ ...op }));
  }

  const sorted = [...allOps].sort(
    (a, b) => (parseFloat(b.contribution_pct) || 0) - (parseFloat(a.contribution_pct) || 0)
  );

  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden">
      <div className="grid grid-cols-[2fr_1fr_1fr_0.7fr_0.7fr_0.7fr] gap-1 px-3.5 py-1.5 bg-secondary/60 border-b border-border sticky top-0 z-10">
        <p className="text-[8px] uppercase tracking-widest text-muted-foreground font-semibold">
          Ops
        </p>
        <p className="text-[8px] uppercase tracking-widest text-muted-foreground font-semibold">
          Revenue
        </p>
        <p className="text-[8px] uppercase tracking-widest text-muted-foreground font-semibold">
          Tickets
        </p>
        <p className="text-[8px] uppercase tracking-widest text-muted-foreground font-semibold">
          ALS
        </p>
        <p className="text-[8px] uppercase tracking-widest text-muted-foreground font-semibold">
          ATV
        </p>
        <p className="text-[8px] uppercase tracking-widest text-muted-foreground font-semibold">
          Contrib
        </p>
      </div>
      <div className="divide-y divide-border max-h-72 overflow-y-auto cb-scroll">
        {sorted.map((ops, idx) => {
          const pct = parseFloat(ops.contribution_pct) || 0;
          return (
            <button
              key={`${ops.ops}_${idx}`}
              onClick={() => onSelectOps?.(ops)}
              className="w-full grid grid-cols-[2fr_1fr_1fr_0.7fr_0.7fr_0.7fr] gap-1 items-center px-3.5 py-2.5 hover:bg-secondary/50 transition-colors text-left"
            >
              <div className="flex items-center gap-1.5 min-w-0">
                <Building2 className="w-3 h-3 text-blue-400 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-[10px] font-extrabold text-foreground truncate">
                    {ops.ops_name || `Ops ${ops.ops}`}
                  </p>
                  <p className="text-[8px] font-mono text-muted-foreground">{ops.ops}</p>
                </div>
              </div>
              <p className="text-[10px] font-extrabold tabular-nums text-foreground">
                {fmt(ops.revenue)}
              </p>
              <p className="text-[10px] font-bold tabular-nums text-foreground">
                {fmt(ops.ticket_count)}
              </p>
              <p className="text-[10px] font-bold tabular-nums text-foreground">{fmt(ops.als)}</p>
              <p className="text-[10px] font-bold tabular-nums text-foreground">{fmt(ops.atv)}</p>
              <div className="space-y-0.5">
                <p className="text-[9px] font-extrabold tabular-nums text-primary">
                  {ops.contribution_pct || "0%"}
                </p>
                <div className="w-full h-0.5 bg-secondary rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary rounded-full"
                    style={{ width: `${Math.min(pct, 100)}%` }}
                  />
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

// ============================================
// LocationsBreakdownTable
// ============================================
const LocationsBreakdownTable = ({ opsBreakdown }) => {
  const isCountryWrapped = (opsBreakdown || []).some((item) => Array.isArray(item.ops));

  const allLocations = [];
  if (isCountryWrapped) {
    (opsBreakdown || []).forEach((country) => {
      if (country.ops && Array.isArray(country.ops)) {
        country.ops.forEach((ops) => {
          if (ops.locations && Array.isArray(ops.locations)) {
            ops.locations.forEach((loc) => {
              allLocations.push({
                ...loc,
                ops_name: ops.ops_name,
                ops_code: ops.ops,
                country_name: country.country_name,
                country: country.country,
              });
            });
          }
        });
      }
    });
  } else {
    (opsBreakdown || []).forEach((ops) => {
      if (ops.locations && Array.isArray(ops.locations)) {
        ops.locations.forEach((loc) => {
          allLocations.push({
            ...loc,
            ops_name: ops.ops_name,
            ops_code: ops.ops,
          });
        });
      }
    });
  }

  const sorted = [...allLocations].sort(
    (a, b) => (parseFloat(b.contribution_pct) || 0) - (parseFloat(a.contribution_pct) || 0)
  );

  if (!sorted.length)
    return <p className="text-xs text-muted-foreground text-center py-4">No location data</p>;

  const fmt = (v) => (v === null || v === undefined ? "0" : v);

  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden">
      <div className="grid grid-cols-[2fr_1fr_1fr_0.7fr_0.7fr_0.7fr] gap-1 px-3.5 py-1.5 bg-secondary/60 border-b border-border sticky top-0 z-10">
        <p className="text-[8px] uppercase tracking-widest text-muted-foreground font-semibold">
          Location
        </p>
        <p className="text-[8px] uppercase tracking-widest text-muted-foreground font-semibold">
          Revenue
        </p>
        <p className="text-[8px] uppercase tracking-widest text-muted-foreground font-semibold">
          Tickets
        </p>
        <p className="text-[8px] uppercase tracking-widest text-muted-foreground font-semibold">
          ALS
        </p>
        <p className="text-[8px] uppercase tracking-widest text-muted-foreground font-semibold">
          ATV
        </p>
        <p className="text-[8px] uppercase tracking-widest text-muted-foreground font-semibold">
          Contrib
        </p>
      </div>
      <div className="divide-y divide-border max-h-72 overflow-y-auto cb-scroll">
        {sorted.map((loc, i) => {
          const pct = parseFloat(loc.contribution_pct) || 0;
          return (
            <div
              key={`${loc.location}_${i}`}
              className="grid grid-cols-[2fr_1fr_1fr_0.7fr_0.7fr_0.7fr] gap-1 items-center px-3.5 py-2.5 hover:bg-secondary/40 transition-colors"
            >
              <div className="flex items-center gap-1.5 min-w-0">
                <MapPin className="w-2.5 h-2.5 text-emerald-400 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-[10px] font-extrabold text-foreground truncate">
                    {loc.location}
                  </p>
                  <p className="text-[8px] text-muted-foreground truncate">{loc.ops_name}</p>
                </div>
              </div>
              <p className="text-[10px] font-extrabold tabular-nums text-foreground">
                {fmt(loc.revenue)}
              </p>
              <p className="text-[10px] font-bold tabular-nums text-foreground">
                {fmt(loc.ticket_count)}
              </p>
              <p className="text-[10px] font-bold tabular-nums text-foreground">{fmt(loc.als)}</p>
              <p className="text-[10px] font-bold tabular-nums text-foreground">{fmt(loc.atv)}</p>
              <div className="space-y-0.5">
                <p className="text-[9px] font-extrabold tabular-nums text-primary">
                  {loc.contribution_pct || "0%"}
                </p>
                <div className="w-full h-0.5 bg-secondary rounded-full overflow-hidden">
                  <div
                    className="h-full bg-emerald-500 rounded-full"
                    style={{ width: `${Math.min(pct, 100)}%` }}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ============================================
// OpsLocationsTable
// ============================================
const OpsLocationsTable = ({ opsBreakdown, onSelectLocation }) => {
  if (!opsBreakdown?.length)
    return <p className="text-xs text-muted-foreground text-center py-4">No location data</p>;
  const fmt = (v) => (v === null || v === undefined ? "0" : v);

  const sorted = [...opsBreakdown].sort(
    (a, b) => (parseFloat(b.contribution_pct) || 0) - (parseFloat(a.contribution_pct) || 0)
  );

  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden">
      <div className="grid grid-cols-[1.5fr_1fr_1fr_0.7fr_0.7fr_0.7fr] gap-1 px-3.5 py-1.5 bg-secondary/60 border-b border-border sticky top-0 z-10">
        {["Location", "Revenue", "Tickets", "ALS", "ATV", "Contrib"].map((h) => (
          <p
            key={h}
            className="text-[8px] uppercase tracking-widest text-muted-foreground font-semibold"
          >
            {h}
          </p>
        ))}
      </div>
      <div className="divide-y divide-border max-h-72 overflow-y-auto cb-scroll">
        {sorted.map((loc, i) => {
          const pct = parseFloat(loc.contribution_pct) || 0;
          return (
            <button
              key={`${loc.location}_${i}`}
              onClick={() => onSelectLocation?.(loc)}
              className="w-full grid grid-cols-[1.5fr_1fr_1fr_0.7fr_0.7fr_0.7fr] gap-1 items-center px-3.5 py-2.5 hover:bg-secondary/50 transition-colors text-left"
            >
              <div className="flex items-center gap-1.5 min-w-0">
                <MapPin className="w-2.5 h-2.5 text-emerald-400 flex-shrink-0" />
                <p className="text-[10px] font-extrabold text-foreground truncate">
                  {loc.location}
                </p>
              </div>
              <p className="text-[10px] font-extrabold tabular-nums text-foreground">
                {fmt(loc.revenue)}
              </p>
              <p className="text-[10px] font-bold tabular-nums text-foreground">
                {fmt(loc.ticket_count)}
              </p>
              <p className="text-[10px] font-bold tabular-nums text-foreground">{fmt(loc.als)}</p>
              <p className="text-[10px] font-bold tabular-nums text-foreground">{fmt(loc.atv)}</p>
              <div className="space-y-0.5">
                <p className="text-[9px] font-extrabold tabular-nums text-primary">
                  {loc.contribution_pct || "0%"}
                </p>
                <div className="w-full h-0.5 bg-secondary rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary rounded-full"
                    style={{ width: `${Math.min(pct, 100)}%` }}
                  />
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

// ============================================
// RegionCountriesTable
// ============================================
const RegionCountriesTable = ({ countries, onSelectCountryItem }) => {
  if (!countries?.length)
    return <p className="text-xs text-muted-foreground text-center py-4">No country data</p>;

  const hasRevenue = countries.some((c) => c.revenue !== null && c.revenue !== undefined);
  const fmt = (v) => (v === null || v === undefined ? "0" : v);

  const sorted = hasRevenue
    ? [...countries].sort(
        (a, b) => (parseFloat(b.contribution_pct) || 0) - (parseFloat(a.contribution_pct) || 0)
      )
    : countries;

  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden">
      <div
        className={`grid ${hasRevenue ? "grid-cols-[2fr_1fr_1fr_0.7fr_0.7fr_0.7fr]" : "grid-cols-[2fr_1fr_1fr]"} gap-1 px-3.5 py-1.5 bg-secondary/60 border-b border-border sticky top-0 z-10`}
      >
        {(hasRevenue
          ? ["Country", "Revenue", "Tickets", "ALS", "ATV", "Contrib"]
          : ["Country", "Ops", "Devices"]
        ).map((h) => (
          <p
            key={h}
            className="text-[8px] uppercase tracking-widest text-muted-foreground font-semibold"
          >
            {h}
          </p>
        ))}
      </div>
      <div className="divide-y divide-border max-h-72 overflow-y-auto cb-scroll">
        {sorted.map((c, idx) => {
          const pct = parseFloat(c.contribution_pct) || 0;
          return (
            <button
              key={`${c.country}_${idx}`}
              onClick={() => onSelectCountryItem?.(c)}
              className={`w-full grid ${hasRevenue ? "grid-cols-[2fr_1fr_1fr_0.7fr_0.7fr_0.7fr]" : "grid-cols-[2fr_1fr_1fr]"} gap-1 items-center px-3.5 py-2.5 hover:bg-secondary/50 transition-colors text-left`}
            >
              <div className="flex items-center gap-1.5 min-w-0">
                <Globe className="w-3 h-3 text-amber-400 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-[10px] font-extrabold text-foreground truncate">
                    {c.country_name || `Country ${c.country}`}
                  </p>
                  <p className="text-[8px] font-mono text-muted-foreground">{c.country}</p>
                </div>
              </div>
              {hasRevenue ? (
                <>
                  <p className="text-[10px] font-extrabold tabular-nums text-foreground">
                    {fmt(c.revenue)}
                  </p>
                  <p className="text-[10px] font-bold tabular-nums text-foreground">
                    {fmt(c.ticket_count)}
                  </p>
                  <p className="text-[10px] font-bold tabular-nums text-foreground">{fmt(c.als)}</p>
                  <p className="text-[10px] font-bold tabular-nums text-foreground">{fmt(c.atv)}</p>
                  <div className="space-y-0.5">
                    <p className="text-[9px] font-extrabold tabular-nums text-primary">
                      {c.contribution_pct || "0%"}
                    </p>
                    <div className="w-full h-0.5 bg-secondary rounded-full overflow-hidden">
                      <div
                        className="h-full bg-primary rounded-full"
                        style={{ width: `${Math.min(pct, 100)}%` }}
                      />
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <p className="text-[10px] font-bold tabular-nums text-foreground">
                    {fmt(c.ops_count)}
                  </p>
                  <p className="text-[10px] font-bold tabular-nums text-foreground">
                    {fmt(c.device_count)}
                  </p>
                </>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

// ============================================
// CityBreakdownList
// ============================================
const CityBreakdownList = ({ cities, onSelectCity }) => {
  if (!cities?.length)
    return <p className="text-xs text-muted-foreground text-center py-4">No city data</p>;

  const sorted = [...cities].sort(
    (a, b) => (parseFloat(b.total_amount) || 0) - (parseFloat(a.total_amount) || 0)
  );

  const totalAmount = cities.reduce((s, c) => s + (Number(c.total_amount) || 0), 0);

  const fmt = (v) => (v === null || v === undefined ? "0" : v);

  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden">
      <div className="grid grid-cols-[1.3fr_0.5fr_0.9fr_0.9fr_0.7fr] gap-1 px-3.5 py-1.5 bg-secondary/60 border-b border-border sticky top-0 z-10">
        {["City", "Ops", "Total Amount", "Notes/Coins", "Contrib"].map((h) => (
          <p
            key={h}
            className="text-[8px] uppercase tracking-widest text-muted-foreground font-semibold"
          >
            {h}
          </p>
        ))}
      </div>
      <div className="divide-y divide-border max-h-72 overflow-y-auto cb-scroll">
        {sorted.map((city, idx) => {
          const cityAmount = Number(city.total_amount) || 0;
          const pct = totalAmount > 0 ? (cityAmount / totalAmount) * 100 : 0;
          return (
            <button
              key={`${city.city_cd}_${idx}`}
              onClick={() => onSelectCity?.(city.raw || city)}
              className="w-full grid grid-cols-[1.3fr_0.5fr_0.9fr_0.9fr_0.7fr] gap-1 items-center px-3.5 py-2.5 hover:bg-secondary/50 transition-colors text-left"
            >
              <div className="flex items-center gap-1.5 min-w-0">
                <MapPin className="w-3 h-3 text-emerald-400 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-[10px] font-extrabold text-foreground truncate">
                    {city.city_name || `City ${city.city_cd}`}
                  </p>
                  <p className="text-[8px] font-mono text-muted-foreground">{city.city_cd}</p>
                </div>
              </div>
              <p className="text-[10px] font-bold tabular-nums text-foreground">
                {fmt(city.total_ops)}
              </p>
              <p className="text-[10px] font-extrabold tabular-nums text-foreground">
                {fmt(
                  Number(city.total_amount ?? 0).toLocaleString(undefined, {
                    maximumFractionDigits: 2,
                  })
                )}
              </p>
              <p className="text-[10px] font-extrabold tabular-nums text-foreground">
                {fmt(
                  Number(city.total_notes_coins ?? 0).toLocaleString(undefined, {
                    maximumFractionDigits: 2,
                  })
                )}
              </p>
              <div className="space-y-0.5">
                <p className="text-[9px] font-extrabold tabular-nums text-primary">
                  {pct.toFixed(2)}%
                </p>
                <div className="w-full h-0.5 bg-secondary rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary rounded-full"
                    style={{ width: `${Math.min(pct, 100)}%` }}
                  />
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

// ============================================
// PanelResizeWrapper
// ============================================
const usePanelResize = (defaultWidth = 520) => {
  const panelRef = useRef(null);
  const [width, setWidth] = useState(defaultWidth);
  const dragging = useRef(false);
  const startX = useRef(0);
  const startW = useRef(0);

  const onMouseDown = (e) => {
    dragging.current = true;
    startX.current = e.clientX;
    startW.current = panelRef.current?.offsetWidth || defaultWidth;
    e.preventDefault();
  };
  useEffect(() => {
    const onMove = (e) => {
      if (!dragging.current) return;
      setWidth(Math.min(900, Math.max(320, startW.current + (startX.current - e.clientX))));
    };
    const onUp = () => {
      dragging.current = false;
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
  }, []);

  return { panelRef, width, onMouseDown };
};

// ============================================
// PanelShell
// ============================================
const PanelShell = ({
  width,
  panelRef,
  onMouseDown,
  onBack,
  onClose,
  icon,
  title,
  subtitle,
  currency,
  children,
}) => (
  <motion.div
    ref={panelRef}
    style={{ width }}
    initial={{ x: "100%", opacity: 0 }}
    animate={{ x: 0, opacity: 1 }}
    exit={{ x: "100%", opacity: 0 }}
    transition={{ type: "spring", damping: 25, stiffness: 200 }}
    className="fixed top-14 right-0 bottom-0 z-[40] overflow-y-auto cb-scroll bg-card border-l border-border shadow-2xl"
  >
    <div
      onMouseDown={onMouseDown}
      style={{
        position: "fixed",
        top: 56,
        bottom: 0,
        width: "6px",
        left: `calc(100vw - ${width}px)`,
        cursor: "col-resize",
        zIndex: 39,
      }}
      className="hover:bg-primary/20 transition-colors"
    />
    <div className="sticky top-0 z-10 backdrop-blur-md border-b border-border bg-card/80 p-4">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2.5">
          {onBack && (
            <button
              onClick={onBack}
              className="p-1.5 rounded-md hover:bg-secondary transition-colors text-muted-foreground hover:text-foreground"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
          )}
          <div className="w-8 h-8 rounded-lg bg-primary/20 flex items-center justify-center flex-shrink-0">
            {icon}
          </div>
          <div className="min-w-0">
            <h2 className="text-sm font-bold text-foreground truncate">{title}</h2>
            {subtitle && (
              <p className="text-[10px] font-mono text-muted-foreground truncate">{subtitle}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {currency && (
            <span
              className={`text-[9px] font-bold px-2 py-1 rounded-full border ${
                currency === "reporting"
                  ? "bg-amber-500/10 text-amber-500 border-amber-500/30"
                  : "bg-primary/10 text-primary border-primary/30"
              }`}
            >
              {currency === "reporting" ? "Reporting Currency" : "Local Currency"}
            </span>
          )}
          <button
            onClick={onClose}
            className="p-1.5 rounded-md hover:bg-secondary transition-colors text-muted-foreground hover:text-foreground"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
    <div className="p-4 space-y-2.5">{children}</div>
  </motion.div>
);

// ============================================
// CountryRevenueSummaryRow — for Region panel (modified to include contribution)
// ============================================
const CountryRevenueSummaryRow = ({ country, onSelectCountry, open, onToggle }) => {
  const toNum = (v) => Number(String(v ?? 0).replace(/,/g, "")) || 0;
  const formatMoney = (v) =>
    toNum(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const cities = country.cities || [];
  const cityCount = country.total_cities ?? cities.length;
  const totalOps = country.total_ops ?? cities.reduce((s, c) => s + (c.ops || []).length, 0);
  const totalDevices =
    country.total_devices ??
    cities.reduce((s, c) => {
      const ops = c.ops || [];
      return (
        s +
        ops.reduce((os, op) => {
          const locs = op.locations || [];
          return (
            os +
            locs.reduce((ls, loc) => {
              const gates = loc.gates || [];
              return ls + gates.reduce((gs, gate) => gs + (gate.devices || []).length, 0);
            }, 0)
          );
        }, 0)
      );
    }, 0);

  const totalAmount = Number(country.total_amount ?? 0);
  const totalNotesCoins = Number(country.total_notes_coins ?? 0);
  const contributionPct = parseFloat(country.contribution_pct) || 0;
  const countryAccessible = country._accessible !== false;

  const handleCountryClick = () => {
    if (countryAccessible) onSelectCountry?.(country);
    if (!open) onToggle?.();
  };

  return (
    <div
      className={`rounded-xl border border-border bg-card overflow-hidden ${countryAccessible ? "" : "opacity-50"}`}
    >
      <div className="w-full flex items-center gap-2.5 px-3.5 py-2.5 hover:bg-secondary/50 transition-colors">
        <button
          onClick={(e) => {
            e.stopPropagation();
            onToggle?.();
          }}
          className="flex-shrink-0 p-0.5 rounded hover:opacity-70 transition-opacity"
          title="Toggle details"
        >
          <div className="w-2 h-2 rounded-full bg-emerald-500" />
        </button>

        <button
          onClick={handleCountryClick}
          className="flex-1 flex items-center gap-2 text-left min-w-0"
          title="View country details"
        >
          <span className="flex-1 text-xs font-semibold text-foreground truncate">
            {country.country_name}
          </span>
          <span className="text-[9px] font-mono text-muted-foreground flex-shrink-0">
            {country.country}
          </span>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <span className="text-[8px] font-bold tabular-nums text-foreground bg-secondary px-2 py-0.5 rounded-full">
              {formatMoney(totalAmount)}
            </span>
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-secondary text-muted-foreground">
              {contributionPct.toFixed(2)}%
            </span>
          </div>
        </button>

        <button
          onClick={(e) => {
            e.stopPropagation();
            onToggle?.();
          }}
          className="flex-shrink-0 p-0.5 rounded hover:opacity-70 transition-opacity"
          title="Toggle details"
        >
          <ChevronDown
            className={`w-3.5 h-3.5 text-muted-foreground transition-transform duration-200 ${open ? "rotate-180" : ""}`}
          />
        </button>
      </div>

      <div className="px-3.5 pb-2">
        <div className="w-full h-1 bg-secondary rounded-full overflow-hidden">
          <div
            className="h-full rounded-full bg-primary transition-all duration-500"
            style={{ width: `${Math.min(contributionPct, 100)}%` }}
          />
        </div>
      </div>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="body"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <div className="border-t border-border p-3.5 space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <div className="rounded-lg bg-primary/10 border border-primary/20 p-2.5">
                  <p className="text-[8px] uppercase tracking-widest text-muted-foreground mb-1">
                    Total Amount
                  </p>
                  <p className="text-lg font-bold tabular-nums leading-none text-primary">
                    {formatMoney(totalAmount)}
                  </p>
                </div>
                <div className="rounded-lg bg-secondary p-2.5">
                  <p className="text-[8px] uppercase tracking-widest text-muted-foreground mb-1">
                    Notes / Coins
                  </p>
                  <p className="text-sm font-bold tabular-nums leading-none text-foreground">
                    {formatMoney(totalNotesCoins)}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div className="rounded-lg bg-secondary p-2.5">
                  <p className="text-[8px] uppercase tracking-widest text-muted-foreground mb-1">
                    Total Cities
                  </p>
                  <p className="text-sm font-bold tabular-nums leading-none text-foreground">
                    {cityCount}
                  </p>
                </div>
                <div className="rounded-lg bg-secondary p-2.5">
                  <p className="text-[8px] uppercase tracking-widest text-muted-foreground mb-1">
                    Total Ops
                  </p>
                  <p className="text-sm font-bold tabular-nums leading-none text-foreground">
                    {totalOps}
                  </p>
                </div>
              </div>

              <div className="rounded-lg bg-secondary p-2.5">
                <p className="text-[8px] uppercase tracking-widest text-muted-foreground mb-1">
                  Total Devices
                </p>
                <p className="text-sm font-bold tabular-nums leading-none text-foreground">
                  {totalDevices}
                </p>
              </div>

              {cities.length > 0 && (
                <div className="rounded-xl border border-border overflow-hidden">
                  <div className="grid grid-cols-[1.1fr_0.5fr_0.8fr_0.8fr_0.7fr] gap-1.5 px-3 py-2 bg-secondary/60 border-b border-border">
                    {["City", "Ops", "Amount", "Notes/Coins", "Contrib"].map((h) => (
                      <p
                        key={h}
                        className="text-[8px] uppercase tracking-widest text-muted-foreground font-semibold"
                      >
                        {h}
                      </p>
                    ))}
                  </div>
                  <div className="max-h-52 overflow-y-auto cb-scroll divide-y divide-border">
                    {[...cities]
                      .sort((a, b) => Number(b.total_amount ?? 0) - Number(a.total_amount ?? 0))
                      .map((city, ci) => {
                        const cityOps = city.ops || [];
                        const cityAmount = Number(city.total_amount ?? 0);
                        const cityNotesCoins = Number(city.total_notes_coins ?? 0);
                        const cityContributionPct =
                          totalAmount > 0 ? (cityAmount / totalAmount) * 100 : 0;
                        return (
                          <div
                            key={`city_${city.city_cd}_${ci}`}
                            className="px-3 py-2 hover:bg-secondary/40 transition-colors"
                          >
                            <div className="grid grid-cols-[1.1fr_0.5fr_0.8fr_0.8fr_0.7fr] gap-1.5 items-center">
                              <div className="flex items-center gap-1 min-w-0">
                                <MapPin className="w-2.5 h-2.5 text-emerald-400 flex-shrink-0" />
                                <p className="text-[10px] font-semibold text-foreground truncate">
                                  {city.city_name}
                                </p>
                              </div>
                              <p className="text-[10px] text-muted-foreground tabular-nums">
                                {cityOps.length}
                              </p>
                              <p className="text-[10px] font-semibold tabular-nums text-foreground">
                                {formatMoney(cityAmount)}
                              </p>
                              <p className="text-[10px] font-semibold tabular-nums text-foreground">
                                {formatMoney(cityNotesCoins)}
                              </p>
                              <div className="space-y-0.5">
                                <p className="text-[9px] font-extrabold tabular-nums text-primary">
                                  {cityContributionPct.toFixed(2)}%
                                </p>
                                <div className="w-full h-0.5 bg-secondary rounded-full overflow-hidden">
                                  <div
                                    className="h-full bg-primary rounded-full"
                                    style={{ width: `${Math.min(cityContributionPct, 100)}%` }}
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>
              )}

              {countryAccessible && (
                <button
                  onClick={() => onSelectCountry?.(country)}
                  className="w-full text-[10px] font-semibold text-primary hover:underline text-center py-1 flex items-center justify-center gap-1"
                >
                  View Country Detail <ChevronRight className="w-3 h-3" />
                </button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ============================================
// CityRevenueSummaryRow — FIXED VERSION
// ============================================
const CityRevenueSummaryRow = ({ city, totalAmount, onSelectCity, open, onToggle }) => {
  const toNum = (v) => Number(String(v ?? 0).replace(/,/g, "")) || 0;
  const formatMoney = (v) =>
    toNum(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  // IMPORTANT: city mey ops_list hai ya ops? dono check karo
  const cityOps = city.ops_list || city.ops || [];

  // Direct fields use karo jo response mey already hain
  const cityAmount = Number(city.total_amount ?? 0);
  const cityNotesCoins = Number(city.total_notes_coins ?? 0);
  const cityDevices = Number(city.total_devices ?? 0); // ← DIRECT FIELD!
  const cityLocations = Number(city.total_locations ?? 0); // ← DIRECT FIELD!
  const cityOpsCount = Number(city.total_ops ?? 0); // ← DIRECT FIELD!

  // Contribution percentage
  const contributionPct = totalAmount > 0 ? (cityAmount / totalAmount) * 100 : 0;

  // Devices count - direct field use karo
  const totalDevices = cityDevices;

  const cityAccessible = city._accessible !== false;

  const handleCityClick = () => {
    if (cityAccessible) onSelectCity?.(city.raw || city);
    if (!open) onToggle?.();
  };

  return (
    <div
      className={`rounded-xl border border-border bg-card overflow-hidden ${cityAccessible ? "" : "opacity-50"}`}
    >
      {/* Header Row */}
      <div className="w-full flex items-center gap-2.5 px-3.5 py-2.5 hover:bg-secondary/50 transition-colors">
        <button
          onClick={(e) => {
            e.stopPropagation();
            onToggle?.();
          }}
          className="flex-shrink-0 p-0.5 rounded hover:opacity-70 transition-opacity"
        >
          <div className="w-2 h-2 rounded-full bg-emerald-500" />
        </button>

        <button
          onClick={handleCityClick}
          className="flex-1 flex items-center gap-2 text-left min-w-0"
        >
          <span className="flex-1 text-xs font-semibold text-foreground truncate">
            {city.city_name}
          </span>
          <span className="text-[9px] font-mono text-muted-foreground flex-shrink-0">
            {city.city_cd}
          </span>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <span className="text-[8px] font-bold tabular-nums text-foreground bg-secondary px-2 py-0.5 rounded-full">
              {formatMoney(cityAmount)}
            </span>
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-secondary text-muted-foreground">
              {contributionPct.toFixed(2)}%
            </span>
          </div>
        </button>

        <button
          onClick={(e) => {
            e.stopPropagation();
            onToggle?.();
          }}
          className="flex-shrink-0 p-0.5 rounded hover:opacity-70 transition-opacity"
        >
          <ChevronDown
            className={`w-3.5 h-3.5 text-muted-foreground transition-transform duration-200 ${open ? "rotate-180" : ""}`}
          />
        </button>
      </div>

      {/* Progress Bar */}
      <div className="px-3.5 pb-2">
        <div className="w-full h-1 bg-secondary rounded-full overflow-hidden">
          <div
            className="h-full rounded-full bg-primary transition-all duration-500"
            style={{ width: `${Math.min(contributionPct, 100)}%` }}
          />
        </div>
      </div>

      {/* Expanded Content */}
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="body"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <div className="border-t border-border p-3.5 space-y-3">
              {/* Summary Cards */}
              <div className="grid grid-cols-2 gap-2">
                <div className="rounded-lg bg-primary/10 border border-primary/20 p-2.5">
                  <p className="text-[8px] uppercase tracking-widest text-muted-foreground mb-1">
                    Total Amount
                  </p>
                  <p className="text-lg font-bold tabular-nums leading-none text-primary">
                    {formatMoney(cityAmount)}
                  </p>
                </div>
                <div className="rounded-lg bg-secondary p-2.5">
                  <p className="text-[8px] uppercase tracking-widest text-muted-foreground mb-1">
                    Notes / Coins
                  </p>
                  <p className="text-sm font-bold tabular-nums leading-none text-foreground">
                    {formatMoney(cityNotesCoins)}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div className="rounded-lg bg-secondary p-2.5">
                  <p className="text-[8px] uppercase tracking-widest text-muted-foreground mb-1">
                    Total Ops
                  </p>
                  <p className="text-sm font-bold tabular-nums leading-none text-foreground">
                    {cityOpsCount || cityOps.length}
                  </p>
                </div>
                <div className="rounded-lg bg-secondary p-2.5">
                  <p className="text-[8px] uppercase tracking-widest text-muted-foreground mb-1">
                    Total Devices
                  </p>
                  <p className="text-sm font-bold tabular-nums leading-none text-foreground">
                    {totalDevices}
                  </p>
                </div>
              </div>

              {/* Ops Breakdown Table */}
              {cityOps.length > 0 && (
                <div className="rounded-xl border border-border overflow-hidden">
                  <div className="grid grid-cols-[1.1fr_0.5fr_0.8fr_0.8fr_0.7fr] gap-1.5 px-3 py-2 bg-secondary/60 border-b border-border">
                    {["Ops", "Locations", "Amount", "Notes/Coins", "Contrib"].map((h) => (
                      <p
                        key={h}
                        className="text-[8px] uppercase tracking-widest text-muted-foreground font-semibold"
                      >
                        {h}
                      </p>
                    ))}
                  </div>
                  <div className="max-h-52 overflow-y-auto cb-scroll divide-y divide-border">
                    {[...cityOps]
                      .sort((a, b) => Number(b.total_amount ?? 0) - Number(a.total_amount ?? 0))
                      .map((op, oi) => {
                        const opLocations = op.locations || [];
                        const opAmount = Number(op.total_amount ?? 0);
                        const opNotesCoins = Number(op.total_notes_coins ?? 0);
                        const opDevices = opLocations.reduce(
                          (s, loc) => s + (loc.devices || []).length,
                          0
                        );
                        const opContributionPct =
                          cityAmount > 0 ? (opAmount / cityAmount) * 100 : 0;
                        return (
                          <div
                            key={`op_${op.ops}_${oi}`}
                            className="px-3 py-2 hover:bg-secondary/40 transition-colors"
                          >
                            <div className="grid grid-cols-[1.1fr_0.5fr_0.8fr_0.8fr_0.7fr] gap-1.5 items-center">
                              <div className="flex items-center gap-1 min-w-0">
                                <Building2 className="w-2.5 h-2.5 text-blue-400 flex-shrink-0" />
                                <p className="text-[10px] font-semibold text-foreground truncate">
                                  {op.ops_name}
                                </p>
                              </div>
                              <p className="text-[10px] text-muted-foreground tabular-nums">
                                {opLocations.length}
                              </p>
                              <p className="text-[10px] font-semibold tabular-nums text-foreground">
                                {formatMoney(opAmount)}
                              </p>
                              <p className="text-[10px] font-semibold tabular-nums text-foreground">
                                {formatMoney(opNotesCoins)}
                              </p>
                              <div className="space-y-0.5">
                                <p className="text-[9px] font-extrabold tabular-nums text-primary">
                                  {opContributionPct.toFixed(2)}%
                                </p>
                                <div className="w-full h-0.5 bg-secondary rounded-full overflow-hidden">
                                  <div
                                    className="h-full bg-primary rounded-full"
                                    style={{ width: `${Math.min(opContributionPct, 100)}%` }}
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>
              )}

              <button
                onClick={() => onSelectCity?.(city.raw || city)}
                className="w-full text-[10px] font-semibold text-primary hover:underline text-center py-1 flex items-center justify-center gap-1"
              >
                View City Detail <ChevronRight className="w-3 h-3" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ============================================
// DeviceNameBreakdownRow — single expandable device-type row
// (same collapsed→expand pattern as CountryRevenueSummaryRow)
// ============================================
const DEV_NAME_META = {
  Coindispenser: { icon: CircleDot, color: "#F59E0B" },
  Notedispenser: { icon: Banknote, color: "#10B981" },
  Cashbox: { icon: Wallet, color: "#6366F1" },
  Recycler: { icon: TrendingUp, color: "#EC4899" },
};

const DeviceNameBreakdownRow = ({ item, totalAmount, open, onToggle }) => {
  const fmtAmount = (v) =>
    Number(v ?? 0).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  const fmtCount = (v) => Number(v ?? 0).toLocaleString();

  const meta = DEV_NAME_META[item.dev_name] || { icon: CircleDot, color: "#6366F1" };
  const Icon = meta.icon;
  const color = meta.color;

  const amount = Number(item.total_amount ?? 0);
  const contributionPct = totalAmount > 0 ? (amount / totalAmount) * 100 : 0;

  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden">
      {/* Header Row */}
      <div className="w-full flex items-center gap-2.5 px-3.5 py-2.5 hover:bg-secondary/50 transition-colors">
        <button
          onClick={() => onToggle?.()}
          className="flex-1 flex items-center gap-2 text-left min-w-0"
        >
          <div
            className="w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ background: `${color}22` }}
          >
            <Icon className="w-3.5 h-3.5" style={{ color }} />
          </div>
          <span className="flex-1 text-xs font-semibold text-foreground truncate">
            {item.dev_name}
          </span>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <span className="text-[8px] font-bold tabular-nums text-foreground bg-secondary px-2 py-0.5 rounded-full">
              {fmtAmount(amount)}
            </span>
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-secondary text-muted-foreground">
              {contributionPct.toFixed(2)}%
            </span>
          </div>
        </button>

        <button
          onClick={(e) => {
            e.stopPropagation();
            onToggle?.();
          }}
          className="flex-shrink-0 p-0.5 rounded hover:opacity-70 transition-opacity"
        >
          <ChevronDown
            className={`w-3.5 h-3.5 text-muted-foreground transition-transform duration-200 ${open ? "rotate-180" : ""}`}
          />
        </button>
      </div>

      {/* Progress Bar */}
      <div className="px-3.5 pb-2">
        <div className="w-full h-1 bg-secondary rounded-full overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{ width: `${Math.min(contributionPct, 100)}%`, background: color }}
          />
        </div>
      </div>

      {/* Expanded Content */}
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="body"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <div className="border-t border-border p-3.5 space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <div className="rounded-lg bg-secondary p-2.5">
                  <p className="text-[8px] uppercase tracking-widest text-muted-foreground mb-1">
                    Total Amount
                  </p>
                  <p className="text-lg font-bold tabular-nums leading-none text-foreground">
                    {fmtAmount(amount)}
                  </p>
                </div>
                <div className="rounded-lg bg-secondary p-2.5">
                  <p className="text-[8px] uppercase tracking-widest text-muted-foreground mb-1">
                    Notes / Coins
                  </p>
                  <p className="text-sm font-bold tabular-nums leading-none text-foreground">
                    {fmtCount(item.total_notes_coins)}
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ============================================
// DeviceNameBreakdownList — wraps rows, mirrors "Countries Breakdown"
// (name first, click/expand to see the card) — used inside Overview section
// ============================================
const DeviceNameBreakdownList = ({ breakdown = [] }) => {
  const [openCard, setOpenCard] = useState(null);

  if (!breakdown || !breakdown.length) return null;

  const totalAmount = breakdown.reduce((s, d) => s + Number(d.total_amount ?? 0), 0);
  const sorted = [...breakdown].sort(
    (a, b) => Number(b.total_amount ?? 0) - Number(a.total_amount ?? 0)
  );

  return (
    <div className="mt-3 space-y-2">
      <p className="text-[9px] uppercase tracking-widest text-muted-foreground font-semibold px-0.5">
        Device Type Breakdown
        {/* Notes & Total Amount Breakdown */}
      </p>
      <div className="space-y-2">
        {sorted.map((item, idx) => {
          const cardKey = `${item.dev_name}_${idx}`;
          return (
            <DeviceNameBreakdownRow
              key={cardKey}
              item={item}
              totalAmount={totalAmount}
              open={openCard === cardKey}
              onToggle={() => setOpenCard((prev) => (prev === cardKey ? null : cardKey))}
            />
          );
        })}
      </div>
    </div>
  );
};

// ============================================
// RegionDetailPanel — type="all" (modified to pass contribution)
// ============================================
const RegionDetailPanel = ({
  region,
  overview,
  onRefresh,
  onClose,
  onSelectCountry,
  onSelectCity,
  activeFilters,
  activeSection,
  onSectionChange,
}) => {
  // 🔥 GUARD - SABSE PEHLE
  if (region?._accessible === false) {
    return null;
  }

  const { panelRef, width, onMouseDown } = usePanelResize(560);
  const REGION_SECTION_IDS = ["overview", "countriesBreakdown", "citiesBreakdown"];
  const [localOpenSection, setLocalOpenSection] = useState(() =>
    resolveOpenSection(activeSection, REGION_SECTION_IDS, "overview")
  );
  const openSection =
    activeSection && REGION_SECTION_IDS.includes(activeSection) ? activeSection : localOpenSection;
  const handleToggleSection = (id) => {
    const next = openSection === id ? null : id;
    setLocalOpenSection(next);
    if (next) onSectionChange?.(next);
  };
  const [refreshing, setRefreshing] = useState(false);
  const handleRefresh = async () => {
    if (!onRefresh) return;
    setRefreshing(true);
    try {
      await onRefresh();
    } finally {
      setRefreshing(false);
    }
  };

  const [openCountryCard, setOpenCountryCard] = useState(null);

  const countriesList = useMemo(() => {
    const children = region?.children || [];
    const totalAmount = children.reduce((s, c) => s + Number(c.total_amount ?? 0), 0);
    return children
      .map((c) => ({
        ...c,
        contribution_pct: totalAmount > 0 ? (Number(c.total_amount ?? 0) / totalAmount) * 100 : 0,
      }))
      .sort((a, b) => Number(b.total_amount ?? 0) - Number(a.total_amount ?? 0));
  }, [region]);

  const citiesBreakdownForTable = useMemo(() => {
    const allCities = [];
    (region?.children || []).forEach((country) => {
      (country.cities || []).forEach((city) => {
        allCities.push({
          city_cd: city.city_cd,
          city_name: city.city_name,
          total_ops: (city.ops || []).length,
          total_amount: city.total_amount,
          total_notes_coins: city.total_notes_coins,
          raw: { ...city, country_code: country.country, country_name: country.country_name },
        });
      });
    });
    return allCities;
  }, [region]);

  const overviewCards = useMemo(() => {
    if (!overview) return [];
    return [
      { label: "Countries", value: overview.total_countries_count, color: "indigo" },
      { label: "Ops", value: overview.total_ops_count, color: "yellow" },
      { label: "Locations", value: overview.total_locations_count, color: "teal" },
      { label: "Devices", value: overview.total_devices_count, color: "fuchsia" },
      {
        label: "Total Amount",
        value: Number(overview.total_amount ?? 0).toLocaleString(undefined, {
          maximumFractionDigits: 2,
        }),
        color: "lime",
      },
      {
        label: "Notes / Coins",
        value: Number(overview.total_notes_coins ?? 0).toLocaleString(),
        color: "amber",
      },
      { label: "Latest Status", value: overview.latest_transaction_date, color: "rose" },
    ].filter((c) => c.value !== undefined && c.value !== null);
  }, [overview]);

  return (
    <PanelShell
      width={width}
      panelRef={panelRef}
      onMouseDown={onMouseDown}
      onClose={onClose}
      icon={<Globe className="w-4 h-4 text-primary" />}
      title={region?.country_name || "All Locations"}
      subtitle={`${(region?.children || []).length} Countries`}
      currency={activeFilters?.currency || "local"}
    >
      <AccordionSection
        id="overview"
        openId={openSection}
        onToggle={handleToggleSection}
        icon={<Wallet className="w-3.5 h-3.5" />}
        title="Overview"
      >
        {refreshing ? (
          <StatsGridShimmer count={6} />
        ) : (
          <>
            <OverviewCards cards={overviewCards} />
            <DeviceNameBreakdownList breakdown={overview?.dev_name_breakdown} />
          </>
        )}
      </AccordionSection>

      <AccordionSection
        id="countriesBreakdown"
        openId={openSection}
        onToggle={handleToggleSection}
        icon={<Globe className="w-3.5 h-3.5" />}
        title="Countries Breakdown"
        badge={`${countriesList.length} Countries`}
      >
        {countriesList.length === 0 ? (
          <p className="text-xs text-muted-foreground text-center py-4">
            No country data available
          </p>
        ) : (
          <div className="max-h-[480px] overflow-y-auto cb-scroll space-y-2 pr-0.5">
            {countriesList.map((country, idx) => {
              const cardKey = `${country.country}_${idx}`;
              return (
                <CountryRevenueSummaryRow
                  key={cardKey}
                  country={country}
                  onSelectCountry={onSelectCountry}
                  open={openCountryCard === cardKey}
                  onToggle={() => setOpenCountryCard((prev) => (prev === cardKey ? null : cardKey))}
                />
              );
            })}
          </div>
        )}
      </AccordionSection>

      <AccordionSection
        id="citiesBreakdown"
        openId={openSection}
        onToggle={handleToggleSection}
        icon={<MapPin className="w-3.5 h-3.5" />}
        title="Cities Breakdown"
        badge={`${citiesBreakdownForTable.length} Cities`}
      >
        <CityBreakdownList cities={citiesBreakdownForTable} onSelectCity={onSelectCity} />
      </AccordionSection>
    </PanelShell>
  );
};

// ============================================
// CountryDetailPanel — type=country
// ============================================
const CountryDetailPanel = ({
  country,
  onClose,
  onBack,
  onSelectCity,
  activeFilters,
  activeSection,
  onSectionChange,
}) => {
  const { panelRef, width, onMouseDown } = usePanelResize(560);
  const COUNTRY_SECTION_IDS = ["overview", "citiesBreakdown"];
  const [openSection, setOpenSection] = useState(() =>
    resolveOpenSection(activeSection, COUNTRY_SECTION_IDS, "overview")
  );
  const handleToggleSection = (id) =>
    setOpenSection((prev) => {
      const next = prev === id ? null : id;
      if (next) onSectionChange?.(next);
      return next;
    });

  // Accordion: only one City card expanded at a time
  const [openCityCard, setOpenCityCard] = useState(null);

  const { userContracts } = usePermissions();
  const currentStatusPerms = useMemo(
    () => new Set(userContracts?.current_status ?? []),
    [userContracts]
  );
  const noCurrentStatusRestrictions = currentStatusPerms.size === 0;

  const countryCounts = useMemo(() => countsFromCountryNode(country), [country]);
  const countryDevices = useMemo(() => devicesFromCountryNode(country), [country]);

  const { data, loading, error, refetch } = useOffStreetNodeData({ countryCd: country?.country });
  const nodeCountry = data?.countries?.[0] || null;

  // Calculate total amount for contribution percentage
  const totalAmount =
    country?.total_amount ||
    (country?.cities || []).reduce((s, c) => s + Number(c.total_amount ?? 0), 0) ||
    0;

  // Cities breakdown with contribution percentage — node fetch se aata hai
  // unfiltered data, isliye yahan bhi current_status permission check karte hain
  // (koi restriction configure hi na ho to sab dikha dete hain)
  const citiesBreakdownForTable = useMemo(() => {
    const sourceCities = (nodeCountry || country)?.cities || [];
    const countryName = country?.country_name;
    return sourceCities
      .filter((city) => {
        if (noCurrentStatusRestrictions) return true;
        const cityAccessible = currentStatusPerms.has(`${countryName}::${city.city_name}`);
        const hasAccessibleOps = (city.ops_list || city.ops || []).some((op) =>
          currentStatusPerms.has(`${countryName}::${city.city_name}::${op.ops}`)
        );
        return cityAccessible || hasAccessibleOps;
      })
      .map((city) => {
        const cityAccessible =
          noCurrentStatusRestrictions ||
          currentStatusPerms.has(`${countryName}::${city.city_name}`);
        return {
          ...city,
          _accessible: cityAccessible,
          raw: { ...city, country_code: country?.country, country_name: countryName },
          contribution_pct:
            totalAmount > 0 ? (Number(city.total_amount ?? 0) / totalAmount) * 100 : 0,
        };
      })
      .sort((a, b) => Number(b.total_amount ?? 0) - Number(a.total_amount ?? 0));
  }, [nodeCountry, country, totalAmount, currentStatusPerms, noCurrentStatusRestrictions]);

  // Overview cards
  const overviewCards = useMemo(() => {
    const cards = [
      {
        label: "Cities",
        value: country?.total_cities ?? citiesBreakdownForTable.length,
        color: "sky",
      },
      { label: "Ops", value: country?.total_ops ?? countryCounts.ops, color: "yellow" },
      {
        label: "Locations",
        value: country?.total_locations ?? countryCounts.locations,
        color: "teal",
      },
      {
        label: "Devices",
        value: country?.total_devices ?? countryCounts.devices,
        color: "fuchsia",
      },
      {
        label: "Total Amount",
        value: Number(totalAmount).toLocaleString(undefined, { maximumFractionDigits: 2 }),
        color: "lime",
      },
      {
        label: "Notes/Coins",
        value: Number(country?.total_notes_coins ?? 0).toLocaleString(),
        color: "amber",
      },
    ];
    if (data?.overview?.latest_transaction_date) {
      cards.push({
        label: "Latest Status",
        value: data.overview.latest_transaction_date,
        color: "rose",
      });
    }
    return cards;
  }, [country, citiesBreakdownForTable, countryCounts, totalAmount, data]);

  return (
    <PanelShell
      width={width}
      panelRef={panelRef}
      onMouseDown={onMouseDown}
      onBack={onBack}
      onClose={onClose}
      icon={<Globe className="w-4 h-4 text-primary" />}
      title={country?.country_name || "Country"}
      subtitle={`Code: ${country?.country}`}
      currency={activeFilters?.currency || "local"}
    >
      {/* ONLY Overview */}
      <AccordionSection
        id="overview"
        openId={openSection}
        onToggle={handleToggleSection}
        icon={<Layers className="w-3.5 h-3.5" />}
        title="Overview"
      >
        {loading ? (
          <StatsGridShimmer count={5} />
        ) : (
          <>
            <OverviewCards cards={overviewCards} />
            <DeviceNameBreakdownList breakdown={data?.overview?.dev_name_breakdown} />
          </>
        )}
      </AccordionSection>

      {/* ONLY Cities Breakdown - same as Region panel */}
      <AccordionSection
        id="citiesBreakdown"
        openId={openSection}
        onToggle={handleToggleSection}
        icon={<MapPin className="w-3.5 h-3.5" />}
        title="Cities Breakdown"
        badge={`${citiesBreakdownForTable.length} Cities`}
      >
        {loading && <PndListShimmer rows={5} withHeader={false} />}
        {!loading && !error && citiesBreakdownForTable.length === 0 && (
          <p className="text-xs text-muted-foreground text-center py-4">No cities data available</p>
        )}
        {!loading && !error && citiesBreakdownForTable.length > 0 && (
          <div className="max-h-[480px] overflow-y-auto cb-scroll space-y-2 pr-0.5">
            {citiesBreakdownForTable.map((city, idx) => {
              const cardKey = `${city.city_cd}_${idx}`;
              return (
                <CityRevenueSummaryRow
                  key={cardKey}
                  city={city}
                  totalAmount={totalAmount}
                  onSelectCity={onSelectCity}
                  open={openCityCard === cardKey}
                  onToggle={() => setOpenCityCard((prev) => (prev === cardKey ? null : cardKey))}
                />
              );
            })}
          </div>
        )}
      </AccordionSection>
    </PanelShell>
  );
};

// ============================================
// CityDetailPanel — type=city
// ============================================
const CityDetailPanel = ({
  city,
  onClose,
  onBack,
  onSelectOps,
  activeFilters,
  activeSection,
  onSectionChange,
}) => {
  const { panelRef, width, onMouseDown } = usePanelResize(540);
  // ONLY these sections: Overview + Ops Breakdown (Cash Collection removed)
  const CITY_SECTION_IDS = ["overview", "opsBreakdown"];
  const [openSection, setOpenSection] = useState(() =>
    resolveOpenSection(activeSection, CITY_SECTION_IDS, "overview")
  );
  const handleToggleSection = (id) =>
    setOpenSection((prev) => {
      const next = prev === id ? null : id;
      if (next) onSectionChange?.(next);
      return next;
    });

  // Accordion: only one Ops card expanded at a time
  const [openOpsCard, setOpenOpsCard] = useState(null);

  const { userContracts } = usePermissions();
  const currentStatusPerms = useMemo(
    () => new Set(userContracts?.current_status ?? []),
    [userContracts]
  );
  const noCurrentStatusRestrictions = currentStatusPerms.size === 0;

  const { data, loading, error, refetch } = useOffStreetNodeData({
    countryCd: city?.country_code,
    cityCd: city?.city_cd,
  });
  const nodeCity = data?.countries?.[0]?.cities?.[0] || null;

  const cityOpsRaw = (nodeCity || city)?.ops || [];

  // Total amount for contribution percentage
  const totalAmount =
    city?.total_amount ??
    data?.overview?.total_amount ??
    cityOpsRaw.reduce((s, op) => s + Number(op.total_amount ?? 0), 0);

  // Ops list with raw data — node fetch se aata hai unfiltered data, isliye
  // yahan bhi current_status permission check karte hain (koi restriction
  // configure hi na ho to sab dikha dete hain)
  const opsListForTable = useMemo(() => {
    return cityOpsRaw
      .filter(
        (op) =>
          noCurrentStatusRestrictions ||
          currentStatusPerms.has(`${city?.country_name}::${city?.city_name}::${op.ops}`)
      )
      .map((op) => ({
        ...op,
        raw: op,
        contribution_pct: totalAmount > 0 ? (Number(op.total_amount ?? 0) / totalAmount) * 100 : 0,
      }))
      .sort((a, b) => Number(b.total_amount ?? 0) - Number(a.total_amount ?? 0));
  }, [cityOpsRaw, totalAmount, city, currentStatusPerms, noCurrentStatusRestrictions]);

  // Overview cards for city
  const overviewCards = useMemo(() => {
    const cards = [
      { label: "Ops", value: city?.total_ops ?? opsListForTable.length, color: "yellow" },
      { label: "Locations", value: city?.total_locations, color: "teal" },
      { label: "Devices", value: city?.total_devices, color: "fuchsia" },
    ];
    if (data?.overview) {
      cards.push(
        {
          label: "Total Amount",
          value: Number(data.overview.total_amount ?? 0).toLocaleString(undefined, {
            maximumFractionDigits: 2,
          }),
          color: "lime",
        },
        {
          label: "Notes/Coins",
          value: Number(data.overview.total_notes_coins ?? 0).toLocaleString(),
          color: "amber",
        },
        { label: "Latest Status", value: data.overview.latest_transaction_date, color: "rose" }
      );
    }
    return cards;
  }, [city, opsListForTable, data]);

  return (
    <PanelShell
      width={width}
      panelRef={panelRef}
      onMouseDown={onMouseDown}
      onBack={onBack}
      onClose={onClose}
      icon={<MapPin className="w-4 h-4 text-primary" />}
      title={city?.city_name || "City"}
      subtitle={`City Code: ${city?.city_cd}`}
      currency={activeFilters?.currency || "local"}
    >
      {/* ONLY Overview */}
      <AccordionSection
        id="overview"
        openId={openSection}
        onToggle={handleToggleSection}
        icon={<Layers className="w-3.5 h-3.5" />}
        title="Overview"
      >
        {loading ? (
          <StatsGridShimmer count={6} />
        ) : (
          <>
            <OverviewCards cards={overviewCards} />
            <DeviceNameBreakdownList breakdown={data?.overview?.dev_name_breakdown} />
          </>
        )}
      </AccordionSection>

      {/* ONLY Ops Breakdown - same style as Region/Country/Ops panels */}
      <AccordionSection
        id="opsBreakdown"
        openId={openSection}
        onToggle={handleToggleSection}
        icon={<Building2 className="w-3.5 h-3.5" />}
        title="Ops Breakdown"
        badge={`${opsListForTable.length} Ops`}
      >
        {loading && <PndListShimmer rows={5} withHeader={false} />}
        {!loading && !error && opsListForTable.length === 0 && (
          <p className="text-xs text-muted-foreground text-center py-4">No ops data available</p>
        )}
        {!loading && !error && opsListForTable.length > 0 && (
          <div className="max-h-[480px] overflow-y-auto cb-scroll space-y-2 pr-0.5">
            {opsListForTable.map((op, idx) => {
              const cardKey = `${op.ops}_${idx}`;
              return (
                <OpsRevenueSummaryRow
                  key={cardKey}
                  opsItem={op}
                  totalAmount={totalAmount}
                  onSelectOps={onSelectOps}
                  open={openOpsCard === cardKey}
                  onToggle={() => setOpenOpsCard((prev) => (prev === cardKey ? null : cardKey))}
                />
              );
            })}
          </div>
        )}
      </AccordionSection>
    </PanelShell>
  );
};

// ============================================
// OpsDetailPanel (City View) — MODIFIED
// ============================================
const OpsDetailPanel = ({
  city,
  onClose,
  onBack,
  onSelectStreet,
  activeFilters,
  activeSection,
  onSectionChange,
}) => {
  const { panelRef, width, onMouseDown } = usePanelResize(540);

  // ONLY these sections: Overview + Locations Breakdown
  const OPS_SECTION_IDS = ["overview", "locationsBreakdown"];
  const [openSection, setOpenSection] = useState(() =>
    resolveOpenSection(activeSection, OPS_SECTION_IDS, "overview")
  );
  const handleToggleSection = (id) =>
    setOpenSection((prev) => {
      const next = prev === id ? null : id;
      if (next) onSectionChange?.(next);
      return next;
    });

  // Accordion: only one Location card expanded at a time
  const [openLocationCard, setOpenLocationCard] = useState(null);

  const cityCounts = useMemo(() => countsFromCityFlat(city), [city]);

  const { data, loading, error, refetch } = useOffStreetNodeData({
    countryCd: city?.country_code,
    cityCd: city?.parent_city_cd,
    ops: city?.city_cd,
  });

  // Total amount for contribution percentage
  const totalAmount =
    city?.total_amount ??
    (city?.streets || []).reduce((s, st) => s + Number(st.total_amount ?? 0), 0);

  // Locations list — this Ops' children are Locations (city.streets), not more Ops
  const locationsListForTable = useMemo(() => {
    const streets = city?.streets || [];
    return streets
      .map((street) => ({
        location: street.location_code,
        location_name: street.street_sub_name || street.location_code,
        total_amount: street.total_amount,
        total_notes_coins: street.total_notes_coins,
        devices: street.pnds || [],
        raw: street,
        contribution_pct:
          totalAmount > 0 ? (Number(street.total_amount ?? 0) / totalAmount) * 100 : 0,
      }))
      .sort((a, b) => Number(b.total_amount ?? 0) - Number(a.total_amount ?? 0));
  }, [city, totalAmount]);

  // Overview cards
  const overviewCards = useMemo(() => {
    const cards = [
      {
        label: "Locations",
        value: city?.total_streets ?? locationsListForTable.length,
        color: "teal",
      },
      { label: "Devices", value: city?.total_pnds ?? cityCounts.devices, color: "fuchsia" },
      {
        label: "Total Amount",
        value: Number(totalAmount).toLocaleString(undefined, { maximumFractionDigits: 2 }),
        color: "lime",
      },
      {
        label: "Notes/Coins",
        value: Number(city?.total_notes_coins ?? 0).toLocaleString(),
        color: "amber",
      },
    ];
    if (data?.overview?.latest_transaction_date) {
      cards.push({
        label: "Latest Status",
        value: data.overview.latest_transaction_date,
        color: "rose",
      });
    }
    return cards;
  }, [city, locationsListForTable, cityCounts, totalAmount, data]);

  return (
    <PanelShell
      width={width}
      panelRef={panelRef}
      onMouseDown={onMouseDown}
      onBack={onBack}
      onClose={onClose}
      icon={<Building2 className="w-4 h-4 text-primary" />}
      title={city?.city_name || "Ops"}
      subtitle={`Ops Code: ${city?.city_cd}`}
      currency={activeFilters?.currency || "local"}
    >
      {/* ONLY Overview */}
      <AccordionSection
        id="overview"
        openId={openSection}
        onToggle={handleToggleSection}
        icon={<Layers className="w-3.5 h-3.5" />}
        title="Overview"
      >
        {loading ? (
          <StatsGridShimmer count={5} />
        ) : (
          <>
            <OverviewCards cards={overviewCards} />
            <DeviceNameBreakdownList breakdown={data?.overview?.dev_name_breakdown} />
          </>
        )}
      </AccordionSection>

      {/* ONLY Locations Breakdown - same expandable card style as City/Region/Country panels */}
      <AccordionSection
        id="locationsBreakdown"
        openId={openSection}
        onToggle={handleToggleSection}
        icon={<MapPin className="w-3.5 h-3.5" />}
        title="Locations Breakdown"
        badge={`${locationsListForTable.length} Locations`}
      >
        {loading && <PndListShimmer rows={5} withHeader={false} />}
        {!loading && locationsListForTable.length === 0 && (
          <p className="text-xs text-muted-foreground text-center py-4">
            No locations data available
          </p>
        )}
        {!loading && locationsListForTable.length > 0 && (
          <div className="max-h-[480px] overflow-y-auto cb-scroll space-y-2 pr-0.5">
            {locationsListForTable.map((loc, idx) => {
              const cardKey = `${loc.location}_${idx}`;
              return (
                <LocationRevenueSummaryRow
                  key={cardKey}
                  locationItem={loc}
                  totalAmount={totalAmount}
                  onSelectStreet={onSelectStreet}
                  open={openLocationCard === cardKey}
                  onToggle={() =>
                    setOpenLocationCard((prev) => (prev === cardKey ? null : cardKey))
                  }
                />
              );
            })}
          </div>
        )}
      </AccordionSection>
    </PanelShell>
  );
};

// ============================================
// GatesBreakdownTable
// ============================================
const GatesBreakdownTable = ({ gatesBreakdown, onSelectGate }) => {
  if (!gatesBreakdown?.length)
    return <p className="text-xs text-muted-foreground text-center py-4">No gates data</p>;

  const fmt = (v) => (v === null || v === undefined ? "0" : v);

  const sorted = [...gatesBreakdown].sort(
    (a, b) => (parseFloat(b.contribution_pct) || 0) - (parseFloat(a.contribution_pct) || 0)
  );

  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden">
      <div className="grid grid-cols-[2fr_1fr_1fr_0.7fr_0.7fr_0.7fr] gap-1 px-3.5 py-1.5 bg-secondary/60 border-b border-border sticky top-0 z-10">
        {["Gate", "Revenue", "Tickets", "ALS", "ATV", "Contrib"].map((h) => (
          <p
            key={h}
            className="text-[8px] uppercase tracking-widest text-muted-foreground font-semibold"
          >
            {h}
          </p>
        ))}
      </div>
      <div className="divide-y divide-border max-h-72 overflow-y-auto cb-scroll">
        {sorted.map((gateRow, idx) => {
          const pct = parseFloat(gateRow.contribution_pct) || 0;
          const isGeneral = isGeneralGate(gateRow);
          const gateLabel = getGateDisplayName(gateRow);
          return (
            <button
              key={`${gateRow.gate_id || gateRow.gate}_${idx}`}
              onClick={() => {
                if (!isGeneral) onSelectGate?.(gateRow);
              }}
              title={
                isGeneral
                  ? "General — unassigned devices, see the Device Type Breakdown above for details"
                  : undefined
              }
              className={`w-full grid grid-cols-[2fr_1fr_1fr_0.7fr_0.7fr_0.7fr] gap-1 items-center px-3.5 py-2.5 transition-colors text-left ${
                isGeneral ? "cursor-default opacity-80" : "hover:bg-secondary/50"
              }`}
            >
              <div className="flex items-center gap-1.5 min-w-0">
                <Navigation className="w-3 h-3 text-blue-400 flex-shrink-0" />
                <p className="text-[10px] font-extrabold text-foreground truncate">{gateLabel}</p>
              </div>
              <p className="text-[10px] font-extrabold tabular-nums text-foreground">
                {fmt(gateRow.revenue)}
              </p>
              <p className="text-[10px] font-bold tabular-nums text-foreground">
                {fmt(gateRow.ticket_count)}
              </p>
              <p className="text-[10px] font-bold tabular-nums text-foreground">
                {fmt(gateRow.als)}
              </p>
              <p className="text-[10px] font-bold tabular-nums text-foreground">
                {fmt(gateRow.atv)}
              </p>
              <div className="space-y-0.5">
                <p className="text-[9px] font-extrabold tabular-nums text-primary">
                  {gateRow.contribution_pct || "0%"}
                </p>
                <div className="w-full h-0.5 bg-secondary rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary rounded-full"
                    style={{ width: `${Math.min(pct, 100)}%` }}
                  />
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

// ============================================
// LocationDetailPanel — type=location
// ============================================
const LocationDetailPanel = ({
  street,
  city,
  onClose,
  onSelectPnd,
  onBack,
  onSectionChange,
  activeSection,
  activeFilters,
}) => {
  const { panelRef, width, onMouseDown } = usePanelResize(520);
  const LOCATION_SECTION_IDS = ["overview", "devicesBreakdown"];
  const [openSection, setOpenSection] = useState(() =>
    resolveOpenSection(activeSection, LOCATION_SECTION_IDS, "overview")
  );
  const handleToggleSection = (id) =>
    setOpenSection((prev) => {
      const next = prev === id ? null : id;
      if (next) onSectionChange?.(next);
      return next;
    });

  // Accordion: only one Device card expanded at a time
  const [openDeviceCard, setOpenDeviceCard] = useState(null);

  const streetCounts = useMemo(() => countsFromStreetFlat(street), [street]);

  const { data, loading, error, refetch } = useOffStreetNodeData({
    countryCd: city?.country_code,
    cityCd: city?.parent_city_cd,
    ops: street?.ops_code,
    location: street?.location_code,
  });

  // Total amount for contribution percentage
  const totalAmount =
    street?.total_amount ??
    data?.overview?.total_amount ??
    (street?.pnds || []).reduce((s, p) => s + Number(p.total_amount ?? 0), 0);

  const devicesForTable = useMemo(() => {
    return [...(street?.pnds || [])].sort(
      (a, b) => Number(b.total_amount ?? 0) - Number(a.total_amount ?? 0)
    );
  }, [street]);

  // Overview cards for location
  const overviewCards = useMemo(() => {
    const cards = [
      { label: "Devices", value: street?.total_pnds || streetCounts.devices, color: "fuchsia" },
    ];
    if (data?.overview) {
      cards.push(
        {
          label: "Total Amount",
          value: Number(data.overview.total_amount ?? 0).toLocaleString(undefined, {
            maximumFractionDigits: 2,
          }),
          color: "lime",
        },
        {
          label: "Notes/Coins",
          value: Number(data.overview.total_notes_coins ?? 0).toLocaleString(),
          color: "amber",
        },
        { label: "Latest Status", value: data.overview.latest_transaction_date, color: "rose" }
      );
    }
    return cards;
  }, [street, streetCounts, data]);

  return (
    <PanelShell
      width={width}
      panelRef={panelRef}
      onMouseDown={onMouseDown}
      onBack={onBack}
      onClose={onClose}
      icon={<MapPin className="w-4 h-4 text-primary" />}
      title={street?.street_name || "Location"}
      subtitle={`${city?.city_name || ""} • Code: ${street?.street_cd || ""}`}
      currency={activeFilters?.currency || "local"}
    >
      <AccordionSection
        id="overview"
        openId={openSection}
        onToggle={handleToggleSection}
        icon={<BarChart2 className="w-3.5 h-3.5" />}
        title="Overview"
      >
        {loading ? (
          <StatsGridShimmer count={4} />
        ) : (
          <>
            <OverviewCards cards={overviewCards} />
            <DeviceNameBreakdownList breakdown={data?.overview?.dev_name_breakdown} />
          </>
        )}
      </AccordionSection>

      <AccordionSection
        id="devicesBreakdown"
        openId={openSection}
        onToggle={handleToggleSection}
        icon={<MonitorSmartphone className="w-3.5 h-3.5" />}
        title="Devices Breakdown"
        badge={`${devicesForTable.length} Devices`}
      >
        {loading && <PndListShimmer rows={5} withHeader={false} />}
        {!loading && devicesForTable.length === 0 && (
          <p className="text-xs text-muted-foreground text-center py-4">
            No devices data available
          </p>
        )}
        {!loading && devicesForTable.length > 0 && (
          <div className="max-h-[480px] overflow-y-auto cb-scroll space-y-2 pr-0.5">
            {devicesForTable.map((device, idx) => {
              const cardKey = `${device.device_id || device.pnd_number}_${idx}`;
              return (
                <DeviceRevenueSummaryRow
                  key={cardKey}
                  device={device}
                  totalAmount={totalAmount}
                  onSelectPnd={onSelectPnd}
                  open={openDeviceCard === cardKey}
                  onToggle={() => setOpenDeviceCard((prev) => (prev === cardKey ? null : cardKey))}
                />
              );
            })}
          </div>
        )}
      </AccordionSection>
    </PanelShell>
  );
};

// ============================================
// DevicesBreakdownTable
// ============================================
const DevicesBreakdownTable = ({ devicesBreakdown, onSelectDevice }) => {
  if (!devicesBreakdown?.length)
    return <p className="text-xs text-muted-foreground text-center py-4">No devices data</p>;

  const fmt = (v) => (v === null || v === undefined ? "0" : v);

  const sorted = [...devicesBreakdown].sort(
    (a, b) => (parseFloat(b.contribution_pct) || 0) - (parseFloat(a.contribution_pct) || 0)
  );

  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden">
      <div className="grid grid-cols-[2fr_1fr_1fr_0.7fr_0.7fr_0.7fr] gap-1 px-3.5 py-1.5 bg-secondary/60 border-b border-border sticky top-0 z-10">
        {["Device", "Revenue", "Tickets", "ALS", "ATV", "Contrib"].map((h) => (
          <p
            key={h}
            className="text-[8px] uppercase tracking-widest text-muted-foreground font-semibold"
          >
            {h}
          </p>
        ))}
      </div>
      <div className="divide-y divide-border max-h-72 overflow-y-auto cb-scroll">
        {sorted.map((d, idx) => {
          const pct = parseFloat(d.contribution_pct) || 0;
          return (
            <button
              key={`${d.device_id}_${idx}`}
              onClick={() => onSelectDevice?.(d)}
              className="w-full grid grid-cols-[2fr_1fr_1fr_0.7fr_0.7fr_0.7fr] gap-1 items-center px-3.5 py-2.5 hover:bg-secondary/50 transition-colors text-left"
            >
              <div className="flex items-center gap-1.5 min-w-0">
                <CircleDot
                  className="w-2.5 h-2.5 flex-shrink-0"
                  style={{ color: getDeviceColor(d.device_type) }}
                />
                <div className="min-w-0">
                  <div className="flex items-center gap-1 min-w-0">
                    <p className="text-[10px] font-extrabold text-foreground truncate font-mono">
                      #{d.device_id}
                    </p>
                    {d.device_type && (
                      <span
                        className="text-[7px] font-bold px-1 py-0.5 rounded flex-shrink-0 leading-none"
                        style={{
                          background: `${getDeviceColor(d.device_type)}22`,
                          color: getDeviceColor(d.device_type),
                        }}
                      >
                        {getDeviceTypeName(d.device_type)}
                      </span>
                    )}
                  </div>
                  <p className="text-[8px] text-muted-foreground truncate">
                    {d.device_name || "—"}
                  </p>
                </div>
              </div>
              <p className="text-[10px] font-extrabold tabular-nums text-foreground">
                {fmt(d.revenue)}
              </p>
              <p className="text-[10px] font-bold tabular-nums text-foreground">
                {fmt(d.ticket_count)}
              </p>
              <p className="text-[10px] font-bold tabular-nums text-foreground">{fmt(d.als)}</p>
              <p className="text-[10px] font-bold tabular-nums text-foreground">{fmt(d.atv)}</p>
              <div className="space-y-0.5">
                <p className="text-[9px] font-extrabold tabular-nums text-primary">
                  {d.contribution_pct || "0%"}
                </p>
                <div className="w-full h-0.5 bg-secondary rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary rounded-full"
                    style={{ width: `${Math.min(pct, 100)}%` }}
                  />
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

// ============================================
// GateDetailPanel — type=gate
// ============================================
const GateDetailPanel = ({
  gate,
  street,
  city,
  onClose,
  activeFilters,
  onBack,
  onSelectPnd,
  activeSection,
  onSectionChange,
}) => {
  const { panelRef, width, onMouseDown } = usePanelResize(520);
  const GATE_SECTION_IDS = [
    "deviceTypeBreakdown",
    "revenueSummary",
    "paymentBreakdown",
    "devices",
    "hourlyRevenue",
  ];
  const [openSection, setOpenSection] = useState(() =>
    resolveOpenSection(activeSection, GATE_SECTION_IDS, "deviceTypeBreakdown")
  );
  const handleToggleSection = (id) =>
    setOpenSection((prev) => {
      const next = prev === id ? null : id;
      if (next) onSectionChange?.(next);
      return next;
    });
  const now = new Date();
  const month = activeFilters?.month || MONTH_NAMES[now.getMonth()];
  const year = activeFilters?.year || String(now.getFullYear());
  const currency = activeFilters?.currency || "local";
  const locationId =
    street?.location_code || street?.street_sub_name || street?.street_cd?.split("_")[1];
  const gateId = gate?.gate ? String(gate.gate).toUpperCase() : gate?.gate;
  const gateName = getGateDisplayName(gate);

  const { data, loading, error, refetch } = useOffStreetMapData(
    locationId,
    year,
    month,
    "gate",
    currency,
    { gate: gateId }
  );

  const badge = `${month} ${year}`;
  const devicesBreakdown = useMemo(() => {
    const rows = data?.data?.devices_breakdown || [];
    return rows.map((row) => {
      const localMatch = (street?.pnds || []).find(
        (p) => String(p.pnd_number || p.device_id) === String(row.device_id)
      );
      return { ...row, device_type: row.device_type || localMatch?.device_type || null };
    });
  }, [data, street]);
  const gateDevices = useMemo(() => filterPndsByGate(street?.pnds, gate), [street, gate]);
  const totalDevices = devicesBreakdown.length || gateDevices.length;

  const handleSelectDevice = (row) => {
    const localMatch = (street?.pnds || []).find(
      (p) => String(p.pnd_number || p.device_id) === String(row.device_id)
    );
    onSelectPnd?.({
      pnd_number: row.device_id,
      device_id: row.device_id,
      device_name: row.device_name,
      device_type: row.device_type || localMatch?.device_type || null,
      gate_id: gateId,
      gate_name: row.gate_name || gateName,
      latitude: localMatch?.latitude,
      longitude: localMatch?.longitude,
    });
  };

  return (
    <PanelShell
      width={width}
      panelRef={panelRef}
      onMouseDown={onMouseDown}
      onBack={onBack}
      onClose={onClose}
      icon={<Navigation className="w-4 h-4 text-primary" />}
      title={gateName || "Gate"}
      subtitle={`${street?.street_name || ""} • ${city?.city_name || ""}`}
      currency={currency}
    >
      <DeviceTypeBreakdownSection
        devices={gateDevices}
        openSection={openSection}
        handleToggleSection={handleToggleSection}
      />

      <AccordionSection
        id="revenueSummary"
        openId={openSection}
        onToggle={handleToggleSection}
        icon={<TrendingUp className="w-3.5 h-3.5" />}
        title="Revenue Summary"
        badge={badge}
      >
        {loading && <StatsGridShimmer count={4} />}
        {error && (
          <div className="flex flex-col items-center gap-2 py-4">
            <p className="text-xs text-muted-foreground">{error}</p>
            <button
              onClick={refetch}
              className="text-[10px] text-primary hover:underline font-semibold"
            >
              Retry
            </button>
          </div>
        )}
        {!loading && !error && <SummaryCards summary={data?.data?.summary} />}
      </AccordionSection>

      <AccordionSection
        id="paymentBreakdown"
        openId={openSection}
        onToggle={handleToggleSection}
        icon={<CreditCard className="w-3.5 h-3.5" />}
        title="Payment Breakdown"
        badge={badge}
      >
        {loading && <PaymentTableShimmer groups={3} rowsPerGroup={2} />}
        {!loading && !error && (
          <PaymentBreakdownTable paymentBreakdown={data?.data?.payment_breakdown} />
        )}
      </AccordionSection>

      <AccordionSection
        id="devices"
        openId={openSection}
        onToggle={handleToggleSection}
        icon={<CircleDot className="w-3.5 h-3.5" />}
        title="Devices"
        badge={[String(totalDevices), badge]}
      >
        {loading && <PndListShimmer rows={4} withHeader={false} />}
        {!loading && !error && (
          <DevicesBreakdownTable
            devicesBreakdown={devicesBreakdown}
            onSelectDevice={handleSelectDevice}
          />
        )}
      </AccordionSection>

      <AccordionSection
        id="hourlyRevenue"
        openId={openSection}
        onToggle={handleToggleSection}
        icon={<Clock className="w-3.5 h-3.5" />}
        title="Hourly Revenue Pattern"
        badge={badge}
      >
        <HourlyRevenueChart
          deviceId={`${locationId} - ${gateId}`}
          month={month}
          year={year}
          title={`${gateName} - Hourly Revenue`}
          valueType="offstreet"
          currency={currency}
        />
      </AccordionSection>
    </PanelShell>
  );
};

// ============================================
// DeviceDetailPanel (was PndDetailPanel)
// ============================================
const DeviceDetailPanel = ({
  pnd,
  street,
  city,
  onClose,
  activeFilters,
  onBack,
  activeSection,
  onSectionChange,
}) => {
  const { panelRef, width, onMouseDown } = usePanelResize(520);
  const DEVICE_SECTION_IDS = ["locationDetails", "deviceInfo", "collection"];
  const [openSection, setOpenSection] = useState(() =>
    resolveOpenSection(activeSection, DEVICE_SECTION_IDS, "locationDetails")
  );
  const handleToggleSection = (id) =>
    setOpenSection((prev) => {
      const next = prev === id ? null : id;
      if (next) onSectionChange?.(next);
      return next;
    });
  const deviceColor = getDeviceColor(pnd.device_type);
  const deviceType = getDeviceTypeName(pnd.device_type);

  const currency = activeFilters?.currency || "local";
  const deviceId = pnd.pnd_number || pnd.device_id;
  const deviceLocationId =
    street?.location_code || street?.street_sub_name || street?.street_cd?.split("_")[1];

  // Fresh fetch on open so Collection shows live totals, not the page-load snapshot
  const { data, loading, error, refetch } = useOffStreetNodeData({
    countryCd: city?.country_code,
    cityCd: city?.parent_city_cd,
    ops: street?.ops_code,
    location: deviceLocationId,
    deviceId,
  });
  const freshDevice = extractFirstDeviceFromApiData(data);
  const cashDevice = freshDevice || pnd;

  return (
    <PanelShell
      width={width}
      panelRef={panelRef}
      onMouseDown={onMouseDown}
      onBack={onBack}
      onClose={onClose}
      icon={
        <div
          className="w-3 h-3 rounded-full"
          style={{ background: deviceColor, boxShadow: `0 0 12px ${deviceColor}66` }}
        />
      }
      title={`Device ${pnd.device_id || pnd.pnd_number}`}
      subtitle={deviceType}
      currency={currency}
    >
      <AccordionSection
        id="locationDetails"
        openId={openSection}
        onToggle={handleToggleSection}
        icon={<MapPin className="w-3.5 h-3.5" />}
        title="Location Details"
      >
        <div className="space-y-2.5">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Ops</p>
            <p className="text-sm font-medium text-foreground">{city?.city_name || "N/A"}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Location</p>
            <p className="text-sm font-medium text-foreground">{street?.street_name || "N/A"}</p>
          </div>
          {pnd.device_zone && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Zone</p>
              <p className="text-sm font-medium text-foreground">{pnd.device_zone}</p>
            </div>
          )}
        </div>
      </AccordionSection>

      <AccordionSection
        id="deviceInfo"
        openId={openSection}
        onToggle={handleToggleSection}
        icon={<Layers className="w-3.5 h-3.5" />}
        title="Device Information"
      >
        <div className="grid grid-cols-2 gap-3">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Device ID</p>
            <p className="text-sm font-mono font-semibold text-primary">
              #{pnd.pnd_number || pnd.device_id}
            </p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground">
              Device Type
            </p>
            <p className="text-sm font-medium text-foreground">
              {pnd.device_type || "Not Installed"}
            </p>
          </div>
        </div>
      </AccordionSection>

      <AccordionSection
        id="collection"
        openId={openSection}
        onToggle={handleToggleSection}
        icon={<Wallet className="w-3.5 h-3.5" />}
        title="Collection"
      >
        {loading && !freshDevice && <StatsGridShimmer count={2} />}
        {error && !freshDevice && (
          <div className="flex flex-col items-center gap-2 py-4">
            <p className="text-xs text-muted-foreground">{error}</p>
            <button
              onClick={refetch}
              className="text-[10px] text-primary hover:underline font-semibold"
            >
              Retry
            </button>
          </div>
        )}
        {(!loading || freshDevice) && cashDevice && (
          <>
            <div className="grid grid-cols-2 gap-2.5">
              <div className="rounded-lg bg-secondary p-3">
                <p className="text-[9px] uppercase tracking-widest text-muted-foreground mb-1.5">
                  Total Amount
                </p>
                <p className="text-[20px] font-medium leading-none text-foreground">
                  {Number(cashDevice.total_amount ?? 0).toLocaleString(undefined, {
                    maximumFractionDigits: 2,
                  })}
                </p>
              </div>
              <div className="rounded-lg bg-secondary p-3">
                <p className="text-[9px] uppercase tracking-widest text-muted-foreground mb-1.5">
                  Notes / Coins
                </p>
                <p className="text-[20px] font-medium leading-none text-foreground">
                  {Number(cashDevice.total_notes_coins ?? 0).toLocaleString()}
                </p>
              </div>
            </div>

            <div className="rounded-lg bg-secondary p-3 mt-2.5">
              <p className="text-[9px] uppercase tracking-widest text-muted-foreground mb-1.5 flex items-center gap-1">
                <Clock className="w-2.5 h-2.5" /> Latest Transaction
              </p>
              <p className="text-[20px] font-medium leading-none text-foreground">
                {cashDevice.latest_transdate || "—"}
              </p>
            </div>

            {/* Components Breakdown Table — same style as Devices Breakdown card */}
            {cashDevice.transactions && cashDevice.transactions.length > 0 && (
              <div className="rounded-xl border border-border overflow-hidden mt-3">
                <div className="grid grid-cols-[1.1fr_0.8fr_0.8fr_0.7fr] gap-1.5 px-3 py-2 bg-secondary/60 border-b border-border">
                  {["Component", "Amount", "Notes/Coins", "Contrib"].map((h) => (
                    <p
                      key={h}
                      className="text-[8px] uppercase tracking-widest text-muted-foreground font-semibold"
                    >
                      {h}
                    </p>
                  ))}
                </div>
                <div className="max-h-52 overflow-y-auto cb-scroll divide-y divide-border">
                  {[...cashDevice.transactions]
                    .sort((a, b) => Number(b.amount ?? 0) - Number(a.amount ?? 0))
                    .map((tx, ti) => {
                      const txAmount = Number(tx.amount ?? 0);
                      const deviceTotal = Number(cashDevice.total_amount ?? 0);
                      const txContributionPct =
                        deviceTotal > 0 ? (txAmount / deviceTotal) * 100 : 0;
                      return (
                        <div
                          key={`${tx.trans_id ?? "tx"}_${tx.line_number ?? ti}`}
                          className="px-3 py-2 hover:bg-secondary/40 transition-colors"
                        >
                          <div className="grid grid-cols-[1.1fr_0.8fr_0.8fr_0.7fr] gap-1.5 items-center">
                            <p className="text-[10px] font-semibold text-foreground truncate">
                              {tx.dev_name || "—"}
                            </p>
                            <p className="text-[10px] font-semibold tabular-nums text-foreground">
                              {txAmount.toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                            </p>
                            <p className="text-[10px] font-semibold tabular-nums text-foreground">
                              {tx.notes_coins ?? 0}
                            </p>
                            <div className="space-y-0.5">
                              <p className="text-[9px] font-extrabold tabular-nums text-primary">
                                {txContributionPct.toFixed(2)}%
                              </p>
                              <div className="w-full h-0.5 bg-secondary rounded-full overflow-hidden">
                                <div
                                  className="h-full bg-primary rounded-full"
                                  style={{ width: `${Math.min(txContributionPct, 100)}%` }}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>
            )}
          </>
        )}
      </AccordionSection>
    </PanelShell>
  );
};

// ============================================
// transformOffStreetApiData
// ============================================
const transformOffStreetApiData = (apiData) => {
  const overview = apiData?.overview || null;
  const countries = (apiData?.countries || []).map((country) => ({
    country: country.country_cd,
    country_cd: country.country_cd,
    country_name: country.country_name,
    total_amount: country.total_amount,
    total_notes_coins: country.total_notes_coins,
    total_cities: country.total_cities,
    total_ops: country.total_ops,
    total_locations: country.total_locations,
    total_devices: country.total_devices,
    // API ab country/city/ops/location par real polygon coordinates bhejta hai
    coordinates: country.coordinates || null,
    polygon_set: country.polygon_set || null,
    cities: (country.cities || []).map((city) => ({
      city_cd: city.city_cd,
      city_name: city.city_name,
      total_amount: city.total_amount,
      total_notes_coins: city.total_notes_coins,
      total_ops: city.total_ops,
      total_locations: city.total_locations,
      total_devices: city.total_devices,
      coordinates: city.coordinates || null,
      polygon_set: city.polygon_set || null,
      ops: (city.ops_list || []).map((op) => ({
        ops: op.ops,
        ops_name: op.ops_name,
        total_amount: op.total_amount,
        total_notes_coins: op.total_notes_coins,
        total_locations: op.total_locations,
        total_devices: op.total_devices,
        coordinates: op.coordinates || null,
        polygon_set: op.polygon_set || null,
        locations: (op.locations || []).map((loc) => ({
          location: loc.location,
          total_amount: loc.total_amount,
          total_notes_coins: loc.total_notes_coins,
          coordinates: loc.coordinates || null,
          polygon_set: loc.polygon_set || null,
          gates: [
            {
              gate_id: "Unknown",
              gate_name: "Unknown",
              devices: (loc.devices || []).map((d) => ({
                device_id: d.device_id,
                device_name: d.device_name,
                device_type: "APM",
                latitude: d.latitude,
                longitude: d.longitude,
                total_amount: d.total_amount,
                total_notes_coins: d.total_notes_coins,
                uploaded_count: d.uploaded_count,
                pending_count: d.pending_count,
                latest_transdate: d.latest_transdate,
                transactions: d.transactions || [],
              })),
            },
          ],
        })),
      })),
    })),
  }));

  return { overview, countries };
};

// ============================================
// usePndData
// ============================================
const usePndData = () => {
  const [rawData, setRawData] = useState([]);
  const [citiesData, setCitiesData] = useState([]);
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchPndData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiCall("get", "/cp-map/data-off-street", null, null, false, false);

      if (response?.success && response?.data) {
        const { overview: apiOverview, countries } = transformOffStreetApiData(response.data);
        setOverview(apiOverview);

        const topLevel = [
          {
            country: "ALL",
            country_name: "All Locations",
            children: countries,
          },
        ];
        setRawData(topLevel);

        const flatCities = [];
        countries.forEach((countryObj) => {
          getCountryOps(countryObj).forEach((op) => {
            const streets = [];
            (op.locations || []).forEach((loc) => {
              const pnds = [];
              (loc.gates || []).forEach((gate) => {
                (gate.devices || []).forEach((device) => {
                  pnds.push({
                    pnd_number: device.device_id,
                    device_id: device.device_id,
                    device_type: device.device_type || null,
                    device_name: device.device_name,
                    gate_name: gate.gate_name,
                    gate_id: gate.gate_id,
                    latitude: device.latitude,
                    longitude: device.longitude,
                    total_amount: device.total_amount,
                    total_notes_coins: device.total_notes_coins,
                    uploaded_count: device.uploaded_count,
                    pending_count: device.pending_count,
                    latest_transdate: device.latest_transdate,
                    transactions: device.transactions || [],
                  });
                });
              });
              streets.push({
                street_cd: `${op.ops}_${loc.location}`,
                street_name: `${op.ops_name} — ${loc.location}`,
                street_sub_name: loc.location,
                street_send_name: `${op.ops}_${loc.location}`,
                ops_code: op.ops,
                location_code: loc.location,
                coordinates: loc.coordinates || null,
                polygon_set: loc.polygon_set || null,
                total_amount: loc.total_amount,
                total_notes_coins: loc.total_notes_coins,
                total_pnds: pnds.length,
                pnds,
              });
            });

            flatCities.push({
              city_cd: op.ops,
              city_name: op.ops_name,
              country_name: countryObj.country_name,
              country_code: countryObj.country,
              city_send_name: op.ops,
              // API polygon coords (ops-level + parent country) — null => no default box
              coordinates: op.coordinates || null,
              polygon_set: op.polygon_set || null,
              country_coordinates: countryObj.coordinates || null,
              parent_city_cd: op.city_cd || null,
              parent_city_name: op.city_name || null,
              total_amount: op.total_amount,
              total_notes_coins: op.total_notes_coins,
              total_streets: streets.length,
              total_pnds: streets.reduce((s, st) => s + st.pnds.length, 0),
              streets,
            });
          });
        });

        setCitiesData(flatCities);
      } else {
        setError("Failed to load data");
      }
    } catch (err) {
      setError(err?.message || "Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPndData();
  }, []);
  return { rawData, citiesData, overview, loading, error, refetch: fetchPndData };
};

// ============================================
// Drill-down node fetch
// ============================================
const buildOffStreetParams = ({ countryCd, cityCd, ops, location, deviceId } = {}) => {
  const params = new URLSearchParams();
  if (countryCd) params.set("country_cd", countryCd);
  if (cityCd) params.set("city_cd", cityCd);
  if (ops) params.set("ops", ops);
  if (location) params.set("location", location);
  if (deviceId) params.set("device_id", deviceId);
  return params.toString();
};

const extractFirstDeviceFromApiData = (apiData) => {
  for (const country of apiData?.countries || []) {
    for (const op of getCountryOps(country)) {
      for (const loc of op.locations || []) {
        for (const gate of loc.gates || []) {
          for (const device of gate.devices || []) {
            return device;
          }
        }
      }
    }
  }
  return null;
};

const useOffStreetNodeData = ({ countryCd, cityCd, ops, location, deviceId } = {}) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const qs = buildOffStreetParams({ countryCd, cityCd, ops, location, deviceId });

  const fetchNode = useCallback(async () => {
    if (!qs) {
      setData(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await apiCall(
        "get",
        `/cp-map/data-off-street?${qs}`,
        null,
        null,
        false,
        false
      );
      if (response?.success && response?.data) {
        const normalized = transformOffStreetApiData(response.data);
        setData({ ...normalized, overview: response.data.overview || normalized.overview });
      } else {
        setError("Failed to load data");
      }
    } catch (err) {
      setError(err?.message || "Failed to load data");
    } finally {
      setLoading(false);
    }
  }, [qs]);

  useEffect(() => {
    fetchNode();
  }, [fetchNode]);
  return { data, loading, error, refetch: fetchNode };
};

// ============================================
// NodeCashCollectionSummary
// ============================================
const NodeCashCollectionSummary = ({ loading, error, overview, onRetry }) => {
  if (loading) return <StatsGridShimmer count={4} />;
  if (error) {
    return (
      <div className="flex flex-col items-center gap-2 py-4">
        <p className="text-xs text-muted-foreground">{error}</p>
        {onRetry && (
          <button
            onClick={onRetry}
            className="text-[10px] text-primary hover:underline font-semibold"
          >
            Retry
          </button>
        )}
      </div>
    );
  }
  if (!overview) return null;
  return (
    <div>
      <div className="grid grid-cols-2 gap-2.5">
        <div className="rounded-lg bg-secondary p-3">
          <p className="text-[9px] uppercase tracking-widest text-muted-foreground mb-1.5">
            Total Amount
          </p>
          <p className="text-[20px] font-medium leading-none text-foreground">
            {Number(overview.total_amount ?? 0).toLocaleString(undefined, {
              maximumFractionDigits: 2,
            })}
          </p>
        </div>
        <div className="rounded-lg bg-secondary p-3">
          <p className="text-[9px] uppercase tracking-widest text-muted-foreground mb-1.5">
            Notes / Coins
          </p>
          <p className="text-[20px] font-medium leading-none text-foreground">
            {Number(overview.total_notes_coins ?? 0).toLocaleString()}
          </p>
        </div>
        <div className="rounded-lg bg-secondary p-3">
          <p className="text-[9px] uppercase tracking-widest text-muted-foreground mb-1.5 flex items-center gap-1">
            <AlertTriangle className="w-2.5 h-2.5" /> Pending
          </p>
          <p className="text-[20px] font-medium leading-none text-foreground">
            {overview.pending_count ?? 0}
          </p>
        </div>
        <div className="rounded-lg bg-secondary p-3">
          <p className="text-[9px] uppercase tracking-widest text-muted-foreground mb-1.5 flex items-center gap-1">
            <Check className="w-2.5 h-2.5" /> Uploaded
          </p>
          <p className="text-[20px] font-medium leading-none text-foreground">
            {overview.uploaded_count ?? 0}
          </p>
        </div>
      </div>
      {overview.latest_transaction_date && (
        <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground mt-3">
          <Clock className="w-3 h-3" />
          Latest Status:{" "}
          <span className="font-medium text-foreground">{overview.latest_transaction_date}</span>
        </div>
      )}
    </div>
  );
};

// ============================================
// NodeBreakdownList
// ============================================
const NodeBreakdownList = ({ items, countLabel, onSelectItem }) => {
  if (!items?.length)
    return <p className="text-xs text-muted-foreground text-center py-5">No data available.</p>;
  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden divide-y divide-border">
      {items.map((item, idx) => (
        <button
          key={item.code ?? idx}
          onClick={() => onSelectItem?.(item)}
          className="w-full flex items-center gap-3 px-3.5 py-2.5 hover:bg-secondary/40 transition-colors text-left"
        >
          <div className="flex-1 min-w-0">
            <p className="text-[11px] font-semibold text-foreground truncate">{item.name}</p>
            <p className="text-[9px] text-muted-foreground">
              {item.count} {countLabel}
            </p>
          </div>
          <div className="text-right flex-shrink-0">
            <p className="text-[11px] font-bold text-foreground">
              {Number(item.total_amount ?? 0).toLocaleString(undefined, {
                maximumFractionDigits: 2,
              })}
            </p>
            <p className="text-[9px] text-muted-foreground">
              {Number(item.total_notes_coins ?? 0).toLocaleString()} notes/coins
            </p>
          </div>
        </button>
      ))}
    </div>
  );
};

// ============================================
// SummaryCards
// ============================================
const SummaryCards = ({ summary, extended = true }) => {
  if (!summary) return null;
  const fmt = (v) => (v === null || v === undefined ? "0" : v);
  const cards = [
    { label: "Revenue", value: fmt(summary.revenue), color: "green" },
    { label: "Ticket Count", value: fmt(summary.ticket_count), color: "blue" },
    { label: "ALS", value: fmt(summary.als), color: "amber" },
    { label: "ATV", value: fmt(summary.atv), color: "violet" },
  ];
  if (extended) {
    cards.push(
      { label: "Avg Daily Revenue", value: fmt(summary.avg_daily_revenue), color: "cyan" },
      { label: "Avg Daily Transaction", value: fmt(summary.avg_daily_transaction), color: "pink" },
      { label: "Parking Occupancy", value: fmt(summary.parking_occupancy), color: "orange" },
      { label: "Total Parking Bays", value: fmt(summary.total_parking_bays), color: "blue" }
    );
  }
  if (summary.contribution_pct !== undefined) {
    cards.push({ label: "Contribution", value: summary.contribution_pct || "0%", color: "red" });
  }
  // Convert to OverviewCards format
  const overviewCards = cards.map(({ label, value, color }) => ({ label, value, color }));
  return <OverviewCards cards={overviewCards} />;
};

// ============================================
// Main OffStreetMapPage
// ============================================
const OffStreetMapPage = () => {
  const findFirstAccessibleNode = (data) => {
    if (!data || !data.length) return null;

    for (const region of data) {
      if (!region.children?.length) continue;
      for (const country of region.children) {
        if (country._accessible !== false) {
          return { type: "country", node: country };
        }
        for (const city of country.cities || []) {
          if (city._accessible !== false) {
            return { type: "city", node: city };
          }
          for (const ops of city.ops || []) {
            if (ops.ops) {
              return { type: "ops", node: { ...city, ops: [ops] } };
            }
          }
        }
      }
    }
    return null;
  };

  const [selectedRegion, setSelectedRegion] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedCity, setSelectedCity] = useState(null);
  const [selectedCityGeo, setSelectedCityGeo] = useState(null);
  const [selectedStreet, setSelectedStreet] = useState(null);
  const [selectedGate, setSelectedGate] = useState(null);
  const [selectedPnd, setSelectedPnd] = useState(null);
  const [detailView, setDetailView] = useState("none");
  const [isDarkMode, setIsDarkMode] = useState(true);
  const [activeFilters, setActiveFilters] = useState({});
  const [belowThresholdPnds, setBelowThresholdPnds] = useState(new Set());
  const [noTransactionPnds, setNoTransactionPnds] = useState(new Set());
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [userClosedPanel, setUserClosedPanel] = useState(false);
  const [activeAccordionSection, setActiveAccordionSection] = useState(null);

  const { rawData, citiesData, overview, loading, error, refetch: fetchData } = usePndData();
  const { userContracts } = usePermissions();
  // OFF STREET permission-based filtering — current_status ki flat
  // "COUNTRY", "COUNTRY::CITY", "COUNTRY::CITY::OPS", "All Locations" paths
  // se independent markers ke hisab se tree filter hoti hai (koi cascade nahi).
  const currentStatusPerms = useMemo(
    () => new Set(userContracts?.current_status ?? []),
    [userContracts]
  );
  // Koi bhi current_status marker assign nahi — matlab ye module ke liye
  // restriction configure hi nahi hui, isliye sab kuch unrestricted show hoga.
  const noCurrentStatusRestrictions = currentStatusPerms.size === 0;

  const filteredRawData = useMemo(() => {
    if (!rawData.length || noCurrentStatusRestrictions) return rawData;
    const region = rawData[0];
    const countries = region.children || [];

    const filteredCountries = countries
      .map((country) => {
        const countryAccessible = currentStatusPerms.has(country.country_name);

        const filteredCities = (country.cities || [])
          .map((city) => {
            const cityAccessible = currentStatusPerms.has(
              `${country.country_name}::${city.city_name}`
            );
            const filteredOps = (city.ops || []).filter((op) =>
              currentStatusPerms.has(`${country.country_name}::${city.city_name}::${op.ops}`)
            );

            if (!cityAccessible && filteredOps.length === 0) return null;
            return { ...city, ops: filteredOps, _accessible: cityAccessible };
          })
          .filter(Boolean);

        if (!countryAccessible && filteredCities.length === 0) return null;
        return { ...country, cities: filteredCities, _accessible: countryAccessible };
      })
      .filter(Boolean);

    const regionAccessible = currentStatusPerms.has("All Locations");
    if (!regionAccessible && filteredCountries.length === 0) return [];

    return [{ ...region, children: filteredCountries, _accessible: regionAccessible }];
  }, [rawData, currentStatusPerms, noCurrentStatusRestrictions]);

  const filteredCitiesData = useMemo(() => {
    if (noCurrentStatusRestrictions) return citiesData;
    return citiesData.filter((c) =>
      currentStatusPerms.has(`${c.country_name}::${c.parent_city_name}::${c.city_cd}`)
    );
  }, [citiesData, currentStatusPerms, noCurrentStatusRestrictions]);

  // Map ke "all countries" view ke liye — country box ke position ke liye
  // asli (unfiltered) geo data chahiye hoti hai, isliye rawData se banate hain,
  // na ke filteredRawData se (jiski cities/ops empty ho sakti hain).
  // "All Locations" granted ho to har country box dikhta hai (non-clickable jab
  // tak wo country khud assign na ho); warna sirf assigned countries dikhte hain.
  const mapCountries = useMemo(() => {
    const rawCountries = rawData[0]?.children || [];
    if (noCurrentStatusRestrictions) return rawCountries;

    const allLocationsGranted = currentStatusPerms.has("All Locations");

    return rawCountries
      .filter((country) => {
        const countryAccessible = currentStatusPerms.has(country.country_name);
        const hasAccessibleDescendant = (country.cities || []).some((city) => {
          const cityAccessible = currentStatusPerms.has(
            `${country.country_name}::${city.city_name}`
          );
          const hasOps = (city.ops || []).some((op) =>
            currentStatusPerms.has(`${country.country_name}::${city.city_name}::${op.ops}`)
          );
          return cityAccessible || hasOps;
        });
        return allLocationsGranted || countryAccessible || hasAccessibleDescendant;
      })
      .map((country) => ({
        ...country,
        _accessible: currentStatusPerms.has(country.country_name),
      }));
  }, [rawData, currentStatusPerms, noCurrentStatusRestrictions]);

  // Real cities (city-level marker, "COUNTRY::CITY") jinke ops individually
  // assign nahi hain — map ke ops-boxes (citiesData) ye cover nahi karte,
  // isliye inke liye alag se raw geo data se city-level boxes banate hain.
  // (Unrestricted case mein zaroorat nahi — ops-boxes already sab dikha dete hain.)
  const mapCityBoxes = useMemo(() => {
    if (noCurrentStatusRestrictions) return [];
    const rawCountries = rawData[0]?.children || [];
    const boxes = [];
    rawCountries.forEach((country) => {
      (country.cities || []).forEach((city) => {
        const cityAccessible = currentStatusPerms.has(`${country.country_name}::${city.city_name}`);
        if (!cityAccessible) return;
        const devices = (city.ops || []).flatMap((op) =>
          (op.locations || []).flatMap((loc) =>
            (loc.gates || []).flatMap((gate) => gate.devices || [])
          )
        );
        boxes.push({
          ...city,
          country_code: country.country,
          country_name: country.country_name,
          coordinates: city.coordinates || null,
          devices,
        });
      });
    });
    return boxes;
  }, [rawData, currentStatusPerms, noCurrentStatusRestrictions]);

  const flatCountries = useMemo(
    () =>
      filteredRawData.flatMap((item) =>
        item.children?.length ? item.children : item.ops || item.cities ? [item] : []
      ),
    [filteredRawData]
  );

  const { theme: appTheme } = useTheme();

  const handleBreakdownLoad = useCallback(({ belowThreshold, noTransaction }) => {
    setBelowThresholdPnds(new Set(belowThreshold));
    setNoTransactionPnds(new Set(noTransaction));
  }, []);

  useEffect(() => {
    setIsDarkMode(appTheme === "dark");
  }, [appTheme]);

  const handleSelectRegion = useCallback((regionObj) => {
    if (regionObj?._accessible === false) return;
    setSelectedRegion(regionObj);
    setSelectedCountry(null);
    setSelectedCityGeo(null);
    setSelectedCity(null);
    setSelectedStreet(null);
    setSelectedGate(null);
    setSelectedPnd(null);
    setDetailView("region");
  }, []);

  useEffect(() => {
    if (
      !loading &&
      !error &&
      filteredRawData.length > 0 &&
      detailView === "none" &&
      !selectedRegion &&
      !userClosedPanel
    ) {
      const hasAllLocationsAccess = filteredRawData.some((node) => node._accessible !== false);

      if (hasAllLocationsAccess) {
        const defaultRegion = filteredRawData.find(
          (node) => Array.isArray(node.children) && node.children.length > 0
        );
        if (defaultRegion) handleSelectRegion(defaultRegion);
      } else {
        const firstAccessible = findFirstAccessibleNode(filteredRawData);
        if (firstAccessible) {
          if (firstAccessible.type === "country") {
            handleSelectCountry(firstAccessible.node);
          } else if (firstAccessible.type === "city") {
            handleCityFromCountry(firstAccessible.node);
          } else if (firstAccessible.type === "ops") {
            handleSelectCity(firstAccessible.node);
          }
        }
      }
    }
    // 🔥 DEPENDENCIES MEIN SE handleSelectCountry, handleCityFromCountry, handleSelectCity HATA DO
  }, [
    loading,
    error,
    filteredRawData,
    detailView,
    selectedRegion,
    handleSelectRegion,
    userClosedPanel,
  ]);

  const handleSelectCountry = useCallback((countryObj) => {
    setSelectedCountry(countryObj);
    setSelectedCityGeo(null);
    setSelectedCity(null);
    setSelectedStreet(null);
    setSelectedGate(null);
    setSelectedPnd(null);
    setDetailView("country");
  }, []);

  const handleCountryClickFromMap = useCallback(
    (countryCode) => {
      const countryObj = flatCountries.find((c) => String(c.country) === String(countryCode));
      if (countryObj) handleSelectCountry(countryObj);
    },
    [flatCountries, handleSelectCountry]
  );

  const handleSelectCity = useCallback((city) => {
    setSelectedCity(city);
    setSelectedCityGeo(null);
    setSelectedStreet(null);
    setSelectedGate(null);
    setSelectedPnd(null);
    setDetailView("city");
  }, []);

  const handleSelectStreet = useCallback((street) => {
    if (!street) return;
    setSelectedStreet(street);
    setSelectedGate(null);
    setSelectedPnd(null);
    setDetailView("street");
  }, []);

  const handleSelectGate = useCallback((gate) => {
    if (!gate) return;
    setSelectedGate(gate);
    setSelectedPnd(null);
    setDetailView("gate");
  }, []);

  const handleGateClickFromMap = useCallback(
    (gateGroup) => {
      if (!gateGroup) return;
      handleSelectGate({
        gate: gateGroup.gate_id || gateGroup.gate_name,
        gate_id: gateGroup.gate_id,
        gate_name: gateGroup.gate_name,
        contribution_pct: null,
      });
    },
    [handleSelectGate]
  );

  const handleSelectPnd = useCallback((pnd) => {
    if (!pnd) return;
    setSelectedPnd(pnd);
    setDetailView("pnd");
  }, []);

  const handleCloseDetail = useCallback(() => {
    setDetailView("none");
    setSelectedPnd(null);
    setSelectedGate(null);
    setSelectedCountry(null);
    setSelectedCityGeo(null);
    setSelectedRegion(null);
    setActiveAccordionSection(null); // 👈 ADD
    setUserClosedPanel(true);
  }, []);

  const handleBackToStreet = useCallback(() => {
    setSelectedGate(null);
    setSelectedPnd(null);
    setDetailView("street");
  }, []);
  const handleBackToGate = useCallback(() => {
    setSelectedPnd(null);
    setDetailView("gate");
  }, []);
  const handleBackToCity = useCallback(() => {
    setSelectedStreet(null);
    setSelectedGate(null);
    setSelectedPnd(null);
    setDetailView("city");
  }, []);
  const handleBackFromCity = useCallback(() => {
    if (selectedCityGeo) {
      setSelectedCity(null);
      setDetailView("citygeo");
    } else if (selectedCountry) {
      setSelectedCity(null);
      setDetailView("country");
    } else if (selectedRegion) {
      setSelectedCity(null);
      setDetailView("region");
    } else {
      setSelectedCity(null);
      setDetailView("none");
    }
  }, [selectedCityGeo, selectedCountry, selectedRegion]);
  const handleBackFromCityGeo = useCallback(() => {
    if (selectedCountry) {
      setSelectedCityGeo(null);
      setDetailView("country");
    } else if (selectedRegion) {
      setSelectedCityGeo(null);
      setDetailView("region");
    } else {
      setSelectedCityGeo(null);
      setDetailView("none");
    }
  }, [selectedCountry, selectedRegion]);
  const handleBackFromCountry = useCallback(() => {
    if (selectedRegion) {
      setSelectedCountry(null);
      setDetailView("region");
    } else {
      setSelectedCountry(null);
      setDetailView("none");
    }
  }, [selectedRegion]);

  const handleStreetFromCity = useCallback((street) => {
    setSelectedStreet(street);
    setSelectedPnd(null);
    setDetailView("street");
  }, []);
  const handlePndFromStreet = useCallback((pnd) => {
    setSelectedPnd(pnd);
    setDetailView("pnd");
  }, []);

  const handleLocationFromOps = useCallback(
    (locItem) => {
      const flatCity = selectedCity;
      const flatStreet = flatCity?.streets?.find(
        (s) => s.street_cd === `${flatCity.city_cd}_${locItem.location}`
      );
      if (flatStreet) {
        setSelectedStreet(flatStreet);
        setSelectedGate(null);
        setSelectedPnd(null);
        setDetailView("street");
      }
    },
    [selectedCity]
  );

  const handleOpsFromCountry = useCallback(
    (opsItem) => {
      const opsCode = opsItem.ops ?? opsItem.ops_code;
      const flatCity = citiesData.find(
        (c) =>
          (opsCode != null && String(c.city_cd) === String(opsCode)) ||
          (opsItem.ops_name && c.city_name === opsItem.ops_name)
      );
      if (flatCity) {
        setSelectedCity(flatCity);
        setSelectedStreet(null);
        setSelectedPnd(null);
        setDetailView("city");
      }
    },
    [citiesData]
  );

  const handleCityFromCountry = useCallback((cityObj) => {
    if (!cityObj) return;
    setSelectedCityGeo(cityObj);
    setSelectedCity(null);
    setSelectedStreet(null);
    setSelectedGate(null);
    setSelectedPnd(null);
    setDetailView("citygeo");
  }, []);

  const handleCountryFromRegion = useCallback((countryObj) => {
    setSelectedCountry(countryObj);
    setSelectedCityGeo(null);
    setSelectedCity(null);
    setSelectedStreet(null);
    setSelectedPnd(null);
    setDetailView("country");
  }, []);

  const handleFiltersChange = useCallback((filters) => {
    setActiveFilters(filters);
  }, []);

  // ── Sync: OffStreet page se jo filter/selection save hui thi, usay yahan
  // (Current Status) ek hi baar, data load hone ke baad, apply kar dete hain.
  // Agar OffStreet mein select ki gayi exact cheez (country/ops/location/
  // gate/device) yahan maujood na ho, to us country/ops/location ka pehla
  // available child khol dete hain.
  const offstreetSyncAppliedRef = useRef(false);

  useEffect(() => {
    if (loading || error) return;
    if (offstreetSyncAppliedRef.current) return;
    if (!filteredRawData.length) return;
    offstreetSyncAppliedRef.current = true;

    const sync = readOffstreetSync();
    if (!sync) return;

    // 1) Filter carry-forward
    // 1) Filter carry-forward
    if (sync.filters && Object.keys(sync.filters).length > 0) {
      setActiveFilters(sync.filters);
    }

    // 👇 YEH ADD KARO
    if (sync.accordionSection) {
      setActiveAccordionSection(sync.accordionSection);
    }

    // 2) Selection carry-forward
    const sel = sync.selection;
    if (!sel) return;

    const countryObj = flatCountries.find((c) => String(c.country) === String(sel.country));
    if (!countryObj) return; // koi anchor hi nahi mila — default flow chalne do

    setUserClosedPanel(false);

    if (sel.level === "country") {
      handleSelectCountry(countryObj);
      return;
    }

    // -- city (ops) --
    let flatCity = sel.city_cd
      ? filteredCitiesData.find((c) => String(c.city_cd) === String(sel.city_cd))
      : null;

    if (!flatCity) {
      // fallback: is country ka pehla ops/child
      const firstOp = getCountryOps(countryObj)[0];
      const fallbackCity = firstOp
        ? filteredCitiesData.find((c) => String(c.city_cd) === String(firstOp.ops))
        : null;
      if (fallbackCity) handleSelectCity(fallbackCity);
      else handleSelectCountry(countryObj);
      return;
    }

    if (sel.level === "city") {
      handleSelectCity(flatCity);
      return;
    }

    // -- street (location) --
    let street = sel.street_cd
      ? flatCity.streets?.find((s) => s.street_cd === sel.street_cd)
      : null;

    if (!street) {
      // fallback: is ops ka pehla location/child
      handleSelectCity(flatCity);
      const fallbackStreet = flatCity.streets?.[0] || null;
      if (fallbackStreet) handleSelectStreet(fallbackStreet);
      return;
    }

    if (sel.level === "street") {
      handleSelectCity(flatCity);
      handleSelectStreet(street);
      return;
    }

    handleSelectCity(flatCity);
    handleSelectStreet(street);

    // -- gate --
    if (sel.level === "gate") {
      const pnds = street.pnds || [];
      const matches = filterPndsByGate(pnds, { gate_id: sel.gate_id, gate_name: sel.gate_name });
      if (matches.length > 0) {
        handleSelectGate({
          gate: sel.gate_id || sel.gate_name,
          gate_id: sel.gate_id,
          gate_name: sel.gate_name,
          contribution_pct: null,
        });
      } else if (pnds[0]) {
        // fallback: is location ka pehla gate/child
        handleSelectGate({
          gate: pnds[0].gate_id || pnds[0].gate_name,
          gate_id: pnds[0].gate_id,
          gate_name: pnds[0].gate_name,
          contribution_pct: null,
        });
      }
      return;
    }

    // -- device (pnd) --
    if (sel.level === "pnd") {
      const pnds = street.pnds || [];
      const matchedPnd = pnds.find(
        (p) => String(p.pnd_number ?? p.device_id) === String(sel.pnd_number ?? sel.device_id)
      );
      if (matchedPnd) handleSelectPnd(matchedPnd);
      else if (pnds[0]) handleSelectPnd(pnds[0]); // fallback: pehla device
      return;
    }
  }, [
    loading,
    error,
    filteredRawData,
    filteredCitiesData,
    flatCountries,
    handleSelectCountry,
    handleSelectCity,
    handleSelectStreet,
    handleSelectGate,
    handleSelectPnd,
  ]);

  // ── Sync (save): yahan (Current Status) jab bhi filter badle, OffStreet
  // page ke liye save kar do — taake wahan wapas jaate hi wahi filter mile.
  // Guard: jab tak upar wala restore-effect apna kaam (hydrate) nahi kar
  // leta, tab tak kuch write nahi karte — warna page-mount ke turant baad
  // wala default/empty state, OffStreet ki save ki hui value ko clobber
  // (overwrite) kar deta.
  useEffect(() => {
    if (!offstreetSyncAppliedRef.current) return;
    writeOffstreetSync({ filters: activeFilters });
  }, [activeFilters]);

  // 👇 NAYA — accordion section bhi save karo
  useEffect(() => {
    if (!offstreetSyncAppliedRef.current) return;
    writeOffstreetSync({ accordionSection: activeAccordionSection });
  }, [activeAccordionSection]);

  // Agar koi entity seedha (map/table se) select ho jaye bina uske parent
  // (country/city) select kiye, to parent code data se resolve kar lete
  // hain — taake OffStreet page par fallback ("...ka pehla child") sahi
  // ancestor ke liye chal sake.
  const resolveCountryCodeForCity = useCallback(
    (cityCd) => {
      if (!cityCd) return null;
      for (const region of rawData) {
        const countries = region.children?.length ? region.children : [region];
        for (const country of countries) {
          if (getCountryOps(country).some((op) => String(op.ops) === String(cityCd))) {
            return country.country;
          }
        }
      }
      return null;
    },
    [rawData]
  );

  const resolveCityCdForStreet = useCallback(
    (streetCd) => {
      if (!streetCd) return null;
      const flatCity = citiesData.find((c) =>
        (c.streets || []).some((s) => s.street_cd === streetCd)
      );
      return flatCity?.city_cd ?? null;
    },
    [citiesData]
  );

  // ── Sync (save): yahan (Current Status) jab bhi hierarchy selection
  // (country/ops/location/gate/device) badle, OffStreet page ke liye save
  // kar do.
  useEffect(() => {
    if (!offstreetSyncAppliedRef.current) return;

    let level = null;
    if (selectedPnd) level = "pnd";
    else if (selectedGate) level = "gate";
    else if (selectedStreet) level = "street";
    else if (selectedCity) level = "city";
    else if (selectedCountry) level = "country";

    if (!level) return;

    let cityCd = selectedCity?.city_cd ?? null;
    let streetCd = selectedStreet?.street_cd ?? null;
    let countryCode = selectedCountry?.country ?? null;

    if (!cityCd && streetCd) cityCd = resolveCityCdForStreet(streetCd);
    if (!countryCode && cityCd) countryCode = resolveCountryCodeForCity(cityCd);

    writeOffstreetSync({
      selection: {
        level,
        country: countryCode,
        city_cd: cityCd,
        street_cd: streetCd,
        gate_id: selectedGate?.gate_id ?? selectedPnd?.gate_id ?? null,
        gate_name: selectedGate?.gate_name ?? selectedPnd?.gate_name ?? null,
        pnd_number: selectedPnd?.pnd_number ?? selectedPnd?.device_id ?? null,
        device_id: selectedPnd?.device_id ?? selectedPnd?.pnd_number ?? null,
        device_name: selectedPnd?.device_name ?? null,
      },
    });
  }, [
    selectedCountry,
    selectedCity,
    selectedStreet,
    selectedGate,
    selectedPnd,
    resolveCityCdForStreet,
    resolveCountryCodeForCity,
  ]);

  const PANEL_DEFAULT_WIDTHS = {
    region: 560,
    country: 560,
    citygeo: 540,
    city: 540,
    street: 520,
    gate: 520,
    pnd: 520,
  };
  const activePanelWidth = PANEL_DEFAULT_WIDTHS[detailView] || 0;

  if (loading) return <LoadingComponent />;
  if (error) return <ErrorState error={error} onRetry={fetchData} />;

  const getPndContext = () => {
    if (!selectedPnd) return { street: null, city: null };
    const selectedPndNumber = selectedPnd.pnd_number;
    for (const city of filteredCitiesData) {
      for (const street of city.streets || []) {
        const matchingPnd = street.pnds?.find(
          (p) => String(p.pnd_number) === String(selectedPndNumber)
        );
        if (matchingPnd) return { street, city };
      }
    }
    return { street: null, city: null };
  };

  const getStreetCity = () => {
    if (!selectedStreet) return null;
    return (
      citiesData.find((c) => c.streets?.some((s) => s.street_cd === selectedStreet.street_cd)) ||
      null
    );
  };

  const { street: pndStreet, city: pndCity } = getPndContext();
  const streetCity = getStreetCity();

  return (
    <div className="flex h-full w-full overflow-hidden bg-background">
      <AnimatePresence initial={false}>
        {sidebarOpen && (
          <motion.div
            key="sidebar"
            initial={{ width: 0, opacity: 0 }}
            animate={{ width: 320, opacity: 1 }}
            exit={{ width: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: "easeInOut" }}
            className="flex-shrink-0 overflow-hidden"
            style={{ minWidth: 0 }}
          >
            <CityStreetSidebar
              rawData={filteredRawData}
              citiesData={filteredCitiesData}
              overview={overview}
              selectedRegion={selectedRegion}
              selectedCountry={selectedCountry}
              selectedCityGeo={selectedCityGeo}
              selectedCity={selectedCity}
              selectedStreet={selectedStreet}
              selectedGate={selectedGate}
              selectedPnd={selectedPnd}
              onSelectRegion={handleSelectRegion}
              onSelectCountry={handleSelectCountry}
              onSelectCityGeo={handleCityFromCountry}
              onSelectCity={handleSelectCity}
              onSelectStreet={handleSelectStreet}
              onSelectPnd={handleSelectPnd}
              onSelectGate={handleSelectGate}
              activeFilters={activeFilters}
              onFiltersChange={handleFiltersChange}
              onToggle={() => setSidebarOpen(false)}
            />
          </motion.div>
        )}
      </AnimatePresence>

      <div className="flex-1 relative h-full min-h-0">
        {!sidebarOpen && (
          <button
            onClick={() => setSidebarOpen(true)}
            className="absolute top-4 left-3 z-50 p-2 rounded-lg bg-card border border-border shadow-md text-muted-foreground hover:text-foreground hover:bg-secondary transition-all"
            title="Open sidebar"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        )}

        <GoogleMapComponent
          citiesData={filteredCitiesData}
          countries={mapCountries}
          cityBoxes={mapCityBoxes}
          permsReady={userContracts !== null}
          selectedCountry={selectedCountry}
          selectedCityGeo={selectedCityGeo}
          selectedCity={selectedCity}
          selectedStreet={selectedStreet}
          selectedGate={selectedGate}
          selectedPnd={selectedPnd}
          onCountryClick={handleCountryClickFromMap}
          onPndClick={handleSelectPnd}
          onCityClick={handleSelectCity}
          onCityGeoClick={handleCityFromCountry}
          onStreetClick={handleSelectStreet}
          onGateClick={handleGateClickFromMap}
          isDarkMode={isDarkMode}
          belowThresholdPnds={belowThresholdPnds}
          noTransactionPnds={noTransactionPnds}
          panelWidth={activePanelWidth}
        />

        <AnimatePresence>
          {detailView === "region" && selectedRegion && (
            <RegionDetailPanel
              key="region-panel"
              region={selectedRegion}
              overview={overview}
              onRefresh={fetchData}
              onClose={handleCloseDetail}
              onSelectCountry={handleCountryFromRegion}
              onSelectCity={handleCityFromCountry}
              onSelectOps={handleOpsFromCountry}
              activeFilters={activeFilters}
              activeSection={activeAccordionSection}
              onSectionChange={setActiveAccordionSection}
            />
          )}
          {detailView === "country" && selectedCountry && (
            <CountryDetailPanel
              key="country-panel"
              country={selectedCountry}
              onClose={handleCloseDetail}
              onBack={handleBackFromCountry}
              onSelectCity={handleCityFromCountry}
              activeFilters={activeFilters}
              activeSection={activeAccordionSection}
              onSectionChange={setActiveAccordionSection}
            />
          )}
          {detailView === "city" && selectedCity && (
            <OpsDetailPanel
              key="ops-panel"
              city={selectedCity}
              onClose={handleCloseDetail}
              onBack={handleBackFromCity}
              onSelectStreet={handleLocationFromOps}
              activeFilters={activeFilters}
              activeSection={activeAccordionSection}
              onSectionChange={setActiveAccordionSection}
            />
          )}
          {detailView === "street" && selectedStreet && (
            <LocationDetailPanel
              key="location-panel"
              street={selectedStreet}
              city={streetCity}
              onClose={handleCloseDetail}
              onBack={selectedCity ? handleBackToCity : undefined}
              onSelectPnd={handlePndFromStreet}
              activeFilters={activeFilters}
              activeSection={activeAccordionSection}
              onSectionChange={setActiveAccordionSection}
            />
          )}
          {detailView === "gate" && selectedGate && (
            <GateDetailPanel
              key="gate-panel"
              gate={selectedGate}
              street={selectedStreet}
              city={streetCity}
              onClose={handleCloseDetail}
              onBack={selectedStreet ? handleBackToStreet : undefined}
              activeFilters={activeFilters}
              onSelectPnd={handleSelectPnd}
              activeSection={activeAccordionSection}
              onSectionChange={setActiveAccordionSection}
            />
          )}
          {detailView === "pnd" && selectedPnd && (
            <DeviceDetailPanel
              key="device-panel"
              pnd={selectedPnd}
              street={pndStreet}
              city={pndCity}
              onClose={handleCloseDetail}
              onBack={
                selectedGate ? handleBackToGate : selectedStreet ? handleBackToStreet : undefined
              }
              activeFilters={activeFilters}
              activeSection={activeAccordionSection}
              onSectionChange={setActiveAccordionSection}
            />
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default OffStreetMapPage;

